import React, { useState, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import debounce from 'lodash.debounce';
import AppContext from '../AppContext/AppContext';
import { useNavigate } from 'react-router-dom';
import Iconify from '../Iconify/Iconify';
import {
  IconButton,
  Tooltip,
  Stack,
  Button,
  Popover,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import './project.scss';
import { Config } from '../../Config';
import Modal from '../Modal/Modal';
import Panel from '../Panel/Panel';
import { styled } from '@mui/material/styles';
import Editor from "@monaco-editor/react";
import HistoryIcon from '@mui/icons-material/History';
import SendIcon from '@mui/icons-material/Send';
import ClearIcon from '@mui/icons-material/Clear';
import StopIcon from '@mui/icons-material/Stop';
import { hasSvgParent, addUibrainxSelectorPopup } from '../../Utils';
import { finder } from '@medv/finder';
import * as esprima from 'esprima';

const NEW_PAGE_START = "<<<<<<< NEW_PAGE_START ";
const NEW_PAGE_END = " >>>>>>> NEW_PAGE_END";
const UPDATE_PAGE_START = "<<<<<<< UPDATE_PAGE_START ";
const UPDATE_PAGE_END = " >>>>>>> UPDATE_PAGE_END";
const SEARCH_START = "<<<<<<< SEARCH";
const DIVIDER = "=======";
const REPLACE_END = ">>>>>>> REPLACE";
const wrapperElements = ['div', 'section', 'article', 'main', 'aside', 'header', 'footer', 'nav', 'figure', 'img'];

interface Message {
  type: 'user' | 'ai';
  content: string;
  explanation?: string; // For AI messages
  action?: string; // For AI messages
  images?: string[];
  projectVersion?: number | null;
  html?: {
    _id: string;
    html: string;
    url: string;
    version: number;
  };
}

interface Page {
  _id: string;
  name: string;
  url?: string;
  projectId?: string;
  deleted?: boolean;
  html?: string;
  htmlVersion?: number;
  createdAt?: string;
  updatedAt?: string;
}

// Template interface
interface Template {
  _id: string;
  name: string;
  html: string;
  tags: string[];
  description: string;
  screenshotUrl?: string;
  deleted?: boolean;
  createdAt: string;
  updatedAt: string;
}

function getTemplateThumbnailUrl(template: Template): string {
  const base = String(Config.PUBLIC_URL).replace(/\/$/, '');
  if (template.screenshotUrl) {
    const path = template.screenshotUrl.startsWith('/') ? template.screenshotUrl : `/${template.screenshotUrl}`;
    return `${base}${path}`;
  }
  return `${base}/resources/screenshots/${template._id}.png`;
}

// Add this helper function to check if a string is valid JSON
const isValidJSON = (str: string): boolean => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

// Add this function near the isValidJSON function to filter out unwanted Chinese text
const removeUnwantedText = (content: string): string => {
  if (!content) return '';

  // Check if content has the unwanted Chinese character
  if (!content.includes('极')) {
    return content;
  }

  try {
    // APPROACH 1: Most aggressive - replace the whole line containing '极'
    let processed = content.split('\n').map(line => {
      if (line.includes('极')) {
        // Attempt to keep HTML tags but remove text with 极
        const tagMatch = line.match(/(<[^>]*>)/g);
        if (tagMatch) {
          // If there are HTML tags, keep them but strip content
          return tagMatch.join('');
        }
        // No tags, just remove the entire line
        return '';
      }
      return line;
    }).join('\n');

    // APPROACH 2: Handle any remaining instances in attributes (class, style, etc.)
    // This regex matches any attribute that contains 极
    const attrRegex = /(\s+[a-zA-Z0-9\-_]+)=["']([^"']*极[^"']*)["']/g;
    processed = processed.replace(attrRegex, (match, attrName) => {
      // Just keep the attribute name with an empty value
      return `${attrName}=""`;
    });

    // APPROACH 3: Clean any text segments between tags containing 极
    processed = processed.replace(/>([^<]*极[^<]*)</g, '><');

    // APPROACH 4: Remove any remaining 极 characters and surrounding non-tag text
    processed = processed.replace(/极[^<>]{0,50}/g, '');
    processed = processed.replace(/[^<>]{0,20}极/g, '');

    // APPROACH 5: Safety check - recursively remove any remaining instances
    if (processed.includes('极')) {
      return removeUnwantedText(processed);
    }

    console.info('Successfully removed unwanted Chinese text');
    return processed;
  } catch (error) {
    // Fallback to a simple replace as last resort
    return content.replace(/极[^<]*/g, '');
  }
};

// Function to remove my-* classes from a string of classes
const removeMyClasses = (classes: string): string => {
  return classes
    .replace(/\bmy-\[\d+px\]\b/g, '')
    .replace(/\bmy-\d+\b/g, '')
    .trim();
};

// Function to validate HTML content before writing to document
const validateHtmlContent = (html: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  try {
    // 1. Check for basic HTML structure issues
    const scriptTagRegex = /<script[^>]*>([\s\S]*?)<\/script>/gi;
    const scriptMatches = html.match(scriptTagRegex);

    if (scriptMatches) {
      scriptMatches.forEach((scriptTag, index) => {
        // Extract JavaScript content from script tag
        const jsContent = scriptTag.replace(/<script[^>]*>|<\/script>/gi, '').trim();

        if (jsContent) {
          try {
            // Validate JavaScript syntax using Esprima
            esprima.parseScript(jsContent, { tolerant: true });
          } catch (jsError: any) {
            errors.push(`JavaScript syntax error in script tag ${index + 1}: ${jsError.message}`);
          }
        }
      });
    }

    // 2. Check for common HTML syntax issues
    // Check for unmatched parentheses in attributes
    // const attributeRegex = /(\w+)=["']([^"']*?)["']/g;
    // let match;
    // while ((match = attributeRegex.exec(html)) !== null) {
    //   const attrValue = match[2];
    //   const openParens = (attrValue.match(/\(/g) || []).length;
    //   const closeParens = (attrValue.match(/\)/g) || []).length;

    //   if (openParens !== closeParens) {
    //     errors.push(`Unmatched parentheses in attribute ${match[1]}: "${attrValue}"`);
    //   }
    // }

    // 3. Check for inline JavaScript in event handlers
    // const eventHandlerRegex = /on\w+\s*=\s*["']([^"']*?)["']/gi;
    // let eventMatch;
    // while ((eventMatch = eventHandlerRegex.exec(html)) !== null) {
    //   const jsCode = eventMatch[1];
    //   if (jsCode.trim()) {
    //     try {
    //       // Wrap in function to make it valid JavaScript for parsing
    //       esprima.parseScript(`function handler() { ${jsCode} }`, { tolerant: true });
    //     } catch (jsError: any) {
    //       errors.push(`JavaScript syntax error in event handler: ${jsError.message}`);
    //     }
    //   }
    // }

    // 4. Check for malformed tags
    const tagRegex = /<[^>]*>/g;
    let tagMatch;
    while ((tagMatch = tagRegex.exec(html)) !== null) {
      const tag = tagMatch[0];
      // Check for unclosed quotes in attributes
      const quoteCount = (tag.match(/"/g) || []).length;
      if (quoteCount % 2 !== 0) {
        errors.push(`Unclosed quote in tag: ${tag}`);
      }
    }

  } catch (error: any) {
    errors.push(`${error.message}`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

function Project() {
  const { projectId, pageId } = useParams();
  const [project, setProject] = useState<any>({});
  const [pages, setPages] = useState<Page[]>([]);
  const [pageEdited, setPageEdited] = useState<Page>({ _id: '', name: '' });
  const { state, dispatch } = useContext(AppContext);
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [fetching, setFetching] = useState<string>('');
  const [openModal, setOpenModal] = useState(false);
  const [isCommittingTemplate, setIsCommittingTemplate] = useState(false);
  const [openCommitDialog, setOpenCommitDialog] = useState(false);
  const [commitIsReplace, setCommitIsReplace] = useState(false);
  const [commitReplaceTemplateId, setCommitReplaceTemplateId] = useState('');
  const openModalRef = useRef(false);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [pageDeleteId, setPageDeleteId] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState('');
  const [selectedModel, setSelectedModel] = useState(project?.settings?.model || 'model1');
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('');
  const [isAnimationActive, setIsAnimationActive] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <script src="https://cdn.tailwindcss.com"></script>
</head>
<body>
</body>
</html>`);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isWaitingResponse, setIsWaitingResponse] = useState(false);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const [detectedUrls, setDetectedUrls] = useState<string[]>([]);
  const [showUrlActions, setShowUrlActions] = useState<boolean>(false);
  const [selectedActions, setSelectedActions] = useState<{ [url: string]: string[] }>({});
  const [actionInProgress, setActionInProgress] = useState<{ url: string, action: string } | null>(null);
  const [showColorPalette, setShowColorPalette] = useState<boolean>(false);
  const [colorPaletteAnchorEl, setColorPaletteAnchorEl] = useState<HTMLElement | null>(null);
  const [showFontSelector, setShowFontSelector] = useState<boolean>(false);
  const [fontSelectorAnchorEl, setFontSelectorAnchorEl] = useState<HTMLElement | null>(null);
  const [colorPaletteSearch, setColorPaletteSearch] = useState('');
  const [fontSearch, setFontSearch] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [historyMessages, setHistoryMessages] = useState<Message[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(() => {
    // Get initial state from localStorage, default to true if not set
    const saved = localStorage.getItem('showHistory');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [cssSelector, setCssSelector] = useState<string>('');
  const [exportType, setExportType] = useState<string>('entirePage');
  const exportTypeRef = useRef<string>('entirePage');

  useEffect(() => {
		if (project?.settings?.model) {
			setSelectedModel(project?.settings?.model);
		}
	}, [project]);

  // Create a ref for the Monaco editor
  const editorRef = useRef<any>(null);
  const autoSaveTimer = useRef<NodeJS.Timeout | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // State for the currently active page
  const [activePage, setActivePage] = useState<string | null>(null);
  const activePageRef = useRef<string | null>(null);

  useEffect(() => {
    activePageRef.current = activePage;
  }, [activePage]);

  // State for editing page names
  const [isEditingName, setIsEditingName] = useState<string | null>(null);

  // Add state for add page popover
  const [addPageAnchorEl, setAddPageAnchorEl] = useState<HTMLElement | null>(null);

  // Add state for templates
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateAnchorEl, setTemplateAnchorEl] = useState<HTMLElement | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [deletingCommunityTemplateId, setDeletingCommunityTemplateId] = useState<string | null>(null);
  const [templateSearch, setTemplateSearch] = useState('');

  // Add state for active HTML ID
  const [activeHtmlId, setActiveHtmlId] = useState<string | null>(null);

  // Properly declare element editing states
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEditEditorMode, setIsEditEditorMode] = useState<{ [pageId: string]: boolean }>({});
  const [selectedElement, setSelectedElement] = useState<HTMLElement | null>(null);
  const [selectedElementDataId, setSelectedElementDataId] = useState<string | null>(null);
  const [selectedElementHTML, setSelectedElementHTML] = useState<string | null>(null);
  const [editData, setEditData] = useState<{ [pageId: string]: { [elementId: string]: { classes: string, content: string, imageUrl: string } } }>({});
  const [elementClasses, setElementClasses] = useState('');
  const [showClassForm, setShowClassForm] = useState(false);

  // Properly declare color picker states
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerAnchorEl, setColorPickerAnchorEl] = useState<HTMLElement | null>(null);
  const [colorPickerTab, setColorPickerTab] = useState<'styles' | 'custom'>('styles');
  const [customColor, setCustomColor] = useState<string>('#000000');

  // Predefined color palettes
  const colorPalettes = [
    { name: "Modern Blue", colors: ["#1A73E8", "#4285F4", "#5E97F6", "#8AB4F8", "#C6DAFC"] },
    { name: "Earthy Tones", colors: ["#795548", "#A1887F", "#BCAAA4", "#D7CCC8", "#EFEBE9"] },
    { name: "Vibrant", colors: ["#F44336", "#FF9800", "#FFEB3B", "#4CAF50", "#2196F3"] },
    { name: "Monochrome", colors: ["#212121", "#424242", "#616161", "#9E9E9E", "#E0E0E0"] },
    { name: "Pastel", colors: ["#FFD3E0", "#FFD6BA", "#FFFFD1", "#C3E5AE", "#B6DCFE"] },
    { name: "Material Primary", colors: ["#F44336", "#E91E63", "#9C27B0", "#673AB7", "#3F51B5"] },
    { name: "Material Secondary", colors: ["#2196F3", "#03A9F4", "#00BCD4", "#009688", "#4CAF50"] },
    { name: "Material Tertiary", colors: ["#8BC34A", "#CDDC39", "#FFEB3B", "#FFC107", "#FF9800"] },
    { name: "Neon", colors: ["#FE53BB", "#09FBD3", "#F5D300", "#7122FA", "#08F7FE"] },
    { name: "Ocean", colors: ["#006064", "#00838F", "#0097A7", "#00ACC1", "#26C6DA"] },
    { name: "Forest", colors: ["#1B5E20", "#2E7D32", "#388E3C", "#43A047", "#66BB6A"] },
    { name: "Sunset", colors: ["#FF9E80", "#FF6E40", "#FF3D00", "#DD2C00", "#BF360C"] },
    // New color palettes
    { name: "Nordic", colors: ["#6E7582", "#8896AB", "#A5B8D0", "#C8DAF1", "#ECF4FF"] },
    { name: "Autumn", colors: ["#BF4904", "#D95204", "#F35D04", "#F18904", "#FFCA40"] },
    { name: "Spring", colors: ["#7FB069", "#FFFBBD", "#E6AA68", "#CA3C25", "#1D1A05"] },
    { name: "Summer", colors: ["#3BCEAC", "#0EAD69", "#FDE74C", "#FF5E5B", "#6C464F"] },
    { name: "Winter", colors: ["#0B3954", "#087E8B", "#BFD7EA", "#FF5A5F", "#C81D25"] },
    { name: "Retro", colors: ["#D7263D", "#F46036", "#2E294E", "#1B998B", "#C5D86D"] },
    { name: "Vintage", colors: ["#694A38", "#A37C40", "#E4A93A", "#F4E3B1", "#D3C6B3"] },
    { name: "Cyberpunk", colors: ["#0D0221", "#0F2557", "#2BAE66", "#FCF951", "#E83F6F"] },
    { name: "Dark Mode", colors: ["#121212", "#1E1E1E", "#2A2A2A", "#3C3C3C", "#505050"] },
    { name: "Tech", colors: ["#0C0D10", "#151821", "#1E2233", "#5561FF", "#FFFFFF"] },
    { name: "Elegant", colors: ["#2D3142", "#4F5D75", "#BFC0C0", "#FFFFFF", "#EF8354"] },
    { name: "Corporate", colors: ["#003049", "#D62828", "#F77F00", "#FCBF49", "#EAE2B7"] },
    { name: "Minimal", colors: ["#FFFFFF", "#F5F5F5", "#EDEDED", "#DBDBDB", "#A5A5A5"] },
    { name: "Bold", colors: ["#780116", "#F7B538", "#DB7C26", "#D8572A", "#C32F27"] },
    { name: "Soft", colors: ["#CAEBF2", "#A9D7F6", "#FF3562", "#F2643D", "#F6D8AE"] },
    { name: "Jewel Tones", colors: ["#560BAD", "#7209B7", "#B5179E", "#F72585", "#4CC9F0"] },
    { name: "Nature", colors: ["#588157", "#3A5A40", "#A3B18A", "#DAD7CD", "#344E41"] },
    { name: "Berry", colors: ["#6E44FF", "#B892FF", "#FFC2E2", "#FF90B3", "#EF7A85"] },
    { name: "Electric", colors: ["#011FFD", "#0B8AF7", "#0DCAF9", "#0EF5EA", "#0FFBBC"] },
    { name: "Warm Gray", colors: ["#5A5754", "#847F77", "#A69F98", "#D1C8C0", "#F1EDE9"] },
    { name: "Cool Gray", colors: ["#5C5F6B", "#7C7F8A", "#9EA0AA", "#BFC1C9", "#DCDDE4"] },
    { name: "Neutrals", colors: ["#262626", "#404040", "#737373", "#A3A3A3", "#D4D4D4"] },
    { name: "Earthy Red", colors: ["#4A0404", "#730606", "#9B0909", "#D30D0D", "#F93131"] },
    { name: "Tropical", colors: ["#264653", "#2A9D8F", "#E9C46A", "#F4A261", "#E76F51"] },
    { name: "Deep Blue", colors: ["#03045E", "#0077B6", "#00B4D8", "#90E0EF", "#CAF0F8"] },
    { name: "Green Tea", colors: ["#D8F3DC", "#B7E4C7", "#95D5B2", "#74C69D", "#52B788"] },
    { name: "Lavender", colors: ["#7D5BA6", "#9670C1", "#B389DE", "#CDA3EF", "#DFB9FB"] },
    { name: "Mustard", colors: ["#5A3A31", "#75543F", "#8D7358", "#DAB995", "#F1D6AB"] },
    { name: "Terracotta", colors: ["#662E1C", "#AC5943", "#E5B299", "#E4D4C8", "#D0A992"] },
    { name: "Navy Gold", colors: ["#0A1128", "#001F54", "#034078", "#FFDDA1", "#FDBF60"] },
    { name: "Cherry Blossom", colors: ["#FBF0F0", "#DFD3D3", "#B8B0B0", "#7C7575", "#E2C2B9"] },
    { name: "Desert", colors: ["#E0C097", "#C1A57B", "#A59E8C", "#7D7570", "#5B5650"] },
    { name: "Candy", colors: ["#F20089", "#E500A4", "#DB00B6", "#D100D1", "#BC00DD"] },
    { name: "Wood", colors: ["#4D3B30", "#70542F", "#93662F", "#B67E31", "#D99838"] },
    { name: "Ice", colors: ["#E3F6F5", "#BAE8E8", "#2C698D", "#272643", "#FFFFFF"] },
    { name: "Coral Reef", colors: ["#0A1D37", "#142850", "#00909E", "#DAE1E7", "#FF9A3C"] },
    { name: "Midnight", colors: ["#1B1B1B", "#2E2E2E", "#454545", "#A0A0A0", "#EFEFEF"] },
    { name: "Sunset Beach", colors: ["#F8B195", "#F67280", "#C06C84", "#6C5B7B", "#355C7D"] },
    { name: "Olive Garden", colors: ["#513B3C", "#8B5E34", "#9F7E69", "#C0B9A5", "#F2F2F2"] }
  ];

  // Font categories with more Google Fonts like Figma
  const fontCategories = [
    {
      name: "Popular Sans-serif",
      fonts: [
        { name: "Inter", family: "'Inter', sans-serif" },
        { name: "Roboto", family: "'Roboto', sans-serif" },
        { name: "Open Sans", family: "'Open Sans', sans-serif" },
        { name: "Montserrat", family: "'Montserrat', sans-serif" },
        { name: "Poppins", family: "'Poppins', sans-serif" },
        { name: "Nunito Sans", family: "'Nunito Sans', sans-serif" }
      ]
    },
    {
      name: "Modern Sans-serif",
      fonts: [
        { name: "DM Sans", family: "'DM Sans', sans-serif" },
        { name: "Manrope", family: "'Manrope', sans-serif" },
        { name: "Outfit", family: "'Outfit', sans-serif" },
        { name: "Plus Jakarta Sans", family: "'Plus Jakarta Sans', sans-serif" },
        { name: "Figtree", family: "'Figtree', sans-serif" },
        { name: "Work Sans", family: "'Work Sans', sans-serif" }
      ]
    },
    {
      name: "Elegant Serif",
      fonts: [
        { name: "Playfair Display", family: "'Playfair Display', serif" },
        { name: "Merriweather", family: "'Merriweather', serif" },
        { name: "Cormorant Garamond", family: "'Cormorant Garamond', serif" },
        { name: "Libre Baskerville", family: "'Libre Baskerville', serif" },
        { name: "EB Garamond", family: "'EB Garamond', serif" },
        { name: "Noto Serif", family: "'Noto Serif', serif" }
      ]
    },
    {
      name: "Slab Serif",
      fonts: [
        { name: "Roboto Slab", family: "'Roboto Slab', serif" },
        { name: "Arvo", family: "'Arvo', serif" },
        { name: "Zilla Slab", family: "'Zilla Slab', serif" },
        { name: "Bitter", family: "'Bitter', serif" },
        { name: "Courier Prime", family: "'Courier Prime', monospace" },
        { name: "IBM Plex Serif", family: "'IBM Plex Serif', serif" }
      ]
    },
    {
      name: "Display & Headlines",
      fonts: [
        { name: "Bebas Neue", family: "'Bebas Neue', cursive" },
        { name: "Anton", family: "'Anton', sans-serif" },
        { name: "Caveat", family: "'Caveat', cursive" },
        { name: "Abril Fatface", family: "'Abril Fatface', cursive" },
        { name: "Rubik", family: "'Rubik', sans-serif" },
        { name: "Staatliches", family: "'Staatliches', cursive" }
      ]
    },
    {
      name: "Handwriting & Script",
      fonts: [
        { name: "Pacifico", family: "'Pacifico', cursive" },
        { name: "Dancing Script", family: "'Dancing Script', cursive" },
        { name: "Sacramento", family: "'Sacramento', cursive" },
        { name: "Great Vibes", family: "'Great Vibes', cursive" },
        { name: "Satisfy", family: "'Satisfy', cursive" },
        { name: "Marck Script", family: "'Marck Script', cursive" }
      ]
    },
    {
      name: "Monospace & Code",
      fonts: [
        { name: "JetBrains Mono", family: "'JetBrains Mono', monospace" },
        { name: "Source Code Pro", family: "'Source Code Pro', monospace" },
        { name: "Fira Code", family: "'Fira Code', monospace" },
        { name: "Space Mono", family: "'Space Mono', monospace" },
        { name: "IBM Plex Mono", family: "'IBM Plex Mono', monospace" },
        { name: "Roboto Mono", family: "'Roboto Mono', monospace" }
      ]
    },
    {
      name: "Unique & Decorative",
      fonts: [
        { name: "Comfortaa", family: "'Comfortaa', cursive" },
        { name: "Righteous", family: "'Righteous', cursive" },
        { name: "Patua One", family: "'Patua One', cursive" },
        { name: "Bungee", family: "'Bungee', cursive" },
        { name: "Quicksand", family: "'Quicksand', sans-serif" },
        { name: "Josefin Sans", family: "'Josefin Sans', sans-serif" }
      ]
    }
  ];

  // Update the editor mount handler
  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
  };

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  };

  // Adjust textarea height when input changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [userInput]);

  // Animated placeholder when no messages
  useEffect(() => {
    if (messages.length === 0 && !userInput.trim()) {
      const placeholderTexts = [
        'Clone yoursiteurl.com',
        'Redesign yoursiteurl.com',
        'Create a landing page',
        'Design a portfolio site',
        'Build an e-commerce store'
      ];

      let currentTextIndex = 0;
      let isActive = true;
      let currentTimeout: NodeJS.Timeout | null = null;

      const typeText = (text: string, index: number = 0) => {
        if (!isActive) return;

        if (index <= text.length) {
          setAnimatedPlaceholder(text.slice(0, index));

          if (index < text.length) {
            currentTimeout = setTimeout(() => typeText(text, index + 1), 100);
          } else {
            // Finished typing, wait then delete
            currentTimeout = setTimeout(() => {
              if (isActive) {
                deleteText(text, text.length);
              }
            }, 2000);
          }
        }
      };

      const deleteText = (text: string, index: number) => {
        if (!isActive) return;

        if (index >= 0) {
          setAnimatedPlaceholder(text.slice(0, index));

          if (index > 0) {
            currentTimeout = setTimeout(() => deleteText(text, index - 1), 50);
          } else {
            // Finished deleting, move to next text
            currentTextIndex = (currentTextIndex + 1) % placeholderTexts.length;
            currentTimeout = setTimeout(() => {
              if (isActive) {
                typeText(placeholderTexts[currentTextIndex]);
              }
            }, 500);
          }
        }
      };

      // Start the animation
      setIsAnimationActive(true);
      typeText(placeholderTexts[currentTextIndex]);

      // Cleanup function
      return () => {
        isActive = false;
        if (currentTimeout) {
          clearTimeout(currentTimeout);
        }
        setAnimatedPlaceholder('');
        setIsAnimationActive(false);
      };
    } else {
      setAnimatedPlaceholder('');
      setIsAnimationActive(false);
    }
  }, [messages.length, userInput]);

  const getAllPages = useCallback(async () => {
    if (!projectId) {
      // If no project ID, mark as loaded but with no pages to show welcome screen
      setProjectLoaded(true);
      return [];
    }

    try {
      const option = {
        method: 'GET',
        url: '/api/v1/pages/project/' + projectId,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      };
      const res = await axios(option);
      const fetchedPages = res.data.data.pages;
      setPages(fetchedPages);
      // Mark project as loaded after getting pages
      setProjectLoaded(true);

      dispatch({ type: 'GET_MESSAGES_REMAINING', payload: res.data.data.messagesRemaining });

      // Determine which page to set as active
      let pageToActivate: string | null = null;

      // First priority: Use pageId from URL if valid
      if (pageId && fetchedPages.some((page: Page) => page._id === pageId)) {
        pageToActivate = pageId;
        // console.log('Setting page from URL as active:', pageId);
      }
      // Second priority: Keep current active page if valid
      else if (activePage && fetchedPages.some((page: Page) => page._id === activePage)) {
        pageToActivate = activePage;
        // console.log('Keeping current page as active:', activePage);
      }
      // Fallback: Set first page as active
      else if (fetchedPages.length > 0) {
        pageToActivate = fetchedPages[0]._id;
        // console.log('Setting first page as active:', fetchedPages[0]._id);
      }

      // Set the active page
      if (pageToActivate) {
        setActivePage(pageToActivate);

        // If the page has HTML content, load it
        const selectedPage = fetchedPages.find((page: Page) => page._id === pageToActivate);
        if (selectedPage && selectedPage.html) {
          try {
            const response = await fetch(`/api/v1/html/${selectedPage.html}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });

            if (response.ok) {
              let htmlContent = await response.text();
              setPreviewHtml(htmlContent);
              setPreviousSavedHtml(htmlContent);

              // Update iframe content
              if (iframeRef.current) {
                const doc = iframeRef.current.contentDocument;
                if (doc) {
                  doc.open();
                  doc.write(addUibrainxSelectorPopup(htmlContent));
                  doc.close();
                }
              }
            }
          } catch (error) {
            console.error('Error loading page HTML:', error);
          }
        }
      }

      return fetchedPages;
    } catch (error: any) {
      setError(error.response.data.error);
      setOpenSnackbar(true);
      // Still mark project as loaded even if there's an error
      setProjectLoaded(true);
      return [];
    }
  }, [projectId, pageId, iframeRef, dispatch]);

  useEffect(() => {
    if (projectId) {
      getAllPages();
    }
  }, [getAllPages]);

  useEffect(() => {
    const getProject = async () => {
      if (projectId) {
        const option = {
          method: 'GET',
          url: '/api/v1/projects/' + projectId,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        };
        const res = await axios(option);
        setProject(res.data.data.project);
      }
    };
    getProject();
  }, [projectId]);

  const updatePage = useCallback(
    debounce(async () => {
      if (pageEdited._id === '') return;
      try {
        const option = {
          method: 'PUT',
          url: '/api/v1/pages/' + pageEdited._id,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          data: pageEdited,
        };
        await axios(option);
      } catch (error: any) {
        setError(error.response.data.error);
        setOpenSnackbar(true);
      }
    }, 300),
    [pageEdited]
  );

  const handleChangePage = (e: any, id: any) => {
    const newPages = pages.map((page: any) => {
      if (page._id === id) {
        page.name = e.target.value;
        setPageEdited(page);
      }
      return page;
    });
    updatePage();
    setPages(newPages);
  };

  const handleClickPage = (id: any) => {
    // Set active page before navigation
    setActivePage(id);
    navigate(`/projects/${projectId}/${id}`, { replace: true });
  };

  const goBackToProjects = () => {
    navigate('/projects');
  };

  const handleDeletePage = async (id: any) => {
    try {
      const option = {
        method: 'DELETE',
        url: '/api/v1/pages/' + id,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      };
      await axios(option);

      // First, find the current index of the page we're deleting
      const currentIndex = pages.findIndex((page: any) => page._id === id);
      const newPages = pages.filter((page: any) => page._id !== id);
      setPages(newPages);

      // Only switch to another page if the deleted page was active
      if (activePage === id && newPages.length > 0) {
        // If it's not the first page, go to previous page, otherwise go to the first available page
        const newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
        const nextActivePage = newPages[newIndex]._id;

        // Set the active page
        setActivePage(nextActivePage);

        // Update the URL to reflect the new active page
        if (projectId) {
          navigate(`/projects/${projectId}/${nextActivePage}`, { replace: true });
        }
      } else if (newPages.length === 0) {
        // If no pages left, clear the active page
        setActivePage(null);
      }
    } catch (error: any) {
      setError(error.response.data.error);
      setOpenSnackbar(true);
    }
  };

  const handleChange = (e: any) => {
    setUrl(e.target.value);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    try {
      const option = {
        method: 'POST',
        url: '/api/v1/pages',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          url: url,
          projectId: projectId,
        },
      };
      const res = await axios(option);
      setPages([...pages, res.data.data.page]);
      setUrl('');
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setError(error.response.data.error);
      setOpenSnackbar(true);
    }
  };

  const handleUpdateHtml = async (htmlId: any, pageId: any) => {
    setFetching(pageId);
    try {
      const option = {
        method: 'PUT',
        url: '/api/v1/html/' + htmlId,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          htmlId: htmlId,
        },
      };
      const res = await axios(option);
      setPages(res.data.data.pages);
      setFetching('');
    } catch (error: any) {
      setFetching('');
      setError(error.response.data.error);
      setOpenSnackbar(true);
    }
  };

  // Add a state variable for tracking image analysis
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    try {
      // Create a FormData object to send the file
      const formData = new FormData();
      formData.append('image', file);

      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found!');
        setOpenSnackbar(true);
        setIsUploading(false);
        return;
      }

      // Send the image directly to the backend for upload and analysis
      setIsAnalyzingImage(true);

      const response = await fetch('/api/v1/messages/analyze-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Unable to process image. Please try again.');
      }

      const data = await response.json();

      if (data.status !== 'success' || !data.data || !data.data.scrapeId) {
        throw new Error('Invalid response from server');
      }

      const scrapeId = data.data.scrapeId;

      const fetchAnalysis = async (): Promise<any> => {
        const responseAnalysis = await fetch(`/api/v1/messages/analysis-scrape/${scrapeId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const dataAnalysis = await responseAnalysis.json();
        if (dataAnalysis.status === 'success' && dataAnalysis.data && dataAnalysis.data.analysis) {
          return dataAnalysis;
        }
        return null;
      };

      // Poll for analysis results
      let dataAnalysis: any = null;
      const interval = setInterval(async () => {
        try {
          const result = await fetchAnalysis();
          if (result) {
            clearInterval(interval);
            dataAnalysis = result;
          }
        } catch (error) {
          console.error('Error fetching analysis:', error);
        }
      }, 5000);

      // Wait for the analysis to complete (with a reasonable timeout)
      const timeout = 180000; // 3 minutes timeout
      const startTime = Date.now();

      while (!dataAnalysis && (Date.now() - startTime) < timeout) {
        await new Promise(resolve => setTimeout(resolve, 100)); // Small delay to prevent busy waiting
      }

      clearInterval(interval);

      if (!dataAnalysis) {
        throw new Error('Analysis timeout - please try again');
      }

      // Create a local object URL for the uploaded file to display in the UI
      const imageUrl = URL.createObjectURL(file);
      setUploadedImages(prev => [...prev, imageUrl]);

      setUserInput(prev => {
        const analysisPrefix = prev.trim() ? '\n\n' : '';
        const newText = prev + analysisPrefix + dataAnalysis.data.analysis;

        // Update URL detection with the new text
        const urls = detectUrls(newText);
        setDetectedUrls(urls);

        return newText;
      });
    } catch (error) {
      console.error('Error with image:', error);
      setError('Unable to process image. Please try again.');
      setOpenSnackbar(true);
    } finally {
      setIsUploading(false);
      setIsAnalyzingImage(false);
    }
  };

  const handleRemovePreview = () => {
    setUploadedImages([]);

    // Reset the file input so we can select the same file again
    const fileInput = document.getElementById('image-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Function to detect URLs in text with improved regex
  const detectUrls = (text: string) => {
    if (!text) return [];

    // Simpler regex that can detect all domain types including single-character domains like a.com
    const urlRegex = /(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(\/\S*)?/gi;
    const matches = text.match(urlRegex) || [];

    if (matches.length === 0) return [];

    // Debug output
    // console.log('Raw URL matches:', matches);

    // Use a Map with normalized domains as keys to ensure uniqueness
    const domainMap = new Map();

    // First pass: normalize and group by domain
    matches.forEach(url => {
      // Add https:// if protocol is missing
      const fullUrl = (!url.startsWith('http://') && !url.startsWith('https://'))
        ? 'https://' + url
        : url;

      // Extract and normalize the domain
      const domain = extractDomain(fullUrl);

      // Keep track of the URL by domain
      // If we already have this domain, don't replace it
      if (!domainMap.has(domain)) {
        domainMap.set(domain, fullUrl);
      }
    });

    // Convert to array and return
    const uniqueUrls = Array.from(domainMap.values());
    // console.log('Final unique URLs:', uniqueUrls);
    return uniqueUrls;
  };

  // For debugging
  // useEffect(() => {
  //   console.log('URL detection state:', { 
  //     showUrlActions, 
  //     detectedUrls,
  //     hasUrls: detectedUrls.length > 0
  //   });
  // }, [showUrlActions, detectedUrls]);

  // Test URL detection on component mount
  useEffect(() => {
    // Test with a sample URL including single-character domains
    const testText = "Check out google.com, x.com, a.com, b.com and https://example.com";
    const detected = detectUrls(testText);
    // console.log('Test URL detection:', detected);
  }, []);

  // Handle input change with URL detection
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setUserInput(value);

    // Always detect URLs from the current input value only
    // This ensures we don't accumulate URLs from previous states
    const currentUrls = detectUrls(value);

    if (currentUrls.length > 0) {
      // console.log('Current detected URLs:', currentUrls);
      setDetectedUrls(currentUrls); // Directly set the new URLs, don't merge with previous state
      setShowUrlActions(true);
    } else {
      // No URLs in current input, clear the URL list
      setDetectedUrls([]);
      setShowUrlActions(false);
    }

    // Adjust textarea height
    adjustTextareaHeight();
  };

  // Extract domain name from URL for display
  const extractDomain = (url: string) => {
    try {
      // Remove protocol and path to get just the domain
      let domain = url.replace(/(https?:\/\/)?(www\.)?/i, '');
      // Remove everything after the first slash
      domain = domain.split('/')[0];
      // Remove port if present (e.g., :8080)
      domain = domain.replace(/:\d+$/, '');
      // Convert to lowercase for consistent comparison
      domain = domain.toLowerCase();
      return domain;
    } catch (e) {
      console.error('Error extracting domain:', e);
      return url;
    }
  };

  // Handle getting style from URL
  const handleGetStyle = async (url: string) => {
    // Set action in progress to show loading state
    setActionInProgress({ url, action: 'style' });

    try {
      // Call backend to scrape the website with puppeteer
      // console.log(`Calling backend to get style from ${url}`);

      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/messages/scrape', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          url,
          action: 'style'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to get style: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status !== 'success' || !data.data || !data.data.result) {
        throw new Error('Invalid response from server');
      }

      // Add the action to selected actions
      setSelectedActions(prev => {
        const urlActions = prev[url] || [];
        if (!urlActions.includes('style')) {
          return {
            ...prev,
            [url]: [...urlActions, 'style']
          };
        }
        return prev;
      });

      // Format the style data for the prompt
      const styleData = data.data.result;
      const fonts = styleData.fonts?.slice(0, 3).join(', ') || 'None detected';

      let headingsText = 'None detected';
      if (Object.keys(styleData.headings || {}).length > 0) {
        headingsText = Object.entries(styleData.headings)
          .map(([tag, style]: [string, any]) => `${tag} (${style.fontSize}, ${style.fontWeight})`)
          .join(', ');
      }

      // Update the user input with the scraped data
      setUserInput(prev => {
        // Only append if not already in the input
        if (!prev.includes(`Style data from ${url}`)) {
          return prev + (prev ? '\n\n' : '') +
            `Typography:\n` +
            `- Fonts: ${fonts}\n` +
            `- Headings: ${headingsText}\n`;
        }
        return prev;
      });
    } catch (error) {
      console.error('Error getting style:', error);
      setError(error instanceof Error ? error.message : String(error));
      setOpenSnackbar(true);
    } finally {
      // Clear the action in progress
      setActionInProgress(null);
    }
  };

  // Handle get text content action
  const handleGetTextContent = async (url: string) => {
    // Set action in progress to show loading state
    setActionInProgress({ url, action: 'content' });

    try {
      // Call backend to scrape the website with puppeteer
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/messages/scrape', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          url,
          action: 'content'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to get content: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status !== 'success' || !data.data || !data.data.result) {
        throw new Error('Invalid response from server');
      }

      // Add the action to selected actions
      setSelectedActions(prev => {
        const urlActions = prev[url] || [];
        if (!urlActions.includes('content')) {
          return {
            ...prev,
            [url]: [...urlActions, 'content']
          };
        }
        return prev;
      });

      // Format the content data for the prompt - only extract key images as requested
      const contentData = data.data.result;

      // Get key images
      const images = contentData.images
        ?.slice(0, 10)  // Increased from 5 to 10 images
        .map((img: any) => `- ${img.alt || 'No alt text'}: ${img.src}`)
        .join('\n');

      // Update the user input with only the scraped image data
      setUserInput(prev => {
        // Only append if not already in the input
        if (!prev.includes(`Images from ${url}`)) {
          return prev + (prev ? '\n\n' : '') +
            `Images from ${url}:\n` +
            (images ? `${images}\n\n` : 'No images found.\n\n');
        }
        return prev;
      });

      try {
        // Automatically capture screenshot without asking
        await handleCaptureScreenshot(url);
      } catch (screenshotError) {
        console.error('Screenshot process failed:', screenshotError);
        // Continue execution even if screenshot fails
      } finally {
        // Clear the action in progress only after everything is done
        setActionInProgress(null);
      }
    } catch (error) {
      console.error('Error getting content:', error);
      setError(error instanceof Error ? error.message : String(error));
      setOpenSnackbar(true);

      // Clear the action in progress if there's an error
      setActionInProgress(null);
    }
  };

  // New function to handle screenshot capture and analysis
  const handleCaptureScreenshot = async (url: string) => {
    // Don't set a new action in progress here, as we're already in the content action
    // setActionInProgress({url, action: 'screenshot'});

    try {
      // First capture the screenshot using puppeteer on the backend
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/messages/screenshot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ url })
      });

      if (!response.ok) {
        throw new Error(`Failed to capture screenshot: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status !== 'success' || !data.data || !data.data.imageUrl) {
        throw new Error('Invalid response from screenshot capture');
      }

      // Now analyze the captured screenshot
      const imageUrl = data.data.imageUrl;

      // Show a loading message in the input
      setUserInput(prev => prev + 'Analyzing screenshot, please wait...');

      const analysisResponse = await fetch('/api/v1/messages/analyze-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ image: imageUrl })
      });

      if (!analysisResponse.ok) {
        throw new Error(`Failed to analyze screenshot: ${analysisResponse.statusText}`);
      }

      const analysisData = await analysisResponse.json();

      if (!analysisData.data || !analysisData.data.analysis) {
        throw new Error('Invalid response from image analysis');
      }

      // Replace the loading message with the analysis
      setUserInput(prev =>
        prev.replace('Analyzing screenshot, please wait...',
          `Screenshot Analysis for ${url}:\n\n${analysisData.data.analysis}`)
      );

    } catch (error) {
      console.error('Error with screenshot:', error);
      setError(error instanceof Error ? error.message : String(error));
      setOpenSnackbar(true);

      // Remove any loading message
      setUserInput(prev => prev.replace('Analyzing screenshot, please wait...', ''));

      // Re-throw error to be handled by the parent function
      throw error;
    }
  };

  // Helper function to check if an action is selected
  const isActionSelected = (url: string, action: string) => {
    return selectedActions[url]?.includes(action) || false;
  };

  // Helper function to check if an action is in progress
  const isActionInProgress = (url: string, action: string) => {
    return actionInProgress?.url === url && actionInProgress?.action === action;
  };

  // Reset selected actions when form is submitted or cleared
  const resetChatForm = () => {
    setUserInput('');
    setUploadedImages([]);
    setDetectedUrls([]);
    setShowUrlActions(false);
    setSelectedActions({});
    setStreamingSections({ sections: [], currentSection: -1 }); // Reset streaming sections

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    const fileInput = document.getElementById('image-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // URL action card component
  const UrlActionCard = useMemo(() => {
    if (!showUrlActions || detectedUrls.length === 0) return null;

    // The URLs should already be unique by this point, so we can use them directly
    // console.log('URLs for display in action card:', detectedUrls);

    // Check if any of the URLs is from Figma
    const hasFigmaUrl = detectedUrls.some(url => url.includes('figma.com'));

    return (
      <div className="url-actions-container">
        <div className="url-actions-promo">
          <div className="url-actions-promo-content">
            Need to clone a website exactly? Try <a href="https://softlite.io/clonewebx/" target="_blank" rel="noopener noreferrer">ClonewebX</a>
          </div>
        </div>

        {hasFigmaUrl && (
          <div className="url-actions-promo">
            <div className="url-actions-promo-content">
              Convert your Figma designs to WordPress or Webflow with <a href="https://softlite.io/figwebx/" target="_blank" rel="noopener noreferrer">FigwebX</a>
            </div>
          </div>
        )}

        {/* {detectedUrls.map((url, index) => (
          <div key={`url-${index}`} className="url-actions-card">
            <div className="url-actions-header">
              <span className="url-actions-icon">
                <Iconify icon="material-symbols:globe" width={16} height={16} />
              </span>
              <span className="url-actions-domain">{extractDomain(url)}</span>
            </div>
            <div className="url-actions-buttons">
              <button 
                className={`url-actions-button ${isActionSelected(url, 'redesign') ? 'url-actions-button--selected' : ''}`}
                onClick={() => handleRedesignUrl(url)}
                disabled={isActionInProgress(url, 'redesign')}
              >
                {isActionInProgress(url, 'redesign') ? (
                  <span className="url-actions-button-loading">
                    <Iconify icon="material-symbols:sync" width={16} height={16} style={{ animation: 'spin 1s linear infinite' }} />
                  </span>
                ) : isActionSelected(url, 'redesign') ? (
                  <span className="url-actions-button-checked">
                    <Iconify icon="material-symbols:check-circle-outline" width={16} height={16} />
                  </span>
                ) : null}
                <span>Click to Redesign</span>
              </button>
              <button 
                className={`url-actions-button ${isActionSelected(url, 'clone') ? 'url-actions-button--selected' : ''}`}
                onClick={() => handleCloneUrl(url)}
                disabled={isActionInProgress(url, 'clone')}
              >
                {isActionInProgress(url, 'clone') ? (
                  <span className="url-actions-button-loading">
                    <Iconify icon="material-symbols:sync" width={16} height={16} style={{ animation: 'spin 1s linear infinite' }} />
                  </span>
                ) : isActionSelected(url, 'clone') ? (
                  <span className="url-actions-button-checked">
                    <Iconify icon="material-symbols:check-circle-outline" width={16} height={16} />
                  </span>
                ) : null}
                <span>Click to Clone</span>
              </button>
            </div>
          </div>
        ))} */}
      </div>
    );
  }, [showUrlActions, detectedUrls, selectedActions, actionInProgress]);

  const scrollIframeToBottom = () => {
    try {
      if (!iframeRef.current || !iframeRef.current.contentDocument) {
        return;
      }

      const doc = iframeRef.current.contentDocument;
      const win = iframeRef.current.contentWindow;

      if (!doc || !doc.body || !win) {
        return;
      }

      // Use multiple approaches for more reliable scrolling

      // 1. First try the standard scrollTo on the window
      win.scrollTo(0, doc.body.scrollHeight);

      // 2. Try scrollIntoView on the last element in the body
      const elements = doc.body.children;
      if (elements && elements.length > 0) {
        const lastElement = elements[elements.length - 1];
        lastElement.scrollIntoView({ behavior: 'auto', block: 'end' });
      }

      // 3. Try setting scrollTop on various elements
      doc.documentElement.scrollTop = doc.documentElement.scrollHeight;
      doc.body.scrollTop = doc.body.scrollHeight;

      // 4. For completeness, try using JS to add a scroll event
      win.dispatchEvent(new CustomEvent('scroll'));

      // console.log('Applied multiple scroll techniques to iframe');
    } catch (error) {
      // console.error('Error scrolling iframe to bottom:', error);
    }
  };

  // Add streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [previousHtml, setPreviousHtml] = useState<string>('');
  const [previousSavedHtml, setPreviousSavedHtml] = useState<string>('');

  // Add streaming sections state
  const [streamingSections, setStreamingSections] = useState<{
    sections: Array<{
      type: 'search' | 'replace' | 'explanation';
      content: string;
      isComplete: boolean;
    }>;
    currentSection: number;
  }>({ sections: [], currentSection: -1 });

  // Ref for streaming code element to enable auto-scroll
  const streamingCodeRef = useRef<HTMLPreElement>(null);

  // Console log state
  const [showConsole, setShowConsole] = useState(true); // Always show console
  const [isConsoleCollapsed, setIsConsoleCollapsed] = useState(false);
  const [consoleLogs, setConsoleLogs] = useState<Array<{
    id: string;
    type: 'log' | 'error' | 'warn' | 'info';
    message: string;
    htmlId: string | null; // Associate with activeHtmlId
  }>>([]);
  const consoleRef = useRef<HTMLDivElement>(null);

  // Auto-collapse/expand console based on log count for current HTML
  useEffect(() => {
    const currentHtmlLogs = consoleLogs.filter(log => log.htmlId === activeHtmlId);
    if (currentHtmlLogs.length === 0) {
      setIsConsoleCollapsed(true); // Collapse when no logs
    } else {
      setIsConsoleCollapsed(false); // Expand when there are logs
    }
  }, [consoleLogs, activeHtmlId]);

  // Auto-scroll streaming code when content updates
  useEffect(() => {
    if (streamingCodeRef.current && streamingSections.currentSection >= 0) {
      const element = streamingCodeRef.current;
      // Scroll to bottom
      element.scrollTop = element.scrollHeight;
    }
  }, [streamingSections]);

  // Update the handleSendMessage function to handle streaming
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if user input is empty or if we're in a processing state
    if ((!userInput.trim() && !uploadedImages.length) || isWaitingResponse || isUploading || isAnalyzingImage) {
      return;
    }

    // Reset URL detection
    setDetectedUrls([]);
    setShowUrlActions(false);

    // Store the current input and images before clearing
    const currentInput = userInput;
    const currentImages = [...uploadedImages];

    // Add user message to history immediately
    const userMessage: Message = {
      type: 'user',
      content: currentInput,
      images: currentImages.length > 0 ? currentImages : undefined
    };

    setHistoryMessages(prev => [...prev, userMessage]);

    // Add AI message placeholder immediately after user message
    const initialAiMessage: Message = {
      type: 'ai',
      content: 'Thinking...',
      explanation: '',
      action: ''
    };

    setHistoryMessages(prev => [...prev, initialAiMessage]);

    // Reset the chat form immediately
    resetChatForm();

    // Set waiting state
    setIsWaitingResponse(true);

    // Save the current HTML before starting the streaming
    const currentHtml = previewHtml;
    setPreviousHtml(currentHtml);

    // close the element select form
    setShowClassForm(false);
    setIsEditMode(false);

    try {
      // Create a new AbortController for this request
      const controller = new AbortController();
      setAbortController(controller);

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/messages/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pageId: activePage,
          projectId,
          content: currentInput,
          images: currentImages,
          html: currentHtml,
          selectedElementId: selectedElementDataId,
          selectedElementHTML: selectedElementHTML,
          model: selectedModel
        }),
        signal: controller.signal // Add the abort signal
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }

      // Set streaming state to true
      setIsStreaming(true);

      let accumulatedCode = '';
      let htmlContent = '';
      let buffer = '';
      let accumulatedAiContent = ''; // Track AI response content before NEW_PAGE_START, UPDATE_PAGE_START or SEARCH_START
      let hasFoundHtmlOrSearchStart = false; // Track if we've hit NEW_PAGE_START, UPDATE_PAGE_START or SEARCH_START
      let allStreamedContent = ''; // Track all content for AI message detection
      let htmlStartFound = false; // Track if NEW_PAGE_START has been found for code accumulation
      let extractedPageName = ''; // Track the extracted page name from NEW_PAGE_START format
      let needsNewPage = false; // Track if we need to create a new page
      let newPageUrl = ''; // Track the URL for the new page

      // Initialize last update time
      const lastUpdateTime = Date.now();
      let lastIframeUpdateTime = lastUpdateTime;

      let startIndex = 0;
      let countNewPageStart = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Convert the chunk to text
        buffer += new TextDecoder().decode(value);

        // Process complete lines from buffer
        while (true) {
          const lineEnd = buffer.indexOf('\n');
          if (lineEnd === -1) break;

          const line = buffer.slice(0, lineEnd).trim();
          buffer = buffer.slice(lineEnd + 1);

          if (line.startsWith('data: ')) {
            try {
              const jsonData = line.slice(6);

              // Skip any empty data or [DONE] message
              if (!jsonData || jsonData === '[DONE]') continue;

              // Check if the JSON string is valid before parsing
              if (!isValidJSON(jsonData)) {
                console.warn('Invalid JSON received:', jsonData.substring(0, 100) + '...');
                continue;
              }

              const data = JSON.parse(jsonData);

              // Handle different types of responses
              if (data.type === 'stream') {
                // Filter out unwanted text content before adding it to the accumulated code
                const filteredContent = removeUnwantedText(data.content);

                // Add to all streamed content for AI message detection
                allStreamedContent += filteredContent;

                // Check if NEW_PAGE_START has been found for code accumulation
                if (allStreamedContent.includes(NEW_PAGE_START) && (allStreamedContent.split(NEW_PAGE_START).length - 1 - countNewPageStart === 1)) {   
                  // console.log('🔍 NEW_PAGE_START detected in streaming content');
                  // Find where NEW_PAGE_START occurs and start accumulating from after it
                  const newPageStartIndex = allStreamedContent.indexOf(NEW_PAGE_START, startIndex);
                  // Find the end of the NEW_PAGE_START line (after the page name and NEW_PAGE_END)
                  const newPageEndIndex = allStreamedContent.indexOf(NEW_PAGE_END, newPageStartIndex);
                  
                  // Only process if we have the complete NEW_PAGE_START...NEW_PAGE_END block
                  if (newPageEndIndex !== -1) {
                    countNewPageStart++;
                    // console.log('🔍 Complete NEW_PAGE block found, processing...');
                    // Reset the editor content to show it's starting fresh
                    htmlStartFound = true;
                    // Extract the filename between NEW_PAGE_START and NEW_PAGE_END
                    const filenameWithExtension = allStreamedContent.substring(
                      newPageStartIndex + NEW_PAGE_START.length, 
                      newPageEndIndex
                    ).trim();
                    
                    // Keep the full page name including .html extension
                    extractedPageName = filenameWithExtension;
                    // console.log('🔍 Extracted page name:', extractedPageName);
                    // console.log('🔍 Filename with extension:', filenameWithExtension);
                    // console.log('🔍 Current pages count:', pages.length);
                    
                    // Check if a page with this URL already exists
                    const expectedPageUrl = filenameWithExtension; // e.g., "home.html"
                    const existingPage = pages.find(page => page.url === expectedPageUrl);
                    // console.log('🔍 Expected page URL:', expectedPageUrl);
                    // console.log('🔍 Existing pages URLs:', pages.map(p => p.url));
                    // console.log('🔍 Existing page found:', !!existingPage);
                    
                    if (!existingPage) {
                      // console.log('🚀 Page with URL', expectedPageUrl, 'does not exist. Creating new page during streaming...');
                      // console.log('🚀 About to call createNewPage with:', extractedPageName, expectedPageUrl);
                      // Create new page immediately during streaming
                      try {
                        await createNewPage(extractedPageName, expectedPageUrl);
                        // console.log('✅ New page created successfully during streaming:', extractedPageName);
                        // Mark that we successfully created the page
                        needsNewPage = false; // Reset since we already created it
                      } catch (error) {
                        console.error('❌ Error creating new page during streaming:', error);
                        // Mark for fallback creation after streaming
                        needsNewPage = true;
                        newPageUrl = expectedPageUrl;
                      }
                    } else {
                      // console.log('🔄 Page with URL', expectedPageUrl, 'already exists. Using existing page.');
                      // Switch to the existing page if it's not already active
                      if (activePage !== existingPage._id) {
                        // console.log('🔄 Switching to existing page:', existingPage._id);
                        setActivePage(existingPage._id);
                        navigate(`/projects/${projectId}/${existingPage._id}`, { replace: true });
                      }
                    }

                    let htmlContent = allStreamedContent.substring(newPageEndIndex + NEW_PAGE_END.length);
                    // Remove any leading whitespace/newlines
                    htmlContent = htmlContent.replace(/^\s*\n/, '');

                    // Remove start with "html"
                    htmlContent = htmlContent.replace(/^html/g, '');

                    // Remove any remaining filename references that might have leaked through
                    htmlContent = htmlContent
                      .replace(
                        new RegExp(
                          NEW_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                            '[\\s\\S]*?' +
                            NEW_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                          'g'
                        ),
                        ''
                      )
                      .replace(/```html/g, '')
                      .replace(/```/g, '')
                      .replace(
                        new RegExp(
                          UPDATE_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                            '[\\s\\S]*?' +
                            UPDATE_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                          'g'
                        ),
                        ''
                      )
                      .replace(
                        new RegExp(
                          SEARCH_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                            '[\\s\\S]*?' +
                            DIVIDER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                            '[\\s\\S]*?' +
                            REPLACE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                          'g'
                        ),
                        ''
                      )
                      .trim();

                    // console.log('accumulatedCode', accumulatedCode);

                    // Trim leading newlines and whitespace but preserve DOCTYPE
                    accumulatedCode = htmlContent.replace(/^\s*\n+/, '').trimStart();
                    
                    // console.log('DEBUG - Initial accumulated code (first 200 chars):', accumulatedCode.substring(0, 200));
                    // console.log('DEBUG - Does accumulated code contain .html?', accumulatedCode.includes('.html'));

                    startIndex = newPageEndIndex + NEW_PAGE_END.length;
                  } else {
                    // console.log('🔍 NEW_PAGE_END not found yet, waiting for complete block...');
                  }
                } else if (htmlStartFound) {
                  // We've found NEW_PAGE_START, so accumulate HTML content
                  // Filter out any remaining markers and filename patterns from the content
                  let cleanContent = filteredContent
                  .replace(
                    new RegExp(
                      NEW_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                        '[\\s\\S]*?' +
                        NEW_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                      'g'
                    ),
                    ''
                  )
                  .replace(/```html/g, '')
                  .replace(/```/g, '')
                  .replace(
                    new RegExp(
                      UPDATE_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                        '[\\s\\S]*?' +
                        UPDATE_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                      'g'
                    ),
                    ''
                  )
                  .replace(
                    new RegExp(
                      SEARCH_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                        '[\\s\\S]*?' +
                        DIVIDER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                        '[\\s\\S]*?' +
                        REPLACE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                      'g'
                    ),
                    ''
                  )
                  
                  accumulatedCode += cleanContent;
                }

                // Don't trim the accumulated code during streaming to preserve DOCTYPE
                // console.log('accumulatedCode', accumulatedCode);

                // Check if we've hit NEW_PAGE_START, UPDATE_PAGE_START, or SEARCH_START for AI message
                if (!hasFoundHtmlOrSearchStart) {
                  if (allStreamedContent.includes(NEW_PAGE_START) || allStreamedContent.includes(UPDATE_PAGE_START) || allStreamedContent.includes(SEARCH_START)) {
                    setPreviewHtml("");

                    if (allStreamedContent.includes(SEARCH_START)) {
                      setCodeLoading(true);
                      // Initialize streaming sections for SEARCH_START pattern
                      setStreamingSections({ sections: [], currentSection: -1 });
                    }

                    hasFoundHtmlOrSearchStart = true;

                    // Extract content before NEW_PAGE_START, UPDATE_PAGE_START, or SEARCH_START
                    const newPageIndex = allStreamedContent.indexOf(NEW_PAGE_START);
                    const updatePageIndex = allStreamedContent.indexOf(UPDATE_PAGE_START);
                    const searchIndex = allStreamedContent.indexOf(SEARCH_START);
                    let cutoffIndex = -1;

                    const indices = [newPageIndex, updatePageIndex, searchIndex].filter(i => i !== -1);
                    if (indices.length > 0) {
                      cutoffIndex = Math.min(...indices);
                    }

                    if (cutoffIndex !== -1) {
                      // Include all content up to the marker (no buffer limitation)
                      accumulatedAiContent = allStreamedContent.substring(0, cutoffIndex);
                    }
                  } else {
                    // We haven't hit NEW_PAGE_START, UPDATE_PAGE_START or SEARCH_START yet
                    // Keep a buffer of 15 characters to avoid showing partial markers
                    const bufferSize = 15;
                    if (allStreamedContent.length > bufferSize) {
                      accumulatedAiContent = allStreamedContent.substring(0, allStreamedContent.length - bufferSize);
                    } else {
                      // If content is less than buffer size, show it all
                      accumulatedAiContent = allStreamedContent;
                    }
                  }
                } else if (allStreamedContent.includes(SEARCH_START)) {
                  // If we've already found a SEARCH_START, look for all sections
                  let currentSectionToShow = {
                    type: 'search' as 'search' | 'replace' | 'explanation',
                    content: '',
                    isComplete: false
                  };

                  // Find the current active section by analyzing all blocks
                  const firstSearchStart = allStreamedContent.indexOf(SEARCH_START);
                  if (firstSearchStart !== -1) {
                    // Extract ALL explanation content (before, between, and after search/replace blocks)
                    let explanationContent = '';

                    // Get content before first SEARCH_START
                    const beforeFirst = allStreamedContent.substring(0, firstSearchStart).trim();
                    if (beforeFirst) {
                      explanationContent += beforeFirst;
                    }

                    // Extract content between and after search/replace blocks
                    let parsePosition = 0;
                    while (true) {
                      const searchStartIndex = allStreamedContent.indexOf(SEARCH_START, parsePosition);
                      if (searchStartIndex === -1) break;

                      const replaceEndIndex = allStreamedContent.indexOf(REPLACE_END, searchStartIndex);
                      if (replaceEndIndex === -1) break;

                      // Move position to after this block
                      parsePosition = replaceEndIndex + REPLACE_END.length;

                      // Look for content between this block and the next
                      const nextSearchStart = allStreamedContent.indexOf(SEARCH_START, parsePosition);
                      let betweenContent = '';

                      if (nextSearchStart !== -1) {
                        // Content between this block and next block
                        betweenContent = allStreamedContent.substring(parsePosition, nextSearchStart).trim();
                      } else {
                        // Content after the last block
                        betweenContent = allStreamedContent.substring(parsePosition).trim();
                      }

                      if (betweenContent) {
                        if (explanationContent) explanationContent += '\n\n';
                        explanationContent += betweenContent;
                      }

                      if (nextSearchStart === -1) break;
                    }

                    // Set accumulatedAiContent to all explanation content
                    accumulatedAiContent = explanationContent || 'Updated HTML code';

                    // Find all search/replace blocks and determine which one is currently active
                    let position = 0;
                    let blockIndex = 0;
                    let foundActiveSection = false;

                    while (!foundActiveSection) {
                      const searchStartIndex = allStreamedContent.indexOf(SEARCH_START, position);
                      if (searchStartIndex === -1) {
                        // No more search blocks
                        break;
                      }

                      const dividerIndex = allStreamedContent.indexOf(DIVIDER, searchStartIndex);
                      const replaceEndIndex = allStreamedContent.indexOf(REPLACE_END, dividerIndex !== -1 ? dividerIndex : searchStartIndex);

                      if (dividerIndex === -1) {
                        // This search block is still in progress
                        currentSectionToShow = {
                          type: 'search',
                          content: allStreamedContent.substring(searchStartIndex + SEARCH_START.length).trim(),
                          isComplete: false
                        };
                        foundActiveSection = true;
                      } else if (replaceEndIndex === -1) {
                        // This search block is complete, replace block is in progress
                        currentSectionToShow = {
                          type: 'replace',
                          content: allStreamedContent.substring(dividerIndex + DIVIDER.length).trim(),
                          isComplete: false
                        };
                        foundActiveSection = true;
                      } else {
                        // This block is complete, move to next block
                        position = replaceEndIndex + REPLACE_END.length;
                        blockIndex++;
                        // Continue to check next block
                      }
                    }

                    // If no active section found, all blocks are complete
                    if (!foundActiveSection) {
                      currentSectionToShow = { type: 'search', content: '', isComplete: true };
                    }
                  }

                  // Update streaming sections with current active section
                  if (currentSectionToShow.content) {
                    setStreamingSections({
                      sections: [currentSectionToShow],
                      currentSection: 0
                    });
                  } else {
                    setStreamingSections({
                      sections: [],
                      currentSection: -1
                    });
                  }
                }

                // Clean up any remaining markers from accumulated content
                // Preserve line breaks while removing markers
                accumulatedAiContent = accumulatedAiContent
                  .replace(
                    new RegExp(
                      NEW_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                        '[\\s\\S]*?' +
                        NEW_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                      'g'
                    ),
                    ''
                  )
                  .replace(/```html[\s\S]*?```/g, '')
                  .replace(/```html/g, '')
                  .replace(/```/g, '')
                  .replace(
                    new RegExp(
                      UPDATE_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                        '[\\s\\S]*?' +
                        UPDATE_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                      'g'
                    ),
                    ''
                  )
                  .replace(
                    new RegExp(
                      SEARCH_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                        '[\\s\\S]*?' +
                        DIVIDER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                        '[\\s\\S]*?' +
                        REPLACE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                      'g'
                    ),
                    ''
                  )
                  .replace(/(?:^|\s)<<+[^\n]*/g, "")
                  .replace(/\n{2,}/g, "\n")
                  .replace(/^\s+/, '') // Remove leading whitespace but preserve line breaks within content
                  .replace(/\s+$/, ''); // Remove trailing whitespace but preserve line breaks within content

                // Update AI message in history with accumulated content
                setHistoryMessages(prev => {
                  const newMessages = [...prev];
                  const lastIndex = newMessages.length - 1;
                  if (newMessages[lastIndex] && newMessages[lastIndex].type === 'ai') {
                    newMessages[lastIndex] = {
                      ...newMessages[lastIndex],
                      content: accumulatedAiContent || 'Generating...'
                    };
                  }
                  return newMessages;
                });

                // Auto-scroll chat history to bottom after updating AI message
                setTimeout(() => scrollChatHistoryToBottom(), 100);

                // Add to editor with streaming visualization (only if we have HTML content)
                if (editorRef.current && htmlStartFound && accumulatedCode) {
                  // Validate HTML content before setting
                  const validation = validateHtmlContent(accumulatedCode);
                  if (!validation.isValid) {
                    validation.errors.forEach(error => {
                      addConsoleLog('error', `${error}`);
                    });
                  }

                  // Remove start with "html"
                  if (accumulatedCode.trim().startsWith('html')) {
                    accumulatedCode = accumulatedCode.trim().replace(/^html/g, '');
                  }

                  // get previous content if accumulatedCode contains "</html>"
                  if (accumulatedCode.trim().includes('</html>')) {
                    accumulatedCode = accumulatedCode.trim().split('</html>')[0] + '</html>';
                  }

                  // Clean the accumulated code before setting it in the editor
                  let cleanAccumulatedCode = accumulatedCode
                    .replace(
                      new RegExp(
                        NEW_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                          '[\\s\\S]*?' +
                          NEW_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                        'g'
                      ),
                      ''
                    )
                    .replace(/```html/g, '')
                    .replace(/```/g, '')
                    .replace(
                      new RegExp(
                        UPDATE_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                          '[\\s\\S]*?' +
                          UPDATE_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                        'g'
                      ),
                      ''
                    )
                    .replace(
                      new RegExp(
                        SEARCH_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                          '[\\s\\S]*?' +
                          DIVIDER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                          '[\\s\\S]*?' +
                          REPLACE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                        'g'
                      ),
                      ''
                    )

                  // Set the cleaned content in the editor
                  // console.log('DEBUG - Clean accumulated code (first 200 chars):', cleanAccumulatedCode.substring(0, 200));
                  // console.log('DEBUG - Does clean code contain .html?', cleanAccumulatedCode.includes('.html'));
                  
                  editorRef.current.setValue(cleanAccumulatedCode);

                  // Also update the preview HTML
                  setPreviewHtml(cleanAccumulatedCode);

                  // Auto-scroll the Monaco editor to the bottom
                  const lineCount = editorRef.current.getModel()?.getLineCount() || 0;
                  editorRef.current.revealLine(lineCount);
                }

                // Throttle iframe updates to once every 3 seconds during streaming
                const currentTime = Date.now();
                if (currentTime - lastIframeUpdateTime >= 3000) {
                  // Clean accumulated code for iframe updates
                  const cleanCodeForIframe = accumulatedCode
                    .replace(
                      new RegExp(
                        NEW_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                          '[\\s\\S]*?' +
                          NEW_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                        'g'
                      ),
                      ''
                    )
                    .replace(/```html/g, '')
                    .replace(/```/g, '')
                    .replace(
                      new RegExp(
                        UPDATE_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                          '[\\s\\S]*?' +
                          UPDATE_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                        'g'
                      ),
                      ''
                    )
                    .replace(
                      new RegExp(
                        SEARCH_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                          '[\\s\\S]*?' +
                          DIVIDER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                          '[\\s\\S]*?' +
                          REPLACE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                        'g'
                      ),
                      ''
                    )

                  // Update the iframe if we have enough content to show
                  if (cleanCodeForIframe.length > 500) {
                    try {
                      // Apply the streamed content to the iframe
                      if (iframeRef.current && iframeRef.current.contentDocument) {
                        // Only clear and update if we have enough content with a doctype or html tag
                        if (cleanCodeForIframe.includes('<!DOCTYPE') || cleanCodeForIframe.includes('<html')) {
                          // Validate HTML content before writing to iframe
                          const validation = validateHtmlContent(cleanCodeForIframe);
                          if (!validation.isValid) {
                            validation.errors.forEach(error => {
                              addConsoleLog('error', `${error}`);
                            });
                          }

                          const doc = iframeRef.current.contentDocument;
                          doc.open();
                          doc.write(cleanCodeForIframe);
                          doc.close();

                          // Add auto-scroll with a slight delay to ensure content is rendered
                          setTimeout(() => {
                            scrollIframeToBottom();
                            // Try once more after a slightly longer delay for reliability
                            setTimeout(scrollIframeToBottom, 100);
                          }, 50);
                        }
                      }
                    } catch (err) {
                      console.error('Error updating iframe during streaming:', err);
                    }
                  }

                  // Update the last update time
                  lastIframeUpdateTime = currentTime;
                }

                // Hide waiting state once we start receiving content
                setIsWaitingResponse(false);
              } else if (data.type === 'ai' && data.data && data.data.pages && hasFoundHtmlOrSearchStart) {
                const currentPage = data.data.pages.find((page: any) => page._id === activePageRef.current);
                htmlContent = currentPage?.htmlContent;

                // Update the final AI message in history
                setHistoryMessages(prev => {
                  const newMessages = [...prev];
                  const lastIndex = newMessages.length - 1;
                  if (newMessages[lastIndex] && newMessages[lastIndex].type === 'ai') {
                    // Clean the final content to ensure no markers remain
                    const finalContent = (accumulatedAiContent || data.data.explanation || 'Generated HTML content')
                      .replace(
                        new RegExp(
                          NEW_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                            '[\\s\\S]*?' +
                            NEW_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                          'g'
                        ),
                        ''
                      )
                      .replace(/```html[\s\S]*?```/g, '')
                      .replace(
                        new RegExp(
                          UPDATE_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                            '[\\s\\S]*?' +
                            UPDATE_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                          'g'
                        ),
                        ''
                      )
                      .replace(
                        new RegExp(
                          SEARCH_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                            '[\\s\\S]*?' +
                            DIVIDER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                            '[\\s\\S]*?' +
                            REPLACE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                          'g'
                        ),
                        ''
                      )
                      .replace(/^\s+/, '') // Remove leading whitespace but preserve line breaks within content
                      .replace(/\s+$/, ''); // Remove trailing whitespace but preserve line breaks within content

                    newMessages[lastIndex] = {
                      ...newMessages[lastIndex],
                      content: finalContent,
                      projectVersion: data.data.projectVersion || null
                    };
                  }
                  return newMessages;
                });

                // Validate final HTML content
                const validation = validateHtmlContent(htmlContent);
                if (!validation.isValid) {
                  validation.errors.forEach(error => {
                    addConsoleLog('error', `${error}`);
                  });
                } else {
                  handleClearConsole();
                }

                // Just ensure we have the final HTML in the editor
                const htmlContentWithDoctype = htmlContent.includes('<!DOCTYPE html>') ? htmlContent : `<!DOCTYPE html>\n${htmlContent}`;
                setPreviewHtml(htmlContentWithDoctype);
                setPreviousSavedHtml(htmlContentWithDoctype);

                // If we have data about the page update, get the HTML ID and update iframe
                if (currentPage && currentPage.html) {
                  // // Get the HTML ID from the updated page
                  const newHtmlId = currentPage.html;
                  setActiveHtmlId(newHtmlId);

                  // // Update pages array with the new HTML information and extracted page name
                  // setPages((prevPages: Page[]) =>
                  //   prevPages.map(page => {
                  //     if (page._id === data.data.page._id) {
                  //       const updatedPage = { ...data.data.page };
                        
                  //       // If we extracted a page name and the page name was updated, log it
                  //       if (extractedPageName && updatedPage.name && updatedPage.name !== page.name) {
                  //         console.log('Page name updated from', page.name, 'to', updatedPage.name);
                  //       }
                        
                  //       return updatedPage;
                  //     }
                  //     return page;
                  //   })
                  // );

                  if (editorRef.current) {
                    editorRef.current.setValue(htmlContentWithDoctype);
                  }

                  // Reload the iframe with the new HTML
                  reloadIframe(newHtmlId, htmlContent);
                } else {
                  // No page data (or no HTML ID) - use the filtered HTML directly
                  reloadIframe(undefined, htmlContent);
                }

                setPages(data.data.pages);

                dispatch({ type: 'GET_MESSAGES_REMAINING', payload: data.data.messagesRemaining });

                // Hide waiting state once we start receiving content
                setIsWaitingResponse(false);
                setIsStreaming(false);
              } else if (data.type === 'error') {
                setError(data.error || 'Failed to send message');
                setOpenSnackbar(true);
                setIsWaitingResponse(false);
              }
            } catch (error) {
              console.error('Error parsing message:', error);
              // Don't break the loop on a single error, continue processing other messages
            }
          }
        }
      }

      // Handle SEARCH_START pattern for HTML updates (no streaming)
      if (allStreamedContent.includes(SEARCH_START) && !htmlStartFound) {

        // console.log('Processing SEARCH_START pattern for HTML updates');

        let newHtml = currentHtml;
        const updatedLines: number[][] = [];

        // Find all search/replace blocks in the streamed content
        let position = 0;
        let moreBlocks = true;

        while (moreBlocks) {
          const searchStartIndex = allStreamedContent.indexOf(SEARCH_START, position);
          if (searchStartIndex === -1) {
            moreBlocks = false;
            continue;
          }

          const dividerIndex = allStreamedContent.indexOf(DIVIDER, searchStartIndex);
          if (dividerIndex === -1) {
            moreBlocks = false;
            continue;
          }

          const replaceEndIndex = allStreamedContent.indexOf(REPLACE_END, dividerIndex);
          if (replaceEndIndex === -1) {
            moreBlocks = false;
            continue;
          }

          // Extract the search and replace blocks
          const searchBlock = allStreamedContent.substring(
            searchStartIndex + SEARCH_START.length,
            dividerIndex
          ).trim();
          const replaceBlock = allStreamedContent.substring(
            dividerIndex + DIVIDER.length,
            replaceEndIndex
          );

          // console.log('Found search block:', searchBlock);
          // console.log('Found replace block:', replaceBlock);

          // Apply the replacement
          if (searchBlock === "") {
            // Inserting at the beginning
            newHtml = `${replaceBlock}\n${newHtml}`;

            // Track first line as updated
            updatedLines.push([1, replaceBlock.split("\n").length]);
          } else {
            // Find the position of the search block in the HTML
            const blockPosition = newHtml.indexOf(searchBlock);
            if (blockPosition !== -1) {
              // Count lines before the search block
              const beforeText = newHtml.substring(0, blockPosition);
              const startLineNumber = beforeText.split("\n").length;

              // Count lines in search and replace blocks
              const replaceLines = replaceBlock.split("\n").length;

              // Calculate end line (start + length of replaced content)
              const endLineNumber = startLineNumber + replaceLines - 1;

              // Track the line numbers that were updated
              updatedLines.push([startLineNumber, endLineNumber]);

              // Perform the replacement
              newHtml = newHtml.replace(searchBlock, replaceBlock);
              // console.log('Applied replacement - updated lines:', startLineNumber, 'to', endLineNumber);
            } else {
              console.warn('Search block not found in HTML:', searchBlock.substring(0, 100) + '...');
            }
          }

          // Move position to after this block to find the next one
          position = replaceEndIndex + REPLACE_END.length;
        }

        // Update the HTML if changes were made
        // if (newHtml !== currentHtml) {
        //   console.log('newHtml', newHtml);

        //   // console.log('HTML updated via SEARCH_START pattern, updated lines:', updatedLines);

        //   // Update the editor
        //   if (editorRef.current) {
        //     editorRef.current.setValue(newHtml);
        //   }

        //   // Update the preview HTML
        //   setPreviewHtml(newHtml);

        //   // Reload the iframe with the new HTML
        //   reloadIframe(undefined, newHtml);

        //   // Update AI message to show completion
        //   setHistoryMessages(prev => {
        //     const newMessages = [...prev];
        //     const lastIndex = newMessages.length - 1;
        //     if (newMessages[lastIndex] && newMessages[lastIndex].type === 'ai') {
        //       const finalContent = accumulatedAiContent || 'Updated HTML code';
        //       newMessages[lastIndex] = {
        //         ...newMessages[lastIndex],
        //         content: finalContent
        //       };
        //     }
        //     return newMessages;
        //   });
        // }

        setCodeLoading(false);

      }

      // Before streaming completion, ensure we show all AI content if no markers were found
      if (!hasFoundHtmlOrSearchStart && allStreamedContent.length > 0) {
        // Include all content since no NEW_PAGE_START, UPDATE_PAGE_START or SEARCH_START was found
        accumulatedAiContent = allStreamedContent;

        // Clean up any remaining markers from accumulated content
        accumulatedAiContent = accumulatedAiContent
          .replace(
            new RegExp(
              NEW_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                '[\\s\\S]*?' +
                NEW_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
              'g'
            ),
            ''
          )
          .replace(/```html[\s\S]*?```/g, '')
          .replace(/```html/g, '')
          .replace(/```/g, '')
          .replace(
            new RegExp(
              UPDATE_PAGE_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                '[\\s\\S]*?' +
                UPDATE_PAGE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
              'g'
            ),
            ''
          )
          .replace(
            new RegExp(
              SEARCH_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                '[\\s\\S]*?' +
                DIVIDER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                '[\\s\\S]*?' +
                REPLACE_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
              'g'
            ),
            ''
          )
          .replace(/^\s+/, '') // Remove leading whitespace but preserve line breaks within content
          .replace(/\s+$/, ''); // Remove trailing whitespace but preserve line breaks within content

        // Update AI message with final complete content
        setHistoryMessages(prev => {
          const newMessages = [...prev];
          const lastIndex = newMessages.length - 1;
          if (newMessages[lastIndex] && newMessages[lastIndex].type === 'ai') {
            newMessages[lastIndex] = {
              ...newMessages[lastIndex],
              content: accumulatedAiContent || 'Generated response'
            };
          }
          return newMessages;
        });

        // Auto-scroll chat history to bottom after final message update
        setTimeout(() => scrollChatHistoryToBottom(), 100);
      }

      // Streaming completed successfully
      setIsStreaming(false);
      setAbortController(null);
      
      // Check if we need to create a new page based on the AI response (fallback if streaming creation failed)
      if (needsNewPage && extractedPageName && newPageUrl) {
        try {
          await createNewPage(extractedPageName, newPageUrl);
        } catch (error) {
          console.error('❌ Error creating new page after streaming (fallback):', error);
          // Don't show error to user as the main response was successful
        }
      } else {
      }
      
      // Clear streaming sections after completion
      setTimeout(() => {
        setStreamingSections({ sections: [], currentSection: -1 });
      }, 2000); // Keep sections visible for 2 seconds after completion
    } catch (error: any) {
      console.error('Error in handleSendMessage:', error);

      // Check if this was an abort error or another error
      if (error.name === 'AbortError') {
        // console.log('Request was aborted');
      } else {
        setError(error.message || 'Failed to send message');
        setOpenSnackbar(true);
      }

      setIsWaitingResponse(false);
      setIsStreaming(false);
      setAbortController(null);
    }
  };

  const handleOpenInNewTab = () => {
    // First check if we have an active HTML ID that's been explicitly set
    if (activeHtmlId) {
      // Open the HTML using the direct API endpoint with the active HTML ID and add timestamp to prevent caching
      window.open(`/api/v1/html/${activeHtmlId}?t=${Date.now()}&preview=true`, '_blank');
      return;
    }

    // Fall back to finding the current page and get its HTML ID
    const currentPage = pages.find(page => page._id === activePageRef.current);
    if (currentPage && currentPage.html) {
      // Open the HTML using the direct API endpoint with the page's HTML ID and add timestamp to prevent caching
      window.open(`/api/v1/html/${currentPage.html}?t=${Date.now()}&preview=true`, '_blank');
    } else {
      // Final fallback: create a blob from current HTML in the editor
      const blob = new Blob([previewHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 100);
    }
  };

  const handleToggleViewMode = () => {
    setIsMobileView(prev => !prev);
  };

  const handleRefreshIframe = () => {
    // console.log('Manual iframe refresh requested. iframeRef exists:', !!iframeRef.current);
    // console.log('activeHtmlId:', activeHtmlId);
    // console.log('previewHtml length:', previewHtml ? previewHtml.length : 0);

    if (iframeRef.current) {
      try {
        if (activeHtmlId) {
          // If using src attribute, need to reload the iframe
          const url = `/api/v1/html/${activeHtmlId}?getfrom=preview&t=${Date.now()}`;
          // console.log(`Manually refreshing iframe with src: ${url}`);
          iframeRef.current.src = url;
        } else if (previewHtml) {
          // If using srcDoc, we can update the document directly
          // console.log('Manually refreshing iframe with document.write');
          const doc = iframeRef.current.contentDocument;
          if (doc) {
            try {
              // Validate HTML content before writing
              const validation = validateHtmlContent(previewHtml);
              if (!validation.isValid) {
                validation.errors.forEach(error => {
                  addConsoleLog('error', `${error}`);
                });
              }

              doc.open();
              doc.write(previewHtml);
              doc.close();
              // console.log('Manual refresh successful via document.write');
            } catch (writeError) {
              const errorMsg = writeError instanceof Error ? writeError.message : String(writeError);
              addConsoleLog('error', `Manual refresh HTML error: ${errorMsg}`);
              console.error('Error during manual iframe refresh:', writeError);
            }
          } else {
            // Fallback to srcdoc attribute
            // console.log('Manually refreshing iframe with srcdoc');
            try {
              iframeRef.current.srcdoc = previewHtml;
            } catch (srcdocError) {
              const errorMsg = srcdocError instanceof Error ? srcdocError.message : String(srcdocError);
              addConsoleLog('error', `Manual refresh srcdoc error: ${errorMsg}`);
              console.error('Error setting srcdoc during manual refresh:', srcdocError);
            }
          }
        } else {
          console.warn('No content available to refresh iframe');
        }
      } catch (error) {
        console.error('Error during manual iframe refresh:', error);
      }
    } else {
      console.error('iframeRef.current is null, cannot refresh iframe');
    }
  };

  const handleOpenCommitDialog = () => {
    setCommitIsReplace(false);
    setCommitReplaceTemplateId('');
    setOpenCommitDialog(true);
    void fetchTemplates();
  };

  const handleCloseCommitDialog = () => {
    if (isCommittingTemplate) return;
    setOpenCommitDialog(false);
  };

  const handleCommitTemplateConfirm = async () => {
    if (!activeHtmlId) {
      setError('No HTML document to commit.');
      setOpenSnackbar(true);
      return;
    }
    if (commitIsReplace && !commitReplaceTemplateId) {
      setError('Choose a template to replace.');
      setOpenSnackbar(true);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Not authenticated.');
      setOpenSnackbar(true);
      return;
    }
    setIsCommittingTemplate(true);
    try {
      const res = await axios({
        method: 'POST',
        url: '/api/v1/templates',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        data: {
          htmlId: activeHtmlId,
          ...(commitIsReplace && commitReplaceTemplateId ? { templateId: commitReplaceTemplateId } : {}),
        },
      });
      if (res.data?.success) {
        setSuccess(commitIsReplace ? 'Template replaced successfully.' : 'Template created successfully.');
        setOpenSuccessSnackbar(true);
        setOpenCommitDialog(false);
        setCommitReplaceTemplateId('');
        setCommitIsReplace(false);
      } else {
        setError(res.data?.error || 'Failed to commit template.');
        setOpenSnackbar(true);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to commit template.';
      setError(msg);
      setOpenSnackbar(true);
    } finally {
      setIsCommittingTemplate(false);
    }
  };

  const handleToggleConsole = () => {
    setIsConsoleCollapsed(!isConsoleCollapsed);
  };

  const handleClearConsole = () => {
    // Only clear logs for the current activeHtmlId
    setConsoleLogs(prev => prev.filter(log => log.htmlId !== activeHtmlId));
  };

  const handleFixBug = async (log: { id: string; type: string; message: string; htmlId: string | null; }) => {
    // Create a detailed error message for the AI
    const errorContext = `I'm getting this ${log.type} in my console:\n\n"${log.message}"\n\nPlease analyze this error and help me fix it.`;

    // Fill the chat input with the error context
    setUserInput(errorContext);

    // Auto-scroll to the chat input
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Automatically send the message
    setTimeout(() => {
      if (!isWaitingResponse && !isUploading && !isAnalyzingImage) {
        handleSendMessage({ preventDefault: () => { } } as React.FormEvent);
      }
    }, 100);
  };

  const addConsoleLog = (type: 'log' | 'error' | 'warn' | 'info', message: string) => {
    // Check if this exact message already exists for the current HTML
    setConsoleLogs(prev => {
      const isDuplicate = prev.some(log =>
        log.type === type &&
        log.message === message &&
        log.htmlId === activeHtmlId
      );

      if (isDuplicate) {
        return prev; // Don't add duplicate
      }

      const newLog = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type,
        message,
        htmlId: activeHtmlId, // Associate with current activeHtmlId
      };

      return [...prev, newLog];
    });

    // Auto-scroll console to bottom
    setTimeout(() => {
      if (consoleRef.current) {
        consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
      }
    }, 10);
  };

  // Setup console capturing for parent window
  // useEffect(() => {
  //   // Capture errors from the main window (parent)
  //   const handleWindowError = (event: ErrorEvent) => {
  //     const message = event.message || 'Unknown error';
  //     const filename = event.filename || 'unknown';
  //     const lineno = event.lineno || 0;
  //     const colno = event.colno || 0;
  //     addConsoleLog('error', `${message} at ${filename}:${lineno}:${colno}`);
  //   };

  //   const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
  //     addConsoleLog('error', `Unhandled promise rejection: ${event.reason}`);
  //   };

  //   // Also capture errors using window.onerror as a fallback
  //   const originalOnError = window.onerror;
  //   // window.onerror = function(message, source, lineno, colno, error) {
  //   //   addConsoleLog('error', `${message} at ${source}:${lineno}:${colno}`);
  //   //   if (originalOnError) {
  //   //     return originalOnError.call(this, message, source, lineno, colno, error);
  //   //   }
  //   //   return false;
  //   // };

  //   // Add parent window error listeners
  //   // window.addEventListener('error', handleWindowError);
  //   // window.addEventListener('unhandledrejection', handleUnhandledRejection);

  //   // Store original console methods but don't override them yet
  //   const originalConsole = {
  //     log: console.log,
  //     error: console.error,
  //     warn: console.warn,
  //     info: console.info
  //   };

  //   // Only override non-error console methods to avoid interfering with error capture
  //   console.log = (...args: any[]) => {
  //     originalConsole.log.apply(console, args);
  //     addConsoleLog('log', args.map(arg => 
  //       typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
  //     ).join(' '));
  //   };

  //   // console.warn = (...args: any[]) => {
  //   //   originalConsole.warn.apply(console, args);
  //   //   addConsoleLog('warn', args.map(arg => 
  //   //     typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
  //   //   ).join(' '));
  //   // };

  //   console.info = (...args: any[]) => {
  //     originalConsole.info.apply(console, args);
  //     addConsoleLog('info', args.map(arg => 
  //       typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
  //     ).join(' '));
  //   };

  //   // Don't override console.error initially - let error events handle it first
  //   // Add console.error override after a delay to allow error events to be captured first
  //   const errorOverrideTimeout = setTimeout(() => {
  //     console.error = (...args: any[]) => {
  //       originalConsole.error.apply(console, args);
  //       // Only add to console log if it's not already captured by error events
  //       const errorMessage = args.map(arg => 
  //         typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
  //       ).join(' ');
  //       addConsoleLog('error', errorMessage);
  //     };
  //   }, 100);

  //   // Cleanup function
  //   return () => {
  //     window.removeEventListener('error', handleWindowError);
  //     window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  //     window.onerror = originalOnError;
  //     clearTimeout(errorOverrideTimeout);

  //     // Restore original console methods
  //     console.log = originalConsole.log;
  //     console.error = originalConsole.error;
  //     console.warn = originalConsole.warn;
  //     console.info = originalConsole.info;
  //   };
  // }, []);

  // Setup console capturing for iframe
  useEffect(() => {
    const setupConsoleCapture = () => {
      if (!iframeRef.current) return;

      const iframe = iframeRef.current;

      const onLoad = () => {
        try {
          const iframeWindow = iframe.contentWindow as Window & { console: Console };
          const iframeConsole = iframeWindow?.console;

          // Check if we can access the iframe (same-origin)
          const canAccess = (() => {
            try {
              return !!iframeWindow && !!iframeConsole;
            } catch {
              return false;
            }
          })();

          // if (canAccess && iframeConsole) {
          //   // Store original console methods
          //   const originalLog = iframeConsole.log;
          //   const originalError = iframeConsole.error;
          //   const originalWarn = iframeConsole.warn;
          //   const originalInfo = iframeConsole.info;

          //   // Override console methods
          //   iframeConsole.log = (...args: any[]) => {
          //     originalLog.apply(iframeConsole, args);
          //     addConsoleLog('log', args.map(arg =>
          //       typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
          //     ).join(' '));
          //   };

          //   iframeConsole.error = (...args: any[]) => {
          //     originalError.apply(iframeConsole, args);
          //     addConsoleLog('error', args.map(arg => 
          //       typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
          //     ).join(' '));
          //   };

          //   // iframeConsole.warn = (...args: any[]) => {
          //   //   originalWarn.apply(iframeConsole, args);
          //   //   addConsoleLog('warn', args.map(arg => 
          //   //     typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
          //   //   ).join(' '));
          //   // };

          //   iframeConsole.info = (...args: any[]) => {
          //     originalInfo.apply(iframeConsole, args);
          //     addConsoleLog('info', args.map(arg =>
          //       typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
          //     ).join(' '));
          //   };

          //   // Capture JavaScript errors
          //   iframeWindow?.addEventListener('error', (event) => {
          //     const errorMsg = event.message || 'Unknown error';
          //     const filename = event.filename || 'unknown';
          //     const lineno = event.lineno || 0;
          //     const colno = event.colno || 0;
          //     addConsoleLog('error', `${errorMsg} at ${filename}:${lineno}:${colno}`);
          //   });

          //   // Capture unhandled promise rejections
          //   iframeWindow?.addEventListener('unhandledrejection', (event) => {
          //     addConsoleLog('error', `Unhandled promise rejection: ${event.reason}`);
          //   });

          //   // Also try to capture errors from the document
          //   const iframeDoc = iframe.contentDocument;
          //   if (iframeDoc) {
          //     iframeDoc.addEventListener('error', (event) => {
          //       addConsoleLog('error', `Document error: ${event.type}`);
          //     }, true);
          //   }

          //   // Override window.onerror as a fallback
          //   if (iframeWindow) {
          //     const originalOnError = iframeWindow.onerror;
          //     iframeWindow.onerror = function (message, source, lineno, colno, error) {
          //       addConsoleLog('error', `${message} at ${source}:${lineno}:${colno}`);
          //       if (originalOnError) {
          //         return originalOnError.call(this, message, source, lineno, colno, error);
          //       }
          //       return false;
          //     };
          //   }

          //   // Inject additional error capture script
          //   try {
          //     const script = iframeDoc?.createElement('script');
          //     if (script && iframeDoc) {
          //       script.textContent = `
          //         // Additional error capturing
          //         (function() {
          //           // Capture promise rejections
          //           window.addEventListener('unhandledrejection', function(e) {
          //             const errorMsg = 'Unhandled promise rejection: ' + (e.reason ? e.reason.toString() : 'Unknown error');
          //             console.error(errorMsg);
          //             window.parent.postMessage({
          //               type: 'console-log',
          //               logType: 'error',
          //               message: errorMsg
          //             }, '*');
          //           });
                    
          //           // Capture resource loading errors
          //           window.addEventListener('error', function(e) {
          //             if (e.target !== window) {
          //               const errorMsg = 'Resource loading error: ' + e.target.tagName + ' ' + (e.target.src || e.target.href || '');
          //               console.error(errorMsg);
          //               window.parent.postMessage({
          //                 type: 'console-log',
          //                 logType: 'error',
          //                 message: errorMsg
          //               }, '*');
          //             }
          //           }, true);
          //         })();
          //       `;
          //       iframeDoc.head.appendChild(script);
          //     }
          //   } catch (scriptError) {
          //     console.warn('Could not inject error capture script:', scriptError);
          //   }
          // }
        } catch (error) {
          console.warn('Could not setup console capture for iframe:', error);
          // Add a test log to verify console is working
          // addConsoleLog('warn', 'Console capture setup failed - some errors may not be visible');
        }
      };

      iframe.addEventListener('load', onLoad);

      // If iframe is already loaded
      if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
        onLoad();
      }

      return () => {
        iframe.removeEventListener('load', onLoad);
      };
    };

    setupConsoleCapture();
  }, [activeHtmlId, previewHtml]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Only proceed if we're not analyzing an image
      if (!isAnalyzingImage && !isUploading) {
        handleSendMessage(e as unknown as React.FormEvent);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollChatHistoryToBottom = () => {
    const chatHistoryContainer = document.querySelector('.chat-preview__history-messages');
    if (chatHistoryContainer) {
      chatHistoryContainer.scrollTop = chatHistoryContainer.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isWaitingResponse]);

  // Auto-scroll chat history when messages change
  useEffect(() => {
    if (historyMessages.length > 0) {
      setTimeout(() => scrollChatHistoryToBottom(), 50);
    }
  }, [historyMessages]);

  // Add useEffect to handle iframe updates more reliably
  useEffect(() => {
    if (!previewHtml) return;

    // console.log('previewHtml changed, updating iframe with content length:', previewHtml.length);

    // Give a small delay to ensure state updates have propagated
    const timer = setTimeout(() => {
      if (iframeRef.current) {
        try {
          // First try to update the iframe using document.write
          const doc = iframeRef.current.contentDocument;
          if (doc) {
            // console.log('Updating iframe via document.write');
            try {
              doc.open();
              doc.write(addUibrainxSelectorPopup(previewHtml));
              doc.close();
            } catch (writeError) {
              const errorMsg = writeError instanceof Error ? writeError.message : String(writeError);
              addConsoleLog('error', `HTML parsing error: ${errorMsg}`);
              console.error('Error writing to iframe document:', writeError);
            }
          } else {
            // Fallback to using srcdoc attribute
            // console.log('Fallback: Updating iframe via srcdoc attribute');
            try {
              iframeRef.current.srcdoc = previewHtml;
            } catch (srcdocError) {
              const errorMsg = srcdocError instanceof Error ? srcdocError.message : String(srcdocError);
              addConsoleLog('error', `HTML srcdoc error: ${errorMsg}`);
              console.error('Error setting iframe srcdoc:', srcdocError);
            }
          }
        } catch (error) {
          console.error('Error updating iframe from previewHtml:', error);

          // Last resort: Try to reload using src attribute if we have an activeHtmlId
          if (activeHtmlId) {
            // console.log('Last resort: Reloading iframe using src attribute');
            iframeRef.current.src = `/api/v1/html/${activeHtmlId}?getfrom=preview&t=${Date.now()}`;
          }
        }
      } else {
        console.warn('iframeRef.current is null in useEffect');
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [previewHtml, activeHtmlId]);

  // Handle page name edit mode
  const handleStartEditName = (pageId: string) => {
    // First set the active page to the one being edited
    setActivePage(pageId);

    // Then set editing mode after a small delay to ensure page is active
    setTimeout(() => {
      setIsEditingName(pageId);
    }, 50);

  };


  // When a user clicks outside, exit edit mode
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditingName) {
        const nameInputs = document.querySelectorAll('.page-tabs__name-input');
        let shouldExit = true;

        nameInputs.forEach(input => {
          if (input.contains(event.target as Node)) {
            shouldExit = false;
          }
        });

        if (shouldExit) {
          setIsEditingName(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditingName]);

  // Function to create a new page
  const createNewPage = async (name: string, url: string) => {
    // console.log('createNewPage called, projectId:', projectId);
    if (!projectId) {
      console.error('Cannot create page: projectId is missing');
      setError('Project ID is missing. Please try reloading the page.');
      setOpenSnackbar(true);
      return;
    }

    setOpenModal(false);
    setLoading(true);
    setButtonLoading(true);
    try {

      // Default HTML template
      const defaultHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <script src="https://cdn.tailwindcss.com"></script>
</head>
<body>
</body>
</html>`;

      const requestData = {
        url: url,
        name: name,
        projectId: projectId,
        pageHTML: defaultHtml // Use default HTML template instead of current HTML
      };
      // console.log('Request data:', requestData);

      const option = {
        method: 'POST',
        url: '/api/v1/pages',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        data: requestData
      };

      // console.log('Sending POST request to /api/v1/pages');
      const res = await axios(option);
      // console.log('Response from create page API:', res.data);

      if (!res.data || !res.data.data || !res.data.data.page) {
        throw new Error('Invalid response format from server');
      }

      const newPage = res.data.data.page;
      // console.log('New page created:', newPage);

      // Update the pages array
      const updatedPages = [...pages, newPage];
      setPages(updatedPages);

      // Set the active page directly first
      setActivePage(newPage._id);

      // Update URL to include the new page ID
      navigate(`/projects/${projectId}/${newPage._id}`, { replace: true });

      // Set the new HTML ID for the iframe
      if (newPage.html) {
        setActiveHtmlId(newPage.html);

        // Also keep the preview HTML updated for the code editor
        try {
          const response = await fetch(`/api/v1/html/${newPage.html}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            const htmlContent = await response.text();
            setPreviewHtml(htmlContent);
            setPreviousSavedHtml(htmlContent);
          }
        } catch (error) {
          console.error('Error loading new page HTML:', error);
          // Fallback to default HTML if fetch fails
          setPreviewHtml(defaultHtml);
        }
      } else {
        // If no HTML ID available, use srcDoc approach with default HTML
        setActiveHtmlId(null);
        setPreviewHtml(defaultHtml);
        setPreviousSavedHtml(defaultHtml);
      }

      // Force the tab selection update with our dedicated function
      forceUpdateActiveTab(newPage._id);

      setLoading(false);
      setButtonLoading(false);
    } catch (error: any) {
      console.error('Error creating new page:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
      setLoading(false);
      setButtonLoading(false);
      setError(error.response?.data?.error || error.message || 'Failed to create page');
      setOpenSnackbar(true);
    }
  };

  // Function to duplicate a page
  const duplicatePage = async (pageId: string) => {
    // console.log('duplicatePage called, pageId:', pageId);
    if (!projectId) {
      console.error('Cannot duplicate page: projectId is missing');
      setError('Project ID is missing. Please try reloading the page.');
      setOpenSnackbar(true);
      return;
    }

    if (!pageId) {
      console.error('Cannot duplicate page: pageId is missing');
      setError('Page ID is missing. Please try again.');
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);
    setButtonLoading(true);
    try {
      // Find the page to duplicate
      const pageToDuplicate = pages.find((p: any) => p._id === pageId);
      // console.log('Page to duplicate:', pageToDuplicate);

      if (!pageToDuplicate) {
        throw new Error('Page not found');
      }

      // Create name for duplicate page
      const duplicateName = `${pageToDuplicate.name} (Copy)`;
      // console.log('Creating duplicate page with name:', duplicateName);

      const requestData = {
        url: duplicateName,
        name: duplicateName,
        projectId: projectId,
        htmlToCopy: pageToDuplicate.html
      };
      // console.log('Request data:', requestData);

      const option = {
        method: 'POST',
        url: '/api/v1/pages',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        data: requestData
      };

      // console.log('Sending POST request to /api/v1/pages');
      const res = await axios(option);
      // console.log('Response from duplicate page API:', res.data);

      if (!res.data || !res.data.data || !res.data.data.page) {
        throw new Error('Invalid response format from server');
      }

      const newPage = res.data.data.page;
      // console.log('Duplicated page created:', newPage);

      // Update the pages array
      const updatedPages = [...pages, newPage];
      setPages(updatedPages);

      // Set the active page directly first
      setActivePage(newPage._id);

      // Update URL to include the new page ID
      navigate(`/projects/${projectId}/${newPage._id}`, { replace: true });

      // Set the HTML ID for the iframe
      if (newPage.html) {
        setActiveHtmlId(newPage.html);

        // Also keep the preview HTML updated for the code editor
        try {
          const response = await fetch(`/api/v1/html/${newPage.html}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            const htmlContent = await response.text();
            setPreviewHtml(htmlContent);
            setPreviousSavedHtml(htmlContent);
          }
        } catch (error) {
          console.error('Error loading duplicated page HTML:', error);
        }
      } else {
        // If no HTML ID is available, clear the active HTML ID
        setActiveHtmlId(null);
      }

      // Force the tab selection update with our dedicated function
      forceUpdateActiveTab(newPage._id);

      setLoading(false);
      setButtonLoading(false);
    } catch (error: any) {
      console.error('Error duplicating page:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
      setLoading(false);
      setButtonLoading(false);
      setError(error.response?.data?.error || error.message || 'Failed to duplicate page');
      setOpenSnackbar(true);
    }
  };

  // Force update the active tab selection with proper scrolling
  const forceUpdateActiveTab = (pageId: string) => {
    // console.log('Setting active page to:', pageId);

    // Set the active page directly without null step
    setActivePage(pageId);

    // Wait for DOM update before scrolling
    setTimeout(() => {
      const activeTab = document.querySelector('.page-tabs__item--active');
      if (activeTab) {
        // console.log('Scrolling to active tab element:', activeTab);
        activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      } else {
        console.warn('Active tab element not found for page:', pageId);
      }
    }, 150);
  };

  // Scroll active tab into view when changed
  useEffect(() => {
    if (activePage) {
      // Give time for DOM to update after state change
      setTimeout(() => {
        const activeTab = document.querySelector('.page-tabs__item--active');
        if (activeTab) {
          activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }, 100);
    }
  }, [activePage]);

  // UseEffect to add click outside listener for dropdown menus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditingName) {
        const nameInputs = document.querySelectorAll('.page-tabs__name-input');
        let shouldExit = true;

        nameInputs.forEach(input => {
          if (input.contains(event.target as Node)) {
            shouldExit = false;
          }
        });

        if (shouldExit) {
          setIsEditingName(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditingName]);

  // When active page changes, load its HTML content
  useEffect(() => {
    if (activePage) {
      const currentPage = pages.find((p: any) => p._id === activePageRef.current);
      if (currentPage && currentPage.html) {
        // Set the preview HTML ID for reference
        setActiveHtmlId(currentPage.html);

        // Fetch the HTML content for this page
        const fetchHtmlContent = async () => {
          try {
            const response = await fetch(`/api/v1/html/${currentPage.html}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });

            if (response.ok) {
              const htmlContent = await response.text();
              setPreviewHtml(htmlContent);
              setPreviousSavedHtml(htmlContent);
            }
          } catch (error) {
            console.error('Error fetching HTML for page:', error);
          }
        };

        fetchHtmlContent();
      }
    }
  }, [activePage, pages]);

  // Handle opening the add page popover
  const handleOpenAddPageMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAddPageAnchorEl(event.currentTarget);
  };

  // Handle closing the add page popover
  const handleCloseAddPageMenu = () => {
    setAddPageAnchorEl(null);
  };

  // Handle add page menu actions
  const handleAddPageAction = (action: string) => {
    if (action === 'duplicate' && pages.length > 0) {
      // Get the last active page or the first page
      const pageIdToDuplicate = activePage || pages[0]._id;
      duplicatePage(pageIdToDuplicate);
    } else if (action === 'template') {
      // Open the template selector - but this never gets called as the button uses handleOpenTemplates directly
      console.log('Template action selected');
      fetchTemplates();
    }
    handleCloseAddPageMenu();
  };

  // Handle opening color palette
  const handleOpenColorPalette = (event: React.MouseEvent<HTMLElement>) => {
    setColorPaletteAnchorEl(event.currentTarget);
    setShowColorPalette(true);
    setShowFontSelector(false); // Close font selector if open
  };

  // Handle closing color palette
  const handleCloseColorPalette = () => {
    setColorPaletteAnchorEl(null);
    setShowColorPalette(false);
    setColorPaletteSearch(''); // Reset search when closing
  };

  // Handle opening font selector
  const handleOpenFontSelector = (event: React.MouseEvent<HTMLElement>) => {
    setFontSelectorAnchorEl(event.currentTarget);
    setShowFontSelector(true);
    setShowColorPalette(false); // Close color palette if open
  };

  // Handle closing font selector
  const handleCloseFontSelector = () => {
    setFontSelectorAnchorEl(null);
    setShowFontSelector(false);
    setFontSearch(''); // Reset search when closing
  };

  // Handle selecting a color palette
  const handleSelectColorPalette = (palette: { name: string, colors: string[] }) => {
    // Format color palette for the prompt
    const paletteFormatted = palette.colors.map(color => `${color}`).join(', ');

    // Update the user input with the selected palette
    setUserInput(prev => {
      return prev + (prev ? '\n\n' : '') +
        `Color palette: ${palette.name}\n` +
        `Colors: ${paletteFormatted}`;
    });

    // Close the palette selector
    handleCloseColorPalette();
  };

  // Handle selecting a font
  const handleSelectFont = (font: { name: string, family: string }) => {
    // Insert the font name and family into the user input
    setUserInput(prev => {
      const fontInsert = `Use the font: "${font.name}" (${font.family})`;
      return prev ? `${prev}\n${fontInsert}` : fontInsert;
    });

    // Close the font selector
    handleCloseFontSelector();
  };

  // Filter color palettes based on search term
  const filteredColorPalettes = useMemo(() => {
    if (!colorPaletteSearch.trim()) return colorPalettes;

    const searchTerm = colorPaletteSearch.toLowerCase();
    return colorPalettes.filter(palette =>
      palette.name.toLowerCase().includes(searchTerm)
    );
  }, [colorPaletteSearch, colorPalettes]);

  // Color palette search handler
  const handleColorPaletteSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setColorPaletteSearch(e.target.value);
  };

  // Handle font search input change
  const handleFontSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFontSearch(e.target.value);
  };

  // Get all fonts from all categories in a flat array for search
  const allFonts = useMemo(() => {
    return fontCategories.flatMap(category => category.fonts);
  }, [fontCategories]);

  // Filter fonts based on search term
  const filteredFonts = useMemo(() => {
    if (!fontSearch.trim()) return null; // Return null when no search to show categories

    const searchTerm = fontSearch.toLowerCase();
    return allFonts.filter(font =>
      font.name.toLowerCase().includes(searchTerm)
    );
  }, [fontSearch, allFonts]);

  // Function to handle opening the history popover
  const handleOpenHistory = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowHistory(!showHistory);
    fetchMessageHistory();
  };

  const handleCloseHistory = () => {
    setShowHistory(false);
  };

  const handleToggleHistory = () => {
    const newShowHistory = !showHistory;
    setShowHistory(newShowHistory);
    // Save to localStorage
    localStorage.setItem('showHistory', JSON.stringify(newShowHistory));
  };

  const handleHistorySearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHistorySearch(e.target.value);
  };

  // Filter messages based on search term
  const filteredMessages = useMemo(() => {
    if (!historySearch.trim()) return historyMessages;

    const searchTerm = historySearch.toLowerCase();
    return historyMessages.filter(message =>
      message.content.toLowerCase().includes(searchTerm)
    );
  }, [historySearch, historyMessages]);

  // Fetch message history from backend
  const fetchMessageHistory = useCallback(async () => {
    if (!projectId) return;

    setIsLoadingHistory(true);
    try {
      const response = await axios({
        method: 'GET',
        url: `/api/v1/messages/project/${projectId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.status === 'success') {
        let messages = response.data.data.messages || [];

        // Debug logs to see what data we're getting
        // console.log('All messages from API:', messages);
        // console.log('Current active page:', activePage);

        // Show all messages for the project - no longer filtering by page
        // This provides a unified message history across all pages

        setHistoryMessages(messages);
      }
    } catch (error: any) {
      console.error('Error fetching message history:', error);
      setError(error.response?.data?.error || 'Failed to load message history');
      setOpenSnackbar(true);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [projectId]);

  // Fetch message history when history popup is opened
  useEffect(() => {
    if (showHistory) {
      fetchMessageHistory();
    }
  }, [fetchMessageHistory, showHistory]);

  // Use pageId from URL if available
  useEffect(() => {
    if (pageId && pages.some(page => page._id === pageId)) {
      setActivePage(pageId);
    }
  }, [pageId, pages]);

  // Enhance the reloadIframe function with better logging
  const reloadIframe = (htmlId?: string, htmlContent?: string) => {
    // console.log('reloadIframe called with:', { htmlId, hasContent: !!htmlContent });

    if (!iframeRef.current) {
      console.warn('iframeRef.current is null, cannot reload iframe');
      return;
    }

    setTimeout(() => {
      try {
        // If we have an HTML ID, use the src attribute with a timestamp
        if (htmlId && iframeRef.current) {
          const timestamp = Date.now();
          const url = `/api/v1/html/${htmlId}?getfrom=preview&t=${timestamp}`;
          // console.log(`Reloading iframe with src: ${url}`);
          iframeRef.current.src = url;
        }
        // If we have HTML content, use srcDoc or document.write
        else if (htmlContent && iframeRef.current) {
          // console.log('Reloading iframe with html content (length):', htmlContent.length);

          // Try using document.write approach
          const doc = iframeRef.current.contentDocument;
          if (doc) {
            // Validate HTML content before writing
            const validation = validateHtmlContent(htmlContent);
            if (!validation.isValid) {
              validation.errors.forEach(error => {
                addConsoleLog('error', `${error}`);
              });
            } else {
              handleClearConsole();
            }

            doc.open();
            doc.write(htmlContent);
            doc.close();
            // console.log('Iframe reloaded using document.write');
          } else {
            console.warn('contentDocument is null, cannot write HTML content');

            // Fallback to srcDoc if document.write fails
            if (iframeRef.current) {
              iframeRef.current.srcdoc = htmlContent;
              // console.log('Fallback: Iframe reloaded using srcdoc attribute');
            }
          }
        } else {
          console.warn('No htmlId or htmlContent provided to reloadIframe');
        }
      } catch (error) {
        console.error('Error reloading iframe:', error);
      }
    }, 100);
  };

  const [isUpdatingPage, setIsUpdatingPage] = useState(false);

  // Update the handleApplyHistoryHtml function with better error handling
  const handleApplyHistoryHtml = async (projectVersion: any) => {
    if (!projectVersion) {
      setError('No project version found to restore');
      setOpenSnackbar(true);
      return;
    }

    if (!activePage) {
      setError('No active page to update');
      setOpenSnackbar(true);
      return;
    }

    try {
      setIsUpdatingPage(true);

      const response = await axios({
        method: 'POST',
        url: `/api/v1/projects/${projectId}/revert`,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        data: {
          version: projectVersion
        }
      });

      if (response.data.success) {
        let currentPage = response.data.data.pages.find((page: any) => page._id === activePageRef.current);

        // Update the pages array with the updated page
        setPages(response.data.data.pages);

        if (!currentPage) {
          console.log('currentPage not found, setting first page as active page');
          const firstPage = response.data.data.pages[0];
          setActivePage(firstPage._id);
          currentPage = firstPage;
        }

        // Reload the iframe with the new HTML ID for consistent state
        reloadIframe(currentPage.html, currentPage.htmlCode);

        // Set the editor content with the historical HTML
        setPreviewHtml(currentPage.htmlCode);
        setPreviousSavedHtml(currentPage.htmlCode);

      // // Update iframe content immediately for visual feedback
      // if (iframeRef.current) {
      //   try {
      //     const doc = iframeRef.current.contentDocument;
      //     if (doc) {
      //       // Validate HTML content before writing
      //       const validation = validateHtmlContent(htmlData.html);
      //       if (!validation.isValid) {
      //         validation.errors.forEach(error => {
      //           addConsoleLog('error', `${error}`);
      //         });
      //       }

      //       doc.open();
      //       doc.write(addUibrainxSelectorPopup(htmlData.html));
      //       doc.close();
      //     }
      //   } catch (iframeError) {
      //     console.error('Error updating iframe:', iframeError);
      //   }
      // }

      // // Update the active HTML ID
      // setActiveHtmlId(htmlData._id);

      // // Get auth token
      // const token = localStorage.getItem('token');
      // if (!token) {
      //   throw new Error('Authentication token is missing');
      // }

      // // Use PUT method with the correct endpoint - use relative URL
      

        // Show success message
        setSuccess('HTML version restored successfully!');
        setOpenSuccessSnackbar(true);
      } else {
        throw new Error(response.data.message || 'Failed to update page HTML');
      }
    } catch (error) {
      console.error('Error restoring HTML:', error);
      if (axios.isAxiosError(error) && error.response) {
        setError(`Failed to restore HTML: ${error.response.data.message || error.message}`);
      } else if (error instanceof Error) {
        setError(`Failed to restore HTML: ${error.message}`);
      } else {
        setError('Failed to restore HTML: An unknown error occurred');
      }
      setOpenSnackbar(true);

      // Revert iframe to previous state if update failed
      if (activePage && iframeRef.current) {
        const currentPage = pages.find(page => page._id === activePageRef.current);
        if (currentPage && currentPage.html) {
          reloadIframe(currentPage.html);
        }
      }
    } finally {
      setIsUpdatingPage(false);
    }
  };

  const handleToggleExpand = () => {
    setIsExpanded(prev => !prev);
    document.body.classList.toggle('preview-expanded', !isExpanded);
  };

  // Add the escape key handler for fullscreen mode
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isExpanded]);

  // Make sure activeHtmlId is always in sync with the current page
  useEffect(() => {
    if (activePage && pages.length > 0) {
      const currentPage = pages.find(page => page._id === activePageRef.current);
      if (currentPage && currentPage.html) {
        // console.log('Syncing activeHtmlId with current page:', currentPage.html);
        setActiveHtmlId(currentPage.html);

        // If iframe exists, update its src
        if (iframeRef.current) {
          iframeRef.current.src = `/api/v1/html/${currentPage.html}?getfrom=preview&t=${Date.now()}`;
        }
      }
    }
  }, [activePage, pages]);

  // Add near the top of the component where other state variables are declared
  
  const [success, setSuccess] = useState('');
  const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState(false);

  // Force reload iframe whenever activeHtmlId changes
  useEffect(() => {
    if (!activeHtmlId) return;

    // console.log('activeHtmlId changed, triggering forced iframe reload:', activeHtmlId);

    // Delay slightly to ensure state updates are complete
    const timer = setTimeout(() => {
      if (iframeRef.current) {
        try {
          const url = `/api/v1/html/${activeHtmlId}?getfrom=preview&t=${Date.now()}`;
          // console.log(`Forced iframe reload with src: ${url}`);
          iframeRef.current.src = url;
        } catch (error) {
          console.error('Error during forced iframe reload:', error);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [activeHtmlId]);

  // Add state for managing editor content
  const [editorContent, setEditorContent] = useState<string>('');

  // Update handleEditorChange function
  const handleEditorChange = (newValue: string | undefined) => {
    // Skip updates during streaming
    if (isStreaming) return;

    if (!newValue) return;

    // Validate HTML content before updating
    const validation = validateHtmlContent(newValue);
    if (!validation.isValid) {
      validation.errors.forEach(error => {
        addConsoleLog('error', `${error}`);
      });
    } else {
      handleClearConsole();
    }

    // Update preview HTML state
    setPreviewHtml(newValue);
    if (newValue !== previousSavedHtml && !isWaitingResponse && !isStreaming) {
      setIsEditEditorMode(prev => ({ ...prev, [String(activePage)]: true }));
    } else {
      setIsEditEditorMode(prev => ({ ...prev, [String(activePage)]: false }));
    }
  };

  const handleSaveHtml = async (newHtml: string) => {
    try {
      if (!activeHtmlId) {
        console.warn('No active HTML ID found for save');
        setIsEditMode(false);
        return;
      }

      setIsSaving(true);

      // Get the current cursor position
      const currentPosition = editorRef.current?.getPosition();
      const currentIframeScrollPosition = iframeRef.current?.contentWindow?.scrollY;

      // Make the API request to save the HTML
      const response = await axios.post(
        `/api/v1/pages/${activePage}/html`,
        {
          htmlContent: newHtml
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data?.data?.html) {
        // Update the preview HTML with the saved content
        setPreviewHtml(response.data.data.html);
        setPreviousSavedHtml(response.data.data.html);
        setActiveHtmlId(response.data.data.htmlId);

        const userMessage: Message = {
          type: 'user',
          content: 'Update HTML',
          projectVersion: response.data.data.htmlVersion || 0,
          html: {
            _id: response.data.data.htmlId || '',
            html: response.data.data.html,
            url: response.data.data.url || '',
            version: response.data.data.htmlVersion || 1
          }
        };

        setHistoryMessages(prev => [...prev, userMessage]);

        const model = editorRef.current.getModel();

        // update the editor content with the saved content
        editorRef.current.executeEdits('element-update', [{
          range: model.getFullModelRange(),
          text: response.data.data.html
        }]);

        setIsEditMode(false);
        setIsEditEditorMode(prev => ({ ...prev, [String(activePage)]: false }));

        // Restore cursor position after content update
        if (currentPosition && editorRef.current) {
          editorRef.current.setPosition(currentPosition);
        }

        // wait for 1 second before restoring iframe scroll position
        setTimeout(() => {
          // Restore iframe scroll position after content update
          if (currentIframeScrollPosition && iframeRef.current) {
            iframeRef.current.contentWindow?.scrollTo(0, currentIframeScrollPosition);
          }
        }, 1000);
      }

      setIsSaving(false);
    } catch (error: any) {
      console.error('Error auto-saving HTML:', error);
      setIsSaving(false);
    }
  };

  // Helper function to generate URL from HTML content
  const generateUrl = (html: string): string => {
    try {
      // Extract title from HTML if present
      const titleMatch = html.match(/<title>(.*?)<\/title>/i);
      const title = titleMatch ? titleMatch[1] : 'untitled';
      // Convert to URL-friendly string
      return title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') +
        '-' + Date.now();
    } catch (error) {
      return 'untitled-' + Date.now();
    }
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, []);

  // Find and replace the Editor component in the chat preview section
  <Editor
    height="100%"
    defaultLanguage="html"
    value={previewHtml || '<!-- Preview HTML will appear here -->'}
    onChange={handleEditorChange}
    options={{
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      wordWrap: 'on',
      wrappingIndent: 'indent',
      automaticLayout: true,
      readOnly: isSaving || isStreaming || isWaitingResponse ? true : false
    }}
  />

  // Add this function after the handleRemovePreview function
  const handleStopStreaming = () => {
    // Abort the fetch request if controller exists
    if (abortController) {
      // console.log('Aborting streaming request');
      abortController.abort();
      setAbortController(null);
    }

    // Reset streaming state
    setIsStreaming(false);
    setStreamingSections({ sections: [], currentSection: -1 }); // Clear streaming sections

    // Revert the editor content to previous HTML
    if (previousHtml && editorRef.current) {
      // Validate previous HTML before reverting
      const validation = validateHtmlContent(previousHtml);
      if (!validation.isValid) {
        validation.errors.forEach(error => {
          addConsoleLog('error', `${error}`);
        });
      }

      editorRef.current.setValue(previousHtml);
      setPreviewHtml(previousHtml);
    }

    // Revert the iframe to previous HTML
    if (iframeRef.current && previousHtml) {
      try {
        const doc = iframeRef.current.contentDocument;
        if (doc) {
          doc.open();
          doc.write(addUibrainxSelectorPopup(previousHtml));
          doc.close();
        }
      } catch (error) {
        console.error('Error reverting iframe content:', error);
      }
    }

    // Send request to backend to set the page's stopped flag
    if (activePage) {
      try {
        const token = localStorage.getItem('token');
        fetch(`/api/v1/pages/${activePage}/stop`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }).then(response => {
          if (response.ok) {
            // console.log('Page marked as stopped');
          } else {
            // console.error('Failed to mark page as stopped');
          }
        }).catch(error => {
          // console.error('Error marking page as stopped:', error);
        });
      } catch (error) {
        // console.error('Error sending stop request:', error);
      }
    }

    // Reset waiting state
    setIsWaitingResponse(false);
  };

  // Handle opening template selector
  const handleOpenTemplates = (event: React.MouseEvent<HTMLElement>) => {
    // console.log('Opening templates popover');

    // Close the add page popover if it's open
    handleCloseAddPageMenu();

    // Open the templates dialog
    setShowTemplates(true);
    fetchTemplates();
  };

  // Handle closing template selector
  const handleCloseTemplates = () => {
    setShowTemplates(false);
    setTemplateSearch('');
  };

  // Handle template search input change
  const handleTemplateSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTemplateSearch(e.target.value);
  };

  // Fetch templates from the API
  const fetchTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      // console.log('Fetching templates...');
      const response = await axios({
        method: 'GET',
        url: '/api/v1/templates',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      // console.log('Templates API response:', response.data);

      if (response.data.success) {
        setTemplates(response.data.data.templates || []);
        // console.log('Templates loaded:', response.data.data.templates?.length || 0);
      }
    } catch (error: any) {
      console.error('Error fetching templates:', error);
      setError(error.response?.data?.error || 'Failed to load templates');
      setOpenSnackbar(true);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handleDeleteCommunityTemplate = async (e: React.MouseEvent, templateId: string, templateName: string) => {
    e.stopPropagation();
    if (!['admin', 'tester'].includes(state.user?.role ?? '')) return;
    if (!window.confirm(`Remove "${templateName}" from community templates?`)) return;

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Not authenticated.');
      setOpenSnackbar(true);
      return;
    }

    setDeletingCommunityTemplateId(templateId);
    try {
      const res = await axios({
        method: 'DELETE',
        url: `/api/v1/templates/${templateId}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.data?.success) {
        setTemplates((prev) => prev.filter((t) => t._id !== templateId));
        if (commitReplaceTemplateId === templateId) {
          setCommitReplaceTemplateId('');
        }
        setSuccess('Template removed from community list.');
        setOpenSuccessSnackbar(true);
      } else {
        setError(res.data?.error || 'Failed to delete template.');
        setOpenSnackbar(true);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to delete template.';
      setError(msg);
      setOpenSnackbar(true);
    } finally {
      setDeletingCommunityTemplateId(null);
    }
  };

  // Create a page using a template
  const createPageFromTemplate = async (templateId: string) => {
    if (!projectId) {
      console.error('Cannot create page from template: projectId is missing');
      setError('Project ID is missing. Please try reloading the page.');
      setOpenSnackbar(true);
      return;
    }

    if (!templateId) {
      console.error('Cannot create page from template: templateId is missing');
      setError('Template ID is missing. Please try again.');
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);
    setButtonLoading(true);

    try {
      // console.log('Fetching template with ID:', templateId);

      // Get the template details
      const templateResponse = await axios({
        method: 'GET',
        url: `/api/v1/templates/${templateId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!templateResponse.data.success) {
        throw new Error('Failed to get template');
      }

      const template = templateResponse.data.data.template;
      // console.log('Template fetched:', template.name);

      // Create a name for the template-based page
      const pageName = template.name;

      // Create a new page with the template HTML
      const requestData = {
        url: pageName,
        name: pageName,
        projectId: projectId,
        pageHTML: template.html
      };

      // console.log('Creating page from template:', pageName);

      const response = await axios({
        method: 'POST',
        url: '/api/v1/pages',
        data: requestData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.data || !response.data.data || !response.data.data.page) {
        throw new Error('Invalid response format from server');
      }

      // Add the new page to the pages array
      const newPage = response.data.data.page;
      // console.log('New page created from template:', newPage._id);

      setPages(prevPages => [...prevPages, newPage]);

      // Set the active page
      setActivePage(newPage._id);

      // Update URL to include the new page ID
      navigate(`/projects/${projectId}/${newPage._id}`, { replace: true });

      // Set the HTML ID for the iframe
      if (newPage.html) {
        setActiveHtmlId(newPage.html);

        // Also keep the preview HTML updated for the code editor
        try {
          const htmlResponse = await fetch(`/api/v1/html/${newPage.html}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (htmlResponse.ok) {
            const htmlContent = await htmlResponse.text();
            setPreviewHtml(htmlContent);
          } else {
            setPreviewHtml(template.html);
          }
        } catch (error) {
          console.error('Error loading new page HTML:', error);
          // Fallback to template HTML if fetch fails
          setPreviewHtml(template.html);
        }
      } else {
        // If no HTML ID available, use template HTML directly
        setActiveHtmlId(null);
        setPreviewHtml(template.html);
      }

      // Force the tab selection update
      forceUpdateActiveTab(newPage._id);

      // Close the templates modal
      handleCloseTemplates();

    } catch (error: any) {
      console.error('Error creating page from template:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
      setError(error.response?.data?.error || error.message || 'Failed to create page from template');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
      setButtonLoading(false);
    }
  };

  // Filter templates based on search term
  const filteredTemplates = useMemo(() => {
    if (!templateSearch.trim()) return templates;

    const searchTerm = templateSearch.toLowerCase();
    return templates.filter(template =>
      template.name.toLowerCase().includes(searchTerm) ||
      (template.description && template.description.toLowerCase().includes(searchTerm)) ||
      (template.tags && template.tags.some(tag => tag.toLowerCase().includes(searchTerm)))
    );
  }, [templateSearch, templates]);

  // For template debugging - check if templates exist on component mount
  useEffect(() => {
    // Fetch templates when the component mounts to verify connection
    const debugTemplates = async () => {
      try {
        // console.log('Debug: Checking templates on mount');
        const response = await axios({
          method: 'GET',
          url: '/api/v1/templates',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        // console.log('Debug: Templates API check result:', response.data);

        if (response.data.status === 'success') {
          // console.log('Debug: Templates available:', response.data.data.templates?.length || 0);
        }
      } catch (error: any) {
        console.error('Debug: Error checking templates:', error);
      }
    };

    debugTemplates();
  }, []);

  // Check if this is a new project from URL query parameter
  const [isNewProject, setIsNewProject] = useState(false);
  // Track if the project has been fully loaded
  const [projectLoaded, setProjectLoaded] = useState(false);
  // Track if the welcome screen should be shown (persists until user makes a choice)
  const [showWelcome, setShowWelcome] = useState(true);

  // Effect to check for the new project query parameter
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const isNew = queryParams.get('new') === 'true';
    setIsNewProject(isNew);

    // Remove the query parameter after checking it
    if (isNew) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }

    // Debug log to trace the welcome screen state
    // console.log('New project check:', isNew);
  }, []);

  // Separate effect for handling initial project loading
  useEffect(() => {
    if (projectId) {
      getAllPages();
    } else {
      // If no project ID, mark as loaded to avoid loading indefinitely
      setProjectLoaded(true);
    }
  }, [projectId, getAllPages]);

  // Function to check if we should show the welcome screen - add logging
  const shouldShowWelcomeScreen = useMemo(() => {
    // Only show welcome screen when:
    // - Project is loaded AND has no pages AND we haven't hidden it yet, OR
    // - It's a new project AND we haven't hidden it yet
    return ((projectLoaded && pages.length === 0) || isNewProject) && showWelcome;
  }, [isNewProject, pages.length, projectLoaded, showWelcome]);

  // Function to handle when user selects a template
  const handleSelectTemplate = (templateId: string) => {
    // Hide welcome screen
    setShowWelcome(false);
    // Create page from template
    createPageFromTemplate(templateId);
  };

  // Function to handle when user selects blank page
  const handleCreateBlankPage = () => {
    // Hide welcome screen
    setShowWelcome(false);
    // Create blank page
    createNewPage('home.html', 'home.html');
  };

  // Function to enable element highlighting in the iframe
  useEffect(() => {
    if (!iframeRef.current) return;

    const iframe = iframeRef.current;
    const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;

    if (!iframeDocument) return;

    // Remove any existing highlight styles
    const existingStyle = iframeDocument.getElementById('element-highlighter-style');
    if (existingStyle) existingStyle.remove();

    // Add hover highlighting style
    const style = iframeDocument.createElement('style');
    style.id = 'element-highlighter-style';
    style.textContent = `
      *:hover {
        outline: 1px dashed #3364E3 !important;
        cursor: pointer !important;
      }
      .element-selected {
        outline: 1px solid #3364E3 !important;
      }
    `;
    iframeDocument.head?.appendChild(style);

    // Add click handler to all elements

    const handleClick = (iframeRef: any, selectedElement: any, elementClasses: any, setSelectedElement: any, setElementClasses: any, setShowClassForm: any, isEditMode: any) => (e: any) => {
      if (!isEditMode) return;
      e.preventDefault();
      e.stopPropagation();

      // If we already have a selected element, apply any pending changes before switching
      if (selectedElement) {
        // Check if we're clicking the same element again
        if (selectedElement === e.target) {
          return; // Don't reselect the same element
        }

        // Store current element state to apply any pending changes
        const currentElement = selectedElement;

        // Apply classes to the current element before switching
        currentElement.className = elementClasses.replace('element-selected', '').trim();

        // If we modified the HTML in the editor, we should update it in the iframe too
        if (editorRef.current) {
          // This will be handled by handleClassSubmit, no need to duplicate here
        }

        if (!['div', 'section', 'article', 'main', 'aside', 'header', 'footer', 'nav', 'figure', 'img'].includes(selectedElement?.tagName?.toLowerCase() || '')) {
          selectedElement.innerHTML = originalElementContent;
        }
        applyClassesToElement(originalElementClasses);
      }

      // Remove previous selection class
      const prevSelected = iframeDocument.querySelector('.element-selected');
      if (prevSelected) prevSelected.classList.remove('element-selected');

      const target = e.target as HTMLElement;
      target.classList.add('element-selected');

      // Get the tag and classes of the selected element for finding similar elements
      const tagName = target.tagName.toLowerCase();
      const originalClasses = typeof target.className === 'string' ? target.className.replace('element-selected', '').trim() : '';

      // Set the selected element and its classes
      setSelectedElement(target);
      setElementClasses(originalClasses);

      // Capture data-id attribute if it exists
      const dataId = target.getAttribute('data-id');
      setSelectedElementDataId(dataId);
      setSelectedElementHTML(target.outerHTML);

      // Save current content for editing
      setElementContent(target.innerHTML || '');

      // Save current image URL for editing
      setElementImageUrl((target as HTMLImageElement).src || '');

      // Save original values for potential revert
      setOriginalElementClasses(typeof target.className === 'string' ? target.className.replace('element-selected', '').trim() : '');
      setOriginalElementContent(target.innerHTML || '');

      // Store the XPath for more reliable element identification
      setSelectedElementXPath(getElementXPath(target));

      // If user wants to apply changes to all similar elements, we can highlight them
      if (originalClasses) {
        // console.log(`Selected element with classes: ${originalClasses}`);

        // Optionally highlight similar elements with the same class for visual feedback
        // This helps users see which elements will be updated together
        const similarElements = iframeDocument.querySelectorAll(`${tagName}[class*="${originalClasses}"]`);
        // console.log(`Found ${similarElements.length} similar elements with same class pattern`);
      }

      // Show the class form first, then scroll the editor to the element
      setShowClassForm(true);
      // The scrollEditorToElement function now has a built-in delay
      scrollEditorToElement(target);
    };

    const boundHandler = handleClick(iframeRef, selectedElement, elementClasses, setSelectedElement, setElementClasses, setShowClassForm, isEditMode);

    iframeDocument.body?.addEventListener('click', boundHandler, true);

    return () => {
      iframeDocument.body?.removeEventListener('click', boundHandler, true);
    }

  }, [isEditMode]);

  // Function to disable element highlighting
  const disableElementHighlighting = useCallback(() => {
    if (!iframeRef.current) return;

    const iframe = iframeRef.current;
    const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;

    if (!iframeDocument) return;

    // Remove highlight styles
    const highlightStyle = iframeDocument.getElementById('element-highlighter-style');
    if (highlightStyle) highlightStyle.remove();

    // Remove selected class from any element
    const selectedElement = iframeDocument.querySelector('.element-selected');
    if (selectedElement) selectedElement.classList.remove('element-selected');

    // Reset state
    setSelectedElement(null);
    setSelectedElementDataId(null);
    setSelectedElementHTML(null);
    setElementClasses('');
    setShowClassForm(false);
  }, [iframeRef]);

  // Handle class form submission
  const handleClassSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedElement || !iframeRef.current || !editorRef.current) return;

    const iframe = iframeRef.current;
    const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;

    if (!iframeDocument) return;

    // Find and update all similar elements with the same original classes in the iframe
    if (iframeDocument) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(editorRef.current.getModel().getValue(), 'text/html');
      // loop through editData and update the elements with the same original classes and the same tag name and the same page id
      Object.keys(editData).forEach(pageId => {
        if (pageId === activePage) {
          Object.keys(editData[pageId]).forEach(elementId => {
            const element = doc.querySelector(`[data-id="${elementId}"]`);
            if (element) {
              const elementTagName = element.tagName.toLowerCase();
              const elementClasses = element.className;
              const similarElements = doc.querySelectorAll(`${elementTagName}[class*="${elementClasses}"]`);

              similarElements.forEach(similarElement => {
                if (editData[pageId][elementId].classes) {
                  const isSvgElement = hasSvgParent(similarElement as HTMLElement);
                  // console.log(isSvgElement);
                  if (isSvgElement) {
                    similarElement.setAttribute('class', editData[pageId][elementId].classes.trim());
                  } else {
                    similarElement.className = editData[pageId][elementId].classes;
                  }
                }
              });

              // update the element content if it has changed
              if (editData[pageId][elementId].content && element.innerHTML !== editData[pageId][elementId].content) {
                element.innerHTML = editData[pageId][elementId].content;
              }

              // update the element image url if it has changed
              if (element.tagName.toLowerCase() === 'img' && editData[pageId][elementId].imageUrl) {
                (element as HTMLImageElement).src = editData[pageId][elementId].imageUrl;
              }
            }
          });
        }
      });

      // update the editor content
      handleSaveHtml(doc.documentElement.outerHTML);
      setEditData({});
    }

    setIsEditMode(false);
  };

  // Toggle edit mode
  useEffect(() => {
    if (!isEditMode) {
      disableElementHighlighting();
    }
  }, [isEditMode, disableElementHighlighting]);

  // Add keyboard shortcut (Ctrl+E) to toggle edit mode
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      // Check for Ctrl+E
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault();
        setIsEditMode(prevMode => !prevMode);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditMode]);

  // Helper function to get preview color for display
  const getColorPreview = (classes: string) => {
    // Check for custom color format: text-[#hexcolor]
    const customColorMatch = classes.match(/(?:^|\s)text-\[(#[0-9A-Fa-f]{3,6})\]/);
    if (customColorMatch) {
      return customColorMatch[1];
    }

    // Check for Tailwind built-in colors
    const colorMatch = classes.match(/\btext-(white|black|slate|gray|zinc|neutral|stone|red|orange|amber|yellow|lime|green|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia|pink|rose)-(\d+)\b/);
    // console.log(colorMatch);
    if (!colorMatch) return 'transparent';

    const colorName = colorMatch[1];
    const shade = colorMatch[2];

    // Basic color map for common tailwind colors
    const colorMap: { [key: string]: { [key: string]: string } } = {
      'white': { '500': '#ffffff' },
      'black': { '500': '#000000' },
      'slate': {
        '50': '#f8fafc', '100': '#f1f5f9', '200': '#e2e8f0', '300': '#cbd5e1',
        '400': '#94a3b8', '500': '#64748b', '600': '#475569', '700': '#334155',
        '800': '#1e293b', '900': '#0f172a', '950': '#020617'
      },
      'gray': {
        '50': '#f9fafb', '100': '#f3f4f6', '200': '#e5e7eb', '300': '#d1d5db',
        '400': '#9ca3af', '500': '#6b7280', '600': '#4b5563', '700': '#374151',
        '800': '#1f2937', '900': '#111827', '950': '#030712'
      },
      'zinc': {
        '50': '#fafafa', '100': '#f4f4f5', '200': '#e4e4e7', '300': '#d4d4d8',
        '400': '#a1a1aa', '500': '#71717a', '600': '#52525b', '700': '#3f3f46',
        '800': '#27272a', '900': '#18181b', '950': '#09090b'
      },
      'neutral': {
        '50': '#fafafa', '100': '#f5f5f5', '200': '#e5e5e5', '300': '#d4d4d4',
        '400': '#a3a3a3', '500': '#737373', '600': '#525252', '700': '#404040',
        '800': '#262626', '900': '#171717', '950': '#0a0a0a'
      },
      'stone': {
        '50': '#fafaf9', '100': '#f5f5f4', '200': '#e7e5e4', '300': '#d6d3d1',
        '400': '#a8a29e', '500': '#78716c', '600': '#57534e', '700': '#44403c',
        '800': '#292524', '900': '#1c1917', '950': '#0c0a09'
      },
      'red': {
        '50': '#fef2f2', '100': '#fee2e2', '200': '#fecaca', '300': '#fca5a5',
        '400': '#f87171', '500': '#ef4444', '600': '#dc2626', '700': '#b91c1c',
        '800': '#991b1b', '900': '#7f1d1d', '950': '#450a0a'
      },
      'orange': {
        '50': '#fff7ed', '100': '#ffedd5', '200': '#fed7aa', '300': '#fdba74',
        '400': '#fb923c', '500': '#f97316', '600': '#ea580c', '700': '#c2410c',
        '800': '#9a3412', '900': '#7c2d12', '950': '#431407'
      },
      'amber': {
        '50': '#fffbeb', '100': '#fef3c7', '200': '#fde68a', '300': '#fcd34d',
        '400': '#fbbf24', '500': '#f59e0b', '600': '#d97706', '700': '#b45309',
        '800': '#92400e', '900': '#78350f', '950': '#451a03'
      },
      'yellow': {
        '50': '#fefce8', '100': '#fef9c3', '200': '#fef08a', '300': '#fde047',
        '400': '#facc15', '500': '#eab308', '600': '#ca8a04', '700': '#a16207',
        '800': '#854d0e', '900': '#713f12', '950': '#422006'
      },
      'lime': {
        '50': '#f7fee7', '100': '#ecfccb', '200': '#d9f99d', '300': '#bef264',
        '400': '#a3e635', '500': '#84cc16', '600': '#65a30d', '700': '#4d7c0f',
        '800': '#3f6212', '900': '#365314', '950': '#1a2e05'
      },
      'green': {
        '50': '#f0fdf4', '100': '#dcfce7', '200': '#bbf7d0', '300': '#86efac',
        '400': '#4ade80', '500': '#22c55e', '600': '#16a34a', '700': '#15803d',
        '800': '#166534', '900': '#14532d', '950': '#052e16'
      },
      'emerald': {
        '50': '#ecfdf5', '100': '#d1fae5', '200': '#a7f3d0', '300': '#6ee7b7',
        '400': '#34d399', '500': '#10b981', '600': '#059669', '700': '#047857',
        '800': '#065f46', '900': '#064e3b', '950': '#022c22'
      },
      'teal': {
        '50': '#f0fdfa', '100': '#ccfbf1', '200': '#99f6e4', '300': '#5eead4',
        '400': '#2dd4bf', '500': '#14b8a6', '600': '#0d9488', '700': '#0f766e',
        '800': '#115e59', '900': '#134e4a', '950': '#042f2e'
      },
      'cyan': {
        '50': '#ecfeff', '100': '#cffafe', '200': '#a5f3fc', '300': '#67e8f9',
        '400': '#22d3ee', '500': '#06b6d4', '600': '#0891b2', '700': '#0e7490',
        '800': '#155e75', '900': '#164e63', '950': '#083344'
      },
      'sky': {
        '50': '#f0f9ff', '100': '#e0f2fe', '200': '#bae6fd', '300': '#7dd3fc',
        '400': '#38bdf8', '500': '#0ea5e9', '600': '#0284c7', '700': '#0369a1',
        '800': '#075985', '900': '#0c4a6e', '950': '#082f49'
      },
      'blue': {
        '50': '#eff6ff', '100': '#dbeafe', '200': '#bfdbfe', '300': '#93c5fd',
        '400': '#60a5fa', '500': '#3b82f6', '600': '#2563eb', '700': '#1d4ed8',
        '800': '#1e40af', '900': '#1e3a8a', '950': '#172554'
      },
      'indigo': {
        '50': '#eef2ff', '100': '#e0e7ff', '200': '#c7d2fe', '300': '#a5b4fc',
        '400': '#818cf8', '500': '#6366f1', '600': '#4f46e5', '700': '#4338ca',
        '800': '#3730a3', '900': '#312e81', '950': '#1e1b4b'
      },
      'violet': {
        '50': '#f5f3ff', '100': '#ede9fe', '200': '#ddd6fe', '300': '#c4b5fd',
        '400': '#a78bfa', '500': '#8b5cf6', '600': '#7c3aed', '700': '#6d28d9',
        '800': '#5b21b6', '900': '#4c1d95', '950': '#2e1065'
      },
      'purple': {
        '50': '#faf5ff', '100': '#f3e8ff', '200': '#e9d5ff', '300': '#d8b4fe',
        '400': '#c084fc', '500': '#a855f7', '600': '#9333ea', '700': '#7e22ce',
        '800': '#6b21a8', '900': '#581c87', '950': '#3b0764'
      },
      'fuchsia': {
        '50': '#fdf4ff', '100': '#fae8ff', '200': '#f5d0fe', '300': '#f0abfc',
        '400': '#e879f9', '500': '#d946ef', '600': '#c026d3', '700': '#a21caf',
        '800': '#86198f', '900': '#701a75', '950': '#4a044e'
      },
      'pink': {
        '50': '#fdf2f8', '100': '#fce7f3', '200': '#fbcfe8', '300': '#f9a8d4',
        '400': '#f472b6', '500': '#ec4899', '600': '#db2777', '700': '#be185d',
        '800': '#9d174d', '900': '#831843', '950': '#500724'
      },
      'rose': {
        '50': '#fff1f2', '100': '#ffe4e6', '200': '#fecdd3', '300': '#fda4af',
        '400': '#fb7185', '500': '#f43f5e', '600': '#e11d48', '700': '#be123c',
        '800': '#9f1239', '900': '#881337', '950': '#4c0519'
      }
    };

    return colorMap[colorName]?.[shade] || 'transparent';
  };

  // Function to scroll the Monaco editor to the selected element's code
  const scrollEditorToElement = (element: HTMLElement) => {
    if (!editorRef.current) return;

    // Add a small delay to ensure the element-class-form-container is fully opened
    setTimeout(() => {
      try {
        const model = editorRef.current.getModel();
        if (!model) return;

        const editorContent = model.getValue();

        // Try to find the element in different ways, starting with the most specific
        let match = null;

        // 0. Find by data-id if available (most reliable)
        if (element.hasAttribute('data-id')) {
          const dataId = element.getAttribute('data-id');
          const dataIdPattern = new RegExp(`<[^>]*\\bdata-id\\s*=\\s*["']${dataId}["'][^>]*>`, 'g');
          match = editorContent.match(dataIdPattern);
        }

        // 1. Find by ID if available (reliable)
        if (!match && element.id) {
          const idPattern = new RegExp(`<[^>]*\\bid\\s*=\\s*["']${element.id}["'][^>]*>`, 'g');
          match = editorContent.match(idPattern);
        }

        // 2. If no ID match, try to find by class names (more reliable than tag alone)
        if (!match && element.className) {
          const classNames = typeof element.className === 'string' ? element.className.replace('element-selected', '').trim() : '';
          if (classNames) {
            const escapedClassNames = classNames.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const classPattern = new RegExp(`<[^>]*\\bclass\\s*=\\s*["'][^"']*${escapedClassNames}[^"']*["'][^>]*>`, 'g');
            match = editorContent.match(classPattern);
          }
        }

        // 3. Last resort: find by tag name and content (least reliable)
        if (!match) {
          const tagName = element.tagName.toLowerCase();
          let content = (element.innerHTML || '').trim().substring(0, 50);
          if (content) {
            content = content.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const contentPattern = new RegExp(`<${tagName}[^>]*>[^<]*${content}`, 'g');
            match = editorContent.match(contentPattern);
          } else {
            // If no content, just try to find the tag
            const simpleTagPattern = new RegExp(`<${tagName}[^>]*>`, 'g');
            match = editorContent.match(simpleTagPattern);
          }
        }

        if (match && match[0]) {
          const position = editorContent.indexOf(match[0]);
          if (position > -1) {
            const lineNumber = editorContent.substring(0, position).split('\n').length;

            // Scroll to the position
            editorRef.current.revealLineInCenter(lineNumber);

            // Set cursor position
            editorRef.current.setPosition({
              lineNumber: lineNumber,
              column: 1
            });
          }
        }
      } catch (error) {
        console.error('Error scrolling editor to element:', error);
      }
    }, 100); // 100ms delay should be enough for the form to render
  };

  // Add color hex helper function
  const getColorHex = (color: string, shade: string): string => {
    const colorMap: Record<string, Record<string, string>> = {
      'white': { '500': '#ffffff' },
      'black': { '500': '#000000' },
      // Slate
      'slate': {
        '50': '#f8fafc', '100': '#f1f5f9', '200': '#e2e8f0', '300': '#cbd5e1',
        '400': '#94a3b8', '500': '#64748b', '600': '#475569', '700': '#334155',
        '800': '#1e293b', '900': '#0f172a', '950': '#020617'
      },
      // Gray
      'gray': {
        '50': '#f9fafb', '100': '#f3f4f6', '200': '#e5e7eb', '300': '#d1d5db',
        '400': '#9ca3af', '500': '#6b7280', '600': '#4b5563', '700': '#374151',
        '800': '#1f2937', '900': '#111827', '950': '#030712'
      },
      // Zinc
      'zinc': {
        '50': '#fafafa', '100': '#f4f4f5', '200': '#e4e4e7', '300': '#d4d4d8',
        '400': '#a1a1aa', '500': '#71717a', '600': '#52525b', '700': '#3f3f46',
        '800': '#27272a', '900': '#18181b', '950': '#09090b'
      },
      // Neutral
      'neutral': {
        '50': '#fafafa', '100': '#f5f5f5', '200': '#e5e5e5', '300': '#d4d4d4',
        '400': '#a3a3a3', '500': '#737373', '600': '#525252', '700': '#404040',
        '800': '#262626', '900': '#171717', '950': '#0a0a0a'
      },
      // Stone
      'stone': {
        '50': '#fafaf9', '100': '#f5f5f4', '200': '#e7e5e4', '300': '#d6d3d1',
        '400': '#a8a29e', '500': '#78716c', '600': '#57534e', '700': '#44403c',
        '800': '#292524', '900': '#1c1917', '950': '#0c0a09'
      },
      // Red
      'red': {
        '50': '#fef2f2', '100': '#fee2e2', '200': '#fecaca', '300': '#fca5a5',
        '400': '#f87171', '500': '#ef4444', '600': '#dc2626', '700': '#b91c1c',
        '800': '#991b1b', '900': '#7f1d1d', '950': '#450a0a'
      },
      // Orange
      'orange': {
        '50': '#fff7ed', '100': '#ffedd5', '200': '#fed7aa', '300': '#fdba74',
        '400': '#fb923c', '500': '#f97316', '600': '#ea580c', '700': '#c2410c',
        '800': '#9a3412', '900': '#7c2d12', '950': '#431407'
      },
      // Amber
      'amber': {
        '50': '#fffbeb', '100': '#fef3c7', '200': '#fde68a', '300': '#fcd34d',
        '400': '#fbbf24', '500': '#f59e0b', '600': '#d97706', '700': '#b45309',
        '800': '#92400e', '900': '#78350f', '950': '#451a03'
      },
      // Yellow
      'yellow': {
        '50': '#fefce8', '100': '#fef9c3', '200': '#fef08a', '300': '#fde047',
        '400': '#facc15', '500': '#eab308', '600': '#ca8a04', '700': '#a16207',
        '800': '#854d0e', '900': '#713f12', '950': '#422006'
      },
      // Lime
      'lime': {
        '50': '#f7fee7', '100': '#ecfccb', '200': '#d9f99d', '300': '#bef264',
        '400': '#a3e635', '500': '#84cc16', '600': '#65a30d', '700': '#4d7c0f',
        '800': '#3f6212', '900': '#365314', '950': '#1a2e05'
      },
      // Green
      'green': {
        '50': '#f0fdf4', '100': '#dcfce7', '200': '#bbf7d0', '300': '#86efac',
        '400': '#4ade80', '500': '#22c55e', '600': '#16a34a', '700': '#15803d',
        '800': '#166534', '900': '#14532d', '950': '#052e16'
      },
      // Emerald
      'emerald': {
        '50': '#ecfdf5', '100': '#d1fae5', '200': '#a7f3d0', '300': '#6ee7b7',
        '400': '#34d399', '500': '#10b981', '600': '#059669', '700': '#047857',
        '800': '#065f46', '900': '#064e3b', '950': '#022c22'
      },
      // Teal
      'teal': {
        '50': '#f0fdfa', '100': '#ccfbf1', '200': '#99f6e4', '300': '#5eead4',
        '400': '#2dd4bf', '500': '#14b8a6', '600': '#0d9488', '700': '#0f766e',
        '800': '#115e59', '900': '#134e4a', '950': '#042f2e'
      },
      // Cyan
      'cyan': {
        '50': '#ecfeff', '100': '#cffafe', '200': '#a5f3fc', '300': '#67e8f9',
        '400': '#22d3ee', '500': '#06b6d4', '600': '#0891b2', '700': '#0e7490',
        '800': '#155e75', '900': '#164e63', '950': '#083344'
      },
      // Sky
      'sky': {
        '50': '#f0f9ff', '100': '#e0f2fe', '200': '#bae6fd', '300': '#7dd3fc',
        '400': '#38bdf8', '500': '#0ea5e9', '600': '#0284c7', '700': '#0369a1',
        '800': '#075985', '900': '#0c4a6e', '950': '#082f49'
      },
      // Blue
      'blue': {
        '50': '#eff6ff', '100': '#dbeafe', '200': '#bfdbfe', '300': '#93c5fd',
        '400': '#60a5fa', '500': '#3b82f6', '600': '#2563eb', '700': '#1d4ed8',
        '800': '#1e40af', '900': '#1e3a8a', '950': '#172554'
      },
      // Indigo
      'indigo': {
        '50': '#eef2ff', '100': '#e0e7ff', '200': '#c7d2fe', '300': '#a5b4fc',
        '400': '#818cf8', '500': '#6366f1', '600': '#4f46e5', '700': '#4338ca',
        '800': '#3730a3', '900': '#312e81', '950': '#1e1b4b'
      },
      // Violet
      'violet': {
        '50': '#f5f3ff', '100': '#ede9fe', '200': '#ddd6fe', '300': '#c4b5fd',
        '400': '#a78bfa', '500': '#8b5cf6', '600': '#7c3aed', '700': '#6d28d9',
        '800': '#5b21b6', '900': '#4c1d95', '950': '#2e1065'
      },
      // Purple
      'purple': {
        '50': '#faf5ff', '100': '#f3e8ff', '200': '#e9d5ff', '300': '#d8b4fe',
        '400': '#c084fc', '500': '#a855f7', '600': '#9333ea', '700': '#7e22ce',
        '800': '#6b21a8', '900': '#581c87', '950': '#3b0764'
      },
      // Fuchsia
      'fuchsia': {
        '50': '#fdf4ff', '100': '#fae8ff', '200': '#f5d0fe', '300': '#f0abfc',
        '400': '#e879f9', '500': '#d946ef', '600': '#c026d3', '700': '#a21caf',
        '800': '#86198f', '900': '#701a75', '950': '#4a044e'
      },
      // Pink
      'pink': {
        '50': '#fdf2f8', '100': '#fce7f3', '200': '#fbcfe8', '300': '#f9a8d4',
        '400': '#f472b6', '500': '#ec4899', '600': '#db2777', '700': '#be185d',
        '800': '#9d174d', '900': '#831843', '950': '#500724'
      },
      // Rose
      'rose': {
        '50': '#fff1f2', '100': '#ffe4e6', '200': '#fecdd3', '300': '#fda4af',
        '400': '#fb7185', '500': '#f43f5e', '600': '#e11d48', '700': '#be123c',
        '800': '#9f1239', '900': '#881337', '950': '#4c0519'
      }
    };

    return colorMap[color]?.[shade] || '#000000';
  };

  // Helper function to remove all color classes
  const removeColorClasses = (classes: string): string => {
    return classes
      // Xóa text-color chuẩn, KHÔNG xóa hover: hoặc prefix khác
      .replace(/(?:^|\s)text-(white|black|slate|gray|zinc|neutral|stone|red|orange|amber|yellow|lime|green|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia|pink|rose)(-\d+)?/g, '')
      // Xóa text-[#hex], KHÔNG xóa hover:text-[#hex]
      .replace(/(?:^|\s)text-\[#.*?\]/g, '')
      // Chuẩn hoá khoảng trắng
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Helper function to remove margin-y classes
  const removeMyClasses = (classes: string): string => {
    return classes
      .replace(/(?:^|\s)my-\[\d+px\]/g, '')  // Remove bracket notation classes like my-[90px]
      .replace(/\bmy-\d+\b/g, '')           // Remove scale classes like my-8
      .replace(/\s+/g, ' ')                 // Normalize whitespace
      .trim();
  };

  // Helper function to remove margin-x classes
  const removeMxClasses = (classes: string): string => {
    return classes
      .replace(/(?:^|\s)mx-\[\d+px\]/g, '')  // Remove bracket notation classes like my-[90px]
      .replace(/\bmx-\d+\b/g, '')           // Remove scale classes like my-8
      .replace(/\s+/g, ' ')                 // Normalize whitespace
      .trim();
  };

  // Helper function to remove padding-y classes
  const removePyClasses = (classes: string): string => {
    return classes
      .replace(/(?:^|\s)py-\[\d+px\]/g, '')  // Remove bracket notation classes like my-[90px]
      .replace(/\bpy-\d+\b/g, '')           // Remove scale classes like my-8
      .replace(/\s+/g, ' ')                 // Normalize whitespace
      .trim();
  };

  // Helper function to remove padding-x classes
  const removePxClasses = (classes: string): string => {
    return classes
      .replace(/(?:^|\s)px-\[\d+px\]/g, '')  // Remove bracket notation classes like my-[90px]
      .replace(/\bpx-\d+\b/g, '')           // Remove scale classes like my-8
      .replace(/\s+/g, ' ')                 // Normalize whitespace
      .trim();
  };

  const replaceLargestTextSize = (prevClasses: string, value: string) => {
    const breakpoints = ['2xl', 'xl', 'lg', 'md', 'sm', '']; // lớn đến nhỏ
    const regex = /\b(?:(sm|md|lg|xl|2xl):)?text-(xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|8xl|9xl)\b/g;

    let largestMatch = null;
    let largestIndex = -1;

    // Tìm match có breakpoint lớn nhất
    let match;
    while ((match = regex.exec(prevClasses)) !== null) {
      const [full, bp = ''] = match;
      const index = breakpoints.indexOf(bp);
      if (index !== -1 && (largestIndex === -1 || index < largestIndex)) {
        largestMatch = { full, prefix: bp };
        largestIndex = index;
      }
    }

    // Nếu tìm thấy thì thay thế
    if (largestMatch) {
      const newClass = largestMatch.prefix ? `${largestMatch.prefix}:text-${value}` : `text-${value}`;
      return prevClasses.replace(largestMatch.full, newClass).trim();
    }

    // Không tìm thấy thì thêm mới
    return `${prevClasses} text-${value}`.trim();
  };

  const getLargestTextSize = (classList: string) => {
    const breakpoints = ['2xl', 'xl', 'lg', 'md', 'sm', '']; // từ lớn đến nhỏ
    const regex = /\b(?:(sm|md|lg|xl|2xl):)?text-(xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|8xl|9xl)\b/g;

    let largestMatch = null;
    let largestIndex = -1;

    let match;
    while ((match = regex.exec(classList)) !== null) {
      const [_, bp = '', size] = match;
      const index = breakpoints.indexOf(bp);
      if (index !== -1 && (largestIndex === -1 || index < largestIndex)) {
        largestMatch = size;
        largestIndex = index;
      }
    }

    return largestMatch || "base"; // fallback nếu không có class
  };

  const replaceLargestFontWeight = (prevClasses: string, value: string) => {
    const breakpoints = ['2xl', 'xl', 'lg', 'md', 'sm', '']; // từ lớn đến nhỏ
    const regex = /\b(?:(sm|md|lg|xl|2xl):)?font-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)\b/g;

    let largestMatch = null;
    let largestIndex = -1;

    // Tìm class font-weight có breakpoint lớn nhất
    let match;
    while ((match = regex.exec(prevClasses)) !== null) {
      const [full, bp = ''] = match;
      const index = breakpoints.indexOf(bp);
      if (index !== -1 && (largestIndex === -1 || index < largestIndex)) {
        largestMatch = { full, prefix: bp };
        largestIndex = index;
      }
    }

    // Nếu tìm thấy thì thay thế nó
    if (largestMatch) {
      const newClass = largestMatch.prefix ? `${largestMatch.prefix}:font-${value}` : `font-${value}`;
      return prevClasses.replace(largestMatch.full, newClass).trim();
    }

    // Nếu không có class font-weight nào thì thêm mới
    return `${prevClasses} font-${value}`.trim();
  };

  const getLargestFontWeight = (classList: string) => {
    const breakpoints = ['2xl', 'xl', 'lg', 'md', 'sm', '']; // từ lớn đến nhỏ
    const regex = /\b(?:(sm|md|lg|xl|2xl):)?font-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)\b/g;

    let largestMatch = null;
    let largestIndex = -1;

    let match;
    while ((match = regex.exec(classList)) !== null) {
      const [_, bp = '', weight] = match;
      const index = breakpoints.indexOf(bp);
      if (index !== -1 && (largestIndex === -1 || index < largestIndex)) {
        largestMatch = weight;
        largestIndex = index;
      }
    }

    return largestMatch || "normal"; // fallback nếu không có class
  };

  // Content state for editing element text
  const [elementContent, setElementContent] = useState<string>('');

  const [elementImageUrl, setElementImageUrl] = useState<string>('');

  // State to track original values for revert functionality
  const [originalElementClasses, setOriginalElementClasses] = useState<string>('');
  const [originalElementContent, setOriginalElementContent] = useState<string>('');

  // Add state for XPath to reliably locate elements
  const [selectedElementXPath, setSelectedElementXPath] = useState<string>('');

  // Helper function to get XPath of an element
  const getElementXPath = (element: HTMLElement): string => {
    if (!element) return '';

    let xpath = '';
    let currentElement: HTMLElement | null = element;

    while (currentElement && currentElement.nodeType === Node.ELEMENT_NODE) {
      let elementName = currentElement.nodeName.toLowerCase();

      // Add id if available for more specificity
      if (currentElement.id) {
        xpath = `//${elementName}[@id="${currentElement.id}"]` + xpath;
        break;
      } else {
        // Count previous siblings with same tag name
        let count = 0;
        let sibling = currentElement.previousSibling;

        while (sibling) {
          if (sibling.nodeType === Node.ELEMENT_NODE &&
            (sibling as HTMLElement).nodeName.toLowerCase() === elementName) {
            count++;
          }
          sibling = sibling.previousSibling;
        }

        // XPath indexes are 1-based
        const index = count + 1;
        xpath = `/${elementName}[${index}]${xpath}`;

        // Move up to parent
        currentElement = currentElement.parentElement;
      }
    }
    // console.log(xpath);
    return xpath || '/';
  };

  // Find element by XPath
  const getElementByXPath = (xpath: string, doc: Document): HTMLElement | null => {
    try {
      const result = doc.evaluate(
        xpath,
        doc,
        null,
        XPathResult.FIRST_ORDERED_NODE_TYPE,
        null
      );
      return result.singleNodeValue as HTMLElement;
    } catch (e) {
      console.error('Error finding element by XPath:', e);
      return null;
    }
  };

  // When an element is selected, update the content state and save original values
  // useEffect(() => {
  //   if (selectedElement) {
  //     // Save current content for editing
  //     setElementContent(selectedElement.textContent || '');

  //     // Save original values for potential revert
  //     setOriginalElementClasses(selectedElement.className.replace('element-selected', '').trim());
  //     setOriginalElementContent(selectedElement.textContent || '');
  //   }
  // }, [selectedElement]);

  // Function to revert changes to original values
  const handleDiscardChanges = () => {
    if (iframeRef.current && editorRef.current) {
      try {
        const doc = iframeRef.current.contentDocument;
        let editorContent = editorRef.current.getModel().getValue();
        if (doc) {
          // console.log('Updating iframe via document.write');
          try {
            doc.open();
            doc.write(addUibrainxSelectorPopup(editorContent));
            doc.close();
          } catch (writeError) {
            const errorMsg = writeError instanceof Error ? writeError.message : String(writeError);
            addConsoleLog('error', `HTML parsing error: ${errorMsg}`);
            console.error('Error writing to iframe document:', writeError);
          }
        }
      } catch (error) {
        console.error('Error updating iframe:', error);
      }
    }

    // Exit edit mode after discarding changes
    setIsEditMode(false);
    setEditData({});
  };

  // Function to apply classes to the selected element
  const applyClassesToElement = useCallback((newClasses: string) => {
    if (!selectedElement) return;

    // Save current selection state
    const wasSelected = selectedElement.classList.contains('element-selected');

    // Check if element is SVG or a child of SVG
    // SVG elements and their children have a specific namespace URI
    const isSvgElement = hasSvgParent(selectedElement);

    if (isSvgElement) {
      // Clear all classes and add new ones for SVG elements
      selectedElement.setAttribute('class', newClasses.trim());
    } else {
      // For HTML elements, we can set className directly
      selectedElement.className = newClasses;
    }

    // Restore selection class if it was previously selected
    if (wasSelected) {
      selectedElement.classList.add('element-selected');
    }
  }, [selectedElement]);

  // Function to update classes and apply them to the element
  const updateElementClasses = useCallback((newClassesOrFn: string | ((prevClasses: string) => string)) => {
    if (typeof newClassesOrFn === 'function') {
      setElementClasses(prevClasses => {
        const newClasses = newClassesOrFn(prevClasses);
        applyClassesToElement(newClasses);
        
        // Update editData state with the new classes
        if (activePage && selectedElementDataId) {
          setEditData(prevEditData => ({
            ...prevEditData,
            [activePage]: {
              ...prevEditData[activePage],
              [selectedElementDataId]: {
                ...prevEditData[activePage]?.[selectedElementDataId],
                classes: newClasses
              }
            }
          }));
        }
        
        return newClasses;
      });
    } else {
      setElementClasses(newClassesOrFn);
      applyClassesToElement(newClassesOrFn);
      
      // Update editData state with the new classes
      if (activePage && selectedElementDataId) {
        setEditData(prevEditData => ({
          ...prevEditData,
          [activePage]: {
            ...prevEditData[activePage],
            [selectedElementDataId]: {
              ...prevEditData[activePage]?.[selectedElementDataId],
              classes: newClassesOrFn
            }
          }
        }));
      }
    }
  }, [setElementClasses, applyClassesToElement, activePage, selectedElementDataId, setEditData]);

  // Handle cloning URL (getting both style and content in one action)
  const handleCloneUrl = async (url: string) => {
    // Set action in progress to show loading state
    setActionInProgress({ url, action: 'clone' });

    try {
      // Call backend to scrape the website with puppeteer
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/messages/scrape', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          url
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to scrape URL: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status !== 'success' || !data.data || !data.data.scrapeId) {
        throw new Error('Invalid response from server');
      }

      const scrapeId = data.data.scrapeId;

      // Call backend to analyze the scrape
      const responseAnalysis = await fetch('/api/v1/messages/analysis-scrape', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          scrapeId
        })
      });

      const fetchAnalysis = async (): Promise<any> => {
        const responseAnalysis = await fetch(`/api/v1/messages/analysis-scrape/${scrapeId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const dataAnalysis = await responseAnalysis.json();
        if (dataAnalysis.status === 'success' && dataAnalysis.data && dataAnalysis.data.analysis) {
          return dataAnalysis;
        }
        return null;
      };

      // Poll for analysis results
      let dataAnalysis: any = null;
      const interval = setInterval(async () => {
        try {
          const result = await fetchAnalysis();
          if (result) {
            clearInterval(interval);
            dataAnalysis = result;
          }
        } catch (error) {
          console.error('Error fetching analysis:', error);
        }
      }, 5000);

      // Wait for the analysis to complete (with a reasonable timeout)
      const timeout = 180000; // 3 minutes timeout
      const startTime = Date.now();

      while (!dataAnalysis && (Date.now() - startTime) < timeout) {
        await new Promise(resolve => setTimeout(resolve, 100)); // Small delay to prevent busy waiting
      }

      clearInterval(interval);

      if (!dataAnalysis) {
        throw new Error('Analysis timeout - please try again');
      }

      // Add the action to selected actions
      setSelectedActions(prev => {
        return {
          ...prev,
          [url]: ['clone']
        };
      });

      // Update the user input with the analysis data
      setUserInput(`Clone this website design and structure: ${url}\n\n${dataAnalysis.data.analysis}`);

    } catch (error) {
      console.error('Error cloning URL:', error);
      setError(error instanceof Error ? error.message : String(error));
      setOpenSnackbar(true);
    } finally {
      // Clear the action in progress
      setActionInProgress(null);
    }
  };

  // Handle redesign URL action
  const handleRedesignUrl = async (url: string) => {
    // Set action in progress to show loading state
    setActionInProgress({ url, action: 'redesign' });

    try {
      // Call backend to scrape the website with puppeteer
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/messages/scrape', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          url
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to scrape URL: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status !== 'success' || !data.data || !data.data.scrapeId) {
        throw new Error('Invalid response from server');
      }

      const scrapeId = data.data.scrapeId;

      // Call backend to analyze the scrape
      const responseAnalysis = await fetch('/api/v1/messages/analysis-scrape', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          scrapeId
        })
      });

      const dataAnalysis = await responseAnalysis.json();

      if (dataAnalysis.status !== 'success' || !dataAnalysis.data || !dataAnalysis.data.analysis) {
        throw new Error('Invalid response from server');
      }

      // Add the action to selected actions
      setSelectedActions(prev => {
        return {
          ...prev,
          [url]: ['redesign']
        };
      });

      // Update the user input with the analysis data
      setUserInput(`Below is the original website design. Please redesign it with a modern and visually appealing layout, but keep the original content: ${url}\n\n${dataAnalysis.data.analysis}.\n\nAbove is the original website. Redesign it and retain the content of the page.`);

    } catch (error) {
      console.error('Error analyzing URL for redesign:', error);
      setError(error instanceof Error ? error.message : String(error));
      setOpenSnackbar(true);
    } finally {
      // Clear the action in progress
      setActionInProgress(null);
    }
  };

  useEffect(() => {
    openModalRef.current = openModal;
    const iframe = iframeRef.current;

    if (iframe) {
      const iframeDocument = iframe.contentDocument;
      const iframeWindow = iframe.contentWindow;
      if (iframeDocument && iframeWindow) {
        const iframeBody = iframeDocument.getElementsByTagName('body')[0];
        if (!iframeBody) return;
        if (!openModalRef.current) {
          // @ts-ignore
          iframeWindow.uibrainxSelectorPopup({open: false, position: { x: 0, y: 0 }, current: [], parent: []});
          // @ts-ignore
          iframeWindow.uibrainxSelectorRect({hide: true});  
        }
      }
    }
  }, [openModal]);

  useEffect(() => {
    exportTypeRef.current = exportType;
  }, [exportType]);

  useEffect(() => {
    const iframe = iframeRef.current;

    if (iframe) {
      const iframeDocument = iframe.contentDocument;
      const iframeWindow = iframe.contentWindow;
        if (iframeDocument && iframeWindow) {
          const iframeBody = iframeDocument.getElementsByTagName('body')[0];
          if (!iframeBody) return;
          const handleClickIframe = (event: MouseEvent) => {
            event.preventDefault();
            const element = event.target as HTMLElement;
            if (element.id !== 'uibrainx-selector-popup' && !element.closest('#uibrainx-selector-popup')) {
              const parent = element.parentElement;
              const selectorCurrent = element.getAttribute('data-id') ? "[data-id='" + element.getAttribute('data-id') + "']" : finder(element, {
                root: iframeBody,
              });

              let selectorCurrentList = [selectorCurrent];

              let selectorParentList:any = [];
              let selectorParent = null;

              try {
                selectorParent = parent ? ( parent.getAttribute('data-id') ? "[data-id='" + parent.getAttribute('data-id') + "']" : finder(parent, {
                  root: iframeBody,
                }) ) : null;
              } catch (error) {
                console.log(error);
              }
              
              if (selectorParent) {
                selectorParentList = [selectorParent];
              }

              if (openModalRef.current && exportTypeRef.current === 'selector') {
                // @ts-ignore
                iframeWindow.uibrainxSelectorPopup({ open: true, position: { x: event.pageX, y: event.pageY }, current: selectorCurrentList, parent: selectorParentList });
              } else {
                // @ts-ignore
                iframeWindow.uibrainxSelectorPopup({open: false, position: { x: 0, y: 0 }, current: [], parent: []});
              }
            }

            const selector = element.getAttribute('data-uibrainx-selector-item');
            if (selector) {
              setExportType('selector');
              setCssSelector(selector);
              // @ts-ignore
              iframeWindow.uibrainxSelectorPopup({open: false, position: { x: 0, y: 0 }, current: [], parent: []});
              // @ts-ignore
              iframeWindow.uibrainxSelectorRect({hide: true});
            }
          };

          const handleHoverIframe = (event: MouseEvent, mouseEvent: string) => {
            debounce((event) => {
              if (event.target.id !== 'uibrainx-selector-popup' && !event.target.closest('#uibrainx-selector-popup')) {
                if (exportTypeRef.current === 'selector') {
                  const element = event.target as HTMLElement;
                  const selector = element.getAttribute('data-id') ? "[data-id='" + element.getAttribute('data-id') + "']" : finder(element, {
                    root: iframeBody,
                  });
                  iframeDocument.querySelectorAll(selector).forEach((item: any) => {
                    switch (mouseEvent) {
                      case 'mouseover':
                        // item.style.outline = '1px solid #3364E3';
                        if (openModalRef.current) {
                          // @ts-ignore
                          iframeWindow.uibrainxSelectorRect(item.getBoundingClientRect());
                        }
                        break;
                      case 'mouseout':
                        // item.style.outline = 'none';
                        break;
                    }
                  });
                } else {
                  // @ts-ignore
                  iframeWindow.uibrainxSelectorRect({hide: true});
                }
              }
            }, 200)(event);
          };

          const handleClickPageLink = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (target.tagName === 'A') {
              let href = target.getAttribute('href');
              if (href && href.startsWith('/')) {
                href = href.substring(1);
              }
              if (href && href.includes('.html') && !href.startsWith('http')) {
                const page = pages.find((p: any) => p.url === href);
                if (page) {
                  setActivePage(page._id);
                  navigate(`/projects/${projectId}/${page._id}`, { replace: true });
                }
              }
            }
          };
          iframeBody.addEventListener('click', handleClickPageLink);

          iframeBody.addEventListener('click', handleClickIframe);
          iframeBody.addEventListener('mouseover', (event) => {
            handleHoverIframe(event, 'mouseover');
          });
          iframeBody.addEventListener('mouseout', (event) => {
            handleHoverIframe(event, 'mouseout');
          });
          return () => {
            iframeBody.removeEventListener('click', handleClickIframe);
            iframeBody.removeEventListener('click', handleClickPageLink);
            iframeBody.removeEventListener('mouseover', (event) => {
              handleHoverIframe(event, 'mouseover');
            });
            iframeBody.removeEventListener('mouseout', (event) => {
              handleHoverIframe(event, 'mouseout');
            });
          };
        }
    }
  }, [iframeLoaded, pageId]);

  // Main render function
  return (
    <div className={isSaving ? 'project project--saving' : 'project'}>
      {shouldShowWelcomeScreen ? (
        // Welcome screen - when project is new or has no pages
        <div className="project-welcome">
          <div className="project-welcome__content">
            <div className="project-welcome__header">
              <h1>Welcome to Your New Project</h1>
              <p>Choose how you'd like to get started:</p>
            </div>
            <div className="project-welcome__options">
              <div className="project-welcome__option" onClick={handleOpenTemplates}>
                <div className="project-welcome__option-icon">
                  <Iconify icon="material-symbols:dashboard-customize" width={48} height={48} />
                </div>
                <div className="project-welcome__option-content">
                  <h3>Prebuilt Template</h3>
                  <p>Choose from our ready-made templates to get a quick start</p>
                </div>
              </div>
              <div className="project-welcome__option" onClick={handleCreateBlankPage}>
                <div className="project-welcome__option-icon">
                  <Iconify icon="material-symbols:add" width={48} height={48} />
                </div>
                <div className="project-welcome__option-content">
                  <h3>Blank Page</h3>
                  <p>Start with a clean slate and build your page from scratch</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        projectLoaded && (
          <div className={`chat-preview ${isExpanded ? 'expanded' : ''}`}>
            {/* Main project interface */}
            <div className={`chat-preview__chat ${!showHistory ? 'chat-preview__chat--history-collapsed' : ''} ${showClassForm ? 'chat-preview__chat--show-class-form' : ''} ${showUrlActions ? 'chat-preview__chat--show-url-actions' : ''}`}>
              <div className="chat-preview__chat-editor">
                {/* Page Tabs */}
                <div className="page-tabs">
                  <div className="page-tabs__list">
                    <button
                      className="page-tabs__back"
                      onClick={goBackToProjects}
                      title="Back to Projects"
                    >
                      <Iconify icon="mdi:arrow-left" width={16} height={16} />
                    </button>

                    {pages.filter((page: any) => !page.deleted).map((page: any) => (
                      <div
                        key={page._id}
                        className={`page-tabs__item ${activePage === page._id ? 'page-tabs__item--active' : ''}`}
                        onClick={() => handleClickPage(page._id)}
                      >
                        {isEditingName === page._id ? (
                          <input
                            type="text"
                            className="page-tabs__name-input"
                            value={page.name || 'Untitled'}
                            onChange={(e) => handleChangePage(e, page._id)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === 'Enter') {
                                setIsEditingName(null);
                              }
                            }}
                            onBlur={() => setIsEditingName(null)}
                            autoFocus
                          />
                        ) : (
                          <span className="page-tabs__name">{page.name || 'Untitled'}</span>
                        )}
                        {pages.length > 1 && (
                          <div className="page-tabs__menu">
                            <button
                              className="page-tabs__menu-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePage(page._id);
                              }}
                              title="Delete page"
                            >
                              <Iconify icon="material-symbols:close" width={14} height={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {/* <button
                      className="page-tabs__add"
                      disabled={buttonLoading}
                      onClick={handleOpenAddPageMenu}
                    >
                      <Iconify icon="material-symbols:add" width={18} height={18} />
                      <span style={{ marginLeft: '4px' }}>Add new page</span>
                    </button> */}
                  </div>
                </div>


                {/* MUI Popover for add page actions */}
                <Popover
                  open={Boolean(addPageAnchorEl)}
                  anchorEl={addPageAnchorEl}
                  onClose={handleCloseAddPageMenu}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  classes={{
                    paper: 'page-actions-popover'
                  }}
                >
                  <div className="page-actions-menu">
                    {pages.length > 0 && (
                      <button
                        className="page-actions-item"
                        onClick={() => handleAddPageAction('duplicate')}
                      >
                        <Iconify icon="material-symbols:content-copy" width={16} height={16} />
                        <div className="page-actions-item-content">
                          <span>Duplicate current page</span>
                          <div className="page-actions-item-description">To keep the page style</div>
                        </div>
                      </button>
                    )}
                    <button
                      className="page-actions-item"
                      onClick={handleOpenTemplates}
                    >
                      <Iconify icon="material-symbols:dashboard-customize" width={16} height={16} />
                      <div className="page-actions-item-content">
                        <span>Prebuilt Template</span>
                        <div className="page-actions-item-description">Choose from ready-made templates</div>
                      </div>
                    </button>
                    <button
                      className="page-actions-item"
                      onClick={() => handleAddPageAction('blank')}
                    >
                      <Iconify icon="material-symbols:add" width={16} height={16} />
                      <div>
                        <span>Blank page</span>
                      </div>
                    </button>
                  </div>
                </Popover>

                {/* Color Palette Popover */}
                <Popover
                  open={showColorPalette}
                  anchorEl={colorPaletteAnchorEl}
                  onClose={handleCloseColorPalette}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  classes={{
                    paper: 'color-palette-popover'
                  }}
                >
                  <div className="color-palette-container">
                    <div className="color-palette-header">
                      <h4>Color Palettes</h4>
                      <div className="color-palette-search">
                        <input
                          type="text"
                          placeholder="Search palettes..."
                          value={colorPaletteSearch}
                          onChange={handleColorPaletteSearchChange}
                        />
                        {colorPaletteSearch && (
                          <button
                            className="color-palette-search-clear"
                            onClick={() => setColorPaletteSearch('')}
                          >
                            <Iconify icon="mdi:close" width={16} height={16} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="color-palette-list">
                      {filteredColorPalettes.map((palette, index) => (
                        <div
                          key={index}
                          className="color-palette-item"
                          onClick={() => handleSelectColorPalette(palette)}
                        >
                          <div className="color-palette-name">{palette.name}</div>
                          <div className="color-palette-colors">
                            {palette.colors.map((color, colorIndex) => (
                              <div
                                key={colorIndex}
                                className="color-palette-color"
                                style={{ backgroundColor: color }}
                                title={color}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                      {filteredColorPalettes.length === 0 && (
                        <div className="color-palette-no-results">
                          No palettes match your search.
                        </div>
                      )}
                    </div>
                  </div>
                </Popover>

                {/* Font Selector Popover */}
                <Popover
                  open={showFontSelector}
                  anchorEl={fontSelectorAnchorEl}
                  onClose={handleCloseFontSelector}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  classes={{
                    paper: 'font-selector-popover'
                  }}
                >
                  <div className="font-selector-container">
                    <div className="font-selector-header">
                      <h4>Font Selection</h4>
                      <div className="font-selector-search">
                        <input
                          type="text"
                          placeholder="Search fonts..."
                          value={fontSearch}
                          onChange={handleFontSearchChange}
                        />
                        {fontSearch && (
                          <button
                            className="font-selector-search-clear"
                            onClick={() => setFontSearch('')}
                          >
                            <Iconify icon="mdi:close" width={16} height={16} />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="font-selector-content">
                      {filteredFonts ? (
                        // Show search results
                        filteredFonts.length > 0 ? (
                          <div className="font-selector-search-results">
                            {filteredFonts.map((font) => (
                              <div
                                key={font.name}
                                className="font-item"
                                onClick={() => handleSelectFont(font)}
                                style={{ fontFamily: font.name }}
                              >
                                {font.name}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="font-selector-no-results">
                            No fonts match your search
                          </div>
                        )
                      ) : (
                        // Show categorized fonts (original display)
                        fontCategories.map((category) => (
                          <div key={category.name} className="font-category">
                            <h5 className="font-category-title">{category.name}</h5>
                            <div className="font-list">
                              {category.fonts.map((font) => (
                                <div
                                  key={font.name}
                                  className="font-item"
                                  onClick={() => handleSelectFont(font)}
                                  style={{ fontFamily: font.name }}
                                >
                                  {font.name}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </Popover>

                {/* Streaming sections display above code editor */}
                {streamingSections.currentSection >= 0 && streamingSections.sections[streamingSections.currentSection] && (
                  <div className="streaming-sections">
                    {(() => {
                      const currentSection = streamingSections.sections[streamingSections.currentSection];
                      return (
                        <div className={`streaming-section streaming-section--${currentSection.type}`}>
                          <div className="streaming-section__header">
                            <div className="streaming-section__title">
                              {currentSection.type === 'search' && (
                                <>
                                  <Iconify icon="material-symbols:search" width={16} height={16} />
                                  <span>Searching</span>
                                </>
                              )}
                              {currentSection.type === 'replace' && (
                                <>
                                  <Iconify icon="material-symbols:edit" width={16} height={16} />
                                  <span>Modifying</span>
                                </>
                              )}
                              {currentSection.type === 'explanation' && (
                                <>
                                  <Iconify icon="material-symbols:info" width={16} height={16} />
                                  <span>Analysis</span>
                                </>
                              )}
                            </div>
                            <div className="streaming-section__status">
                              {currentSection.isComplete ? (
                                <Iconify icon="material-symbols:check-circle" width={16} height={16} />
                              ) : (
                                <div className="streaming-section__status--streaming">
                                  <div className="streaming-dots">
                                    <div className="streaming-dot"></div>
                                    <div className="streaming-dot"></div>
                                    <div className="streaming-dot"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="streaming-section__content">
                            <pre
                              ref={streamingCodeRef}
                              className="streaming-section__code"
                            >
                              {currentSection.content}
                            </pre>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div className="code-editor">
                  {codeLoading ? (
                    <div className="code-editor__loading">
                      <div className="code-editor__loading-spinner">
                        <svg width="36" height="36" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="256" cy="256" r="254" stroke="url(#paint0_linear_305_38)" stroke-width="24" stroke-dasharray="1590 1590" stroke-dashoffset="1590">
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              from="0 256 256"
                              to="360 256 256"
                              dur="2s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="stroke-dashoffset"
                              values="1590;0"
                              dur="2s"
                              repeatCount="indefinite"
                            />
                          </circle>
                          <g transform="scale(0.7) translate(110, 110)">
                            <path d="M496.007 233.729L377.781 193.688C349.823 184.211 327.789 162.177 318.312 134.219L278.271 15.9926C271.163 -5.33087 240.837 -5.33087 233.729 15.9926L193.688 134.219C184.211 162.177 162.177 184.211 134.219 193.688L15.9926 233.729C-5.33087 240.837 -5.33087 271.163 15.9926 278.271L134.219 318.312C162.177 327.789 184.211 349.823 193.688 377.781L233.729 496.007C240.837 517.331 271.163 517.331 278.271 496.007L318.312 377.781C327.789 349.823 349.823 327.789 377.781 318.312L496.007 278.271C517.331 271.163 517.331 240.837 496.007 233.729Z" fill="url(#paint0_linear_305_38)" />
                          </g>
                          <defs>
                            <linearGradient id="paint0_linear_305_38" x1="256" y1="0" x2="256" y2="512" gradientUnits="userSpaceOnUse">
                              <stop stop-color="#9031F0" />
                              <stop offset="1" stop-color="#FED3A9" />
                            </linearGradient>
                          </defs>
                        </svg>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Editor
                        height="100%"
                        defaultLanguage="html"
                        value={previewHtml || '<!-- Preview HTML will appear here -->'}
                        theme="vs-dark"
                        onChange={handleEditorChange}
                        onMount={handleEditorDidMount}
                        options={{
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          fontSize: 10,
                          wordWrap: 'on',
                          wrappingIndent: 'indent',
                          automaticLayout: true,
                          readOnly: isSaving || isStreaming || isWaitingResponse ? true : false
                        }}
                      />
                      {isStreaming && (
                        <button
                          className="code-editor__stop-button"
                          onClick={handleStopStreaming}
                          title="Stop generating"
                        >
                          <StopIcon fontSize="small" />
                          Stop
                        </button>
                      )}

                      {/* Add Save button when editing but not streaming */}
                      {isEditEditorMode[String(activePage)] && !isStreaming && (
                        <div className="code-editor__buttons">
                          <button
                            className="code-editor__button code-editor__button--discard"
                            onClick={() => {
                              setPreviewHtml(previousSavedHtml);
                              setIsEditEditorMode(prev => ({ ...prev, [String(activePage)]: false }));
                            }}
                          >
                            Discard
                          </button>
                          <button
                            className="code-editor__button code-editor__button--save"
                            onClick={() => handleSaveHtml(previewHtml)}
                          >
                            Save changes
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Element class editing form */}
              {showClassForm && (
                <div className="element-class-form-container">
                  <form onSubmit={handleClassSubmit} className="element-class-form">
                    <div className="element-class-form-header">
                      <div className="element-form-header-left">
                        <div
                          className="element-form-back-button"
                          title="Select parent element"
                          onClick={() => {
                            if (selectedElement && selectedElement.parentElement) {
                              // Don't select body or html elements
                              if (selectedElement.parentElement.tagName.toLowerCase() === 'body' ||
                                selectedElement.parentElement.tagName.toLowerCase() === 'html') {
                                return;
                              }

                              // Remove current selection class
                              selectedElement.classList.remove('element-selected');

                              // Select the parent element
                              const parentElement = selectedElement.parentElement;

                              // click the parent element
                              const event = new MouseEvent('click', {
                                bubbles: true,
                                cancelable: true,
                                view: window
                              });
                              parentElement.dispatchEvent(event);
                            }
                          }}
                        >
                          <Iconify icon="humbleicons:arrow-go-back" width={14} height={14} />
                        </div>
                        <h4>{selectedElement?.tagName?.toLowerCase() || 'div'}</h4>
                      </div>
                      <div className="element-form-header-actions">
                        <IconButton
                          onClick={() => {
                            if (selectedElement) {
                              // Get the data-id of the selected element
                              const elementId = selectedElement.getAttribute('data-id');

                              if (elementId && editorRef.current) {
                                // Use DOMParser (available in browsers) instead of JSDOM
                                const parser = new DOMParser();
                                const doc = parser.parseFromString(previewHtml, 'text/html');

                                // Find the element with the same data-id
                                const elementToRemove = doc.querySelector(`[data-id="${elementId}"]`);

                                if (elementToRemove) {
                                  // Remove any element-selected class before removing the element
                                  const selectedElements = doc.querySelectorAll('.element-selected');
                                  selectedElements.forEach(el => {
                                    el.classList.remove('element-selected');
                                  });

                                  // Remove the element
                                  elementToRemove.remove();

                                  // Get the updated HTML content
                                  const newContent = new XMLSerializer().serializeToString(doc);

                                  const model = editorRef.current.getModel();
                                  if (!model) return;

                                  // Use executeEdits to remove the element
                                  editorRef.current.executeEdits('remove-element', [
                                    {
                                      range: model.getFullModelRange(),
                                      text: newContent
                                    }
                                  ]);

                                  // Also remove the element from the preview
                                  selectedElement.remove();
                                  setShowClassForm(false);
                                  setIsEditMode(false);
                                } else {
                                  // Fallback to direct removal if data-id is not available
                                  selectedElement.remove();
                                  setShowClassForm(false);

                                  // Update the preview HTML
                                  if (iframeRef.current && iframeRef.current.contentDocument) {
                                    setPreviewHtml(iframeRef.current.contentDocument.documentElement.outerHTML);
                                  }
                                }
                              } else {
                                // Fallback to direct removal if data-id is not available
                                selectedElement.remove();
                                setShowClassForm(false);

                                // Update the preview HTML
                                if (iframeRef.current && iframeRef.current.contentDocument) {
                                  setPreviewHtml(iframeRef.current.contentDocument.documentElement.outerHTML);
                                }
                              }
                            }
                          }}
                          size="small"
                          title="Delete Element"
                        >
                          <Iconify icon="mdi:trash-can-outline" width={16} height={16} />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            // Discard changes when closing
                            handleDiscardChanges();
                            setShowClassForm(false);
                          }}
                          size="small"
                          title="Close"
                        >
                          <Iconify icon="mdi:close" width={16} height={16} />
                        </IconButton>
                      </div>
                    </div>

                    <div className="element-class-form-content">
                      {/* Content option - only for non-wrapper elements */}
                      {!wrapperElements.includes(selectedElement?.tagName?.toLowerCase() || '') && (
                        <div className="element-property-group">
                          <label>Content</label>
                          <div className="property-inputs">
                            <textarea
                              className="content-input"
                              value={elementContent}
                              onChange={(e) => {
                                // Update the state first
                                setElementContent(e.target.value);
                                setEditData(prevEditData => {
                                  if (!activePage || !selectedElementDataId) return prevEditData;
                                  const currentElementData = prevEditData[activePage]?.[selectedElementDataId] || {};
                                  return {
                                    ...prevEditData,
                                    [activePage]: {
                                      ...prevEditData[activePage],
                                      [selectedElementDataId]: {
                                        ...currentElementData,
                                        content: e.target.value
                                      }
                                    }
                                  };
                                });

                                // Then update the DOM element for preview
                                if (selectedElement) {
                                  selectedElement.innerHTML = e.target.value;
                                }
                              }}
                              placeholder="Element content"
                            ></textarea>
                          </div>
                        </div>
                      )}

                      {selectedElement?.tagName?.toLowerCase() === 'img' && (
                        <div className="element-property-group">
                          <label>Image URL</label>
                          <div className="property-inputs">
                            <textarea
                              className="content-input"
                              value={elementImageUrl}
                              onChange={(e) => {
                                // Update the state first
                                setElementImageUrl(e.target.value);
                                setEditData(prevEditData => {
                                  if (!activePage || !selectedElementDataId) return prevEditData;
                                  const currentElementData = prevEditData[activePage]?.[selectedElementDataId] || {};
                                  return {
                                    ...prevEditData,
                                    [activePage]: {
                                      ...prevEditData[activePage],
                                      [selectedElementDataId]: {
                                        ...currentElementData,
                                        imageUrl: e.target.value
                                      }
                                    }
                                  };
                                });

                                // Then update the DOM element for preview
                                if (selectedElement) {
                                  (selectedElement as HTMLImageElement).src = e.target.value;
                                }
                              }}
                            ></textarea>
                          </div>
                        </div>
                      )}

                      <div className="element-property-group">
                        <label>Margin</label>
                        <div className="property-inputs">
                          <div className="property-input-group">
                            <Iconify icon="mdi:arrow-expand-vertical" width={18} height={18} />
                            <input
                              type="number"
                              min={0}
                              value={(() => {
                                const match = elementClasses.match(/(?:^|\s)my-\[(\d+)px\]/);
                                return match?.[1] || "0";
                              })()}
                              className="property-input"
                              onChange={(e) => {
                                const value = e.target.value;
                                // console.log(value);
                                updateElementClasses(prevClasses => {
                                  // Use the helper function to remove existing my-* classes
                                  const updatedClasses = removeMyClasses(prevClasses);

                                  // Add new my-* class if value is greater than 0
                                  return value === ""
                                    ? updatedClasses
                                    : (value === "0") ? `${updatedClasses} my-0` : `${updatedClasses} my-[${value}px]`;
                                });
                              }}
                            />
                          </div>
                          <div className="property-input-group">
                            <Iconify icon="mdi:arrow-expand-horizontal" width={18} height={18} />
                            <input
                              type="number"
                              min={0}
                              value={(() => {
                                const match = elementClasses.match(/(?:^|\s)mx-\[(\d+)px\]/);
                                return match?.[1] || "0";
                              })()}
                              className="property-input"
                              onChange={(e) => {
                                const value = e.target.value;
                                updateElementClasses(prevClasses => {
                                  // Use the helper function to remove existing mx-* classes
                                  const updatedClasses = removeMxClasses(prevClasses);

                                  // Add new mx-* class if value is greater than 0
                                  return value === ""
                                    ? updatedClasses
                                    : (value === "0") ? `${updatedClasses} mx-0` : `${updatedClasses} mx-[${value}px]`;
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="element-property-group">
                        <label>Padding</label>
                        <div className="property-inputs">
                          <div className="property-input-group">
                            <Iconify icon="mdi:arrow-expand-vertical" width={18} height={18} />
                            <input
                              type="number"
                              min={0}
                              value={(() => {
                                const match = elementClasses.match(/(?:^|\s)py-\[(\d+)px\]/);
                                return match?.[1] || "0";
                              })()}
                              className="property-input"
                              onChange={(e) => {
                                const value = e.target.value;
                                updateElementClasses(prevClasses => {
                                  // Use the helper function to remove existing py-* classes
                                  const updatedClasses = removePyClasses(prevClasses);
                                  // Add new py-* class if value is greater than 0
                                  return value === ""
                                    ? updatedClasses
                                    : (value === "0") ? `${updatedClasses} py-0` : `${updatedClasses} py-[${value}px]`;
                                });
                              }}
                            />
                          </div>
                          <div className="property-input-group">
                            <Iconify icon="mdi:arrow-expand-horizontal" width={18} height={18} />
                            <input
                              type="number"
                              min={0}
                              value={(() => {
                                const match = elementClasses.match(/(?:^|\s)px-\[(\d+)px\]/);
                                return match?.[1] || "0";
                              })()}
                              className="property-input"
                              onChange={(e) => {
                                const value = e.target.value;
                                updateElementClasses(prevClasses => {
                                  // Use the helper function to remove existing px-* classes
                                  const updatedClasses = removePxClasses(prevClasses);
                                  // Add new px-* class if value is greater than 0
                                  return value === ""
                                    ? updatedClasses
                                    : (value === "0") ? `${updatedClasses} px-0` : `${updatedClasses} px-[${value}px]`;
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Font options - only for non-wrapper elements */}
                      {!wrapperElements.includes(selectedElement?.tagName?.toLowerCase() || '') && (
                        <>
                          <div className="element-property-group">
                            <label>Font size</label>
                            <div className="property-select property-select--select">
                              <select
                                className="property-select-input"
                                value={getLargestTextSize(elementClasses)}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  updateElementClasses(prevClasses => {
                                    return replaceLargestTextSize(prevClasses, value);
                                  });
                                }}
                              >
                                <option value="9xl">9XL</option>
                                <option value="8xl">8XL</option>
                                <option value="7xl">7XL</option>
                                <option value="6xl">6XL</option>
                                <option value="5xl">5XL</option>
                                <option value="4xl">4XL</option>
                                <option value="3xl">3XL</option>
                                <option value="2xl">2XL</option>
                                <option value="xl">XL</option>
                                <option value="lg">Large</option>
                                <option value="base">Body</option>
                                <option value="sm">Small</option>
                                <option value="xs">XS</option>
                              </select>
                              <Iconify icon="mdi:chevron-down" width={16} height={16} />
                            </div>
                          </div>

                          <div className="element-property-group">
                            <label>Font weight</label>
                            <div className="property-select property-select--select">
                              <select
                                className="property-select-input"
                                value={getLargestFontWeight(elementClasses) || "normal"}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  updateElementClasses(prevClasses => {
                                    return replaceLargestFontWeight(prevClasses, value);
                                  });
                                }}
                              >
                                <option value="thin">Thin (100)</option>
                                <option value="extralight">Extra Light (200)</option>
                                <option value="light">Light (300)</option>
                                <option value="normal">Normal (400)</option>
                                <option value="medium">Medium (500)</option>
                                <option value="semibold">Semi Bold (600)</option>
                                <option value="bold">Bold (700)</option>
                                <option value="extrabold">Extra Bold (800)</option>
                                <option value="black">Black (900)</option>
                              </select>
                              <Iconify icon="mdi:chevron-down" width={16} height={16} />
                            </div>
                          </div>

                          <div className="element-property-group">
                            <label>Color</label>
                            <div className="property-select property-select--color">
                              <div
                                className="color-selector"
                                onClick={(e) => {
                                  // Set the anchor element for the color picker popover
                                  setColorPickerAnchorEl(e.currentTarget);
                                  setShowColorPicker(true);
                                }}
                              >
                                <div
                                  className="color-indicator"
                                  style={{
                                    backgroundColor: getColorPreview(elementClasses)
                                  }}
                                ></div>
                                <span>
                                  {(() => {
                                    // Custom color format: text-[#hexcolor]
                                    const customColorMatch = elementClasses.match(/(?:^|\s)text-\[(#[0-9A-Fa-f]{3,6})\]/);
                                    if (customColorMatch) {
                                      return customColorMatch[1];
                                    }

                                    // Special base colors (white/black) without suffixes
                                    const baseColorMatch = elementClasses.match(/\btext-(white|black)\b/);
                                    if (baseColorMatch) {
                                      return baseColorMatch[1];
                                    }

                                    // Built-in Tailwind colors
                                    const colorMatch = elementClasses.match(/\btext-(white|black|slate|gray|zinc|neutral|stone|red|orange|amber|yellow|lime|green|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia|pink|rose)-(\d+)\b/);
                                    if (colorMatch) {
                                      return `${colorMatch[1]}-${colorMatch[2]}`;
                                    }

                                    return 'Inherit';
                                  })()}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="element-property-group">
                            <label>Alignment</label>
                            <div className="alignment-buttons">
                              <button
                                type="button"
                                className={`alignment-button ${elementClasses.includes('text-left') ? 'active' : ''}`}
                                onClick={() => {
                                  updateElementClasses(prevClasses => {
                                    // Remove existing text-* alignment classes
                                    const updatedClasses = prevClasses.replace(/\btext-(left|center|right|justify)\b/g, '');
                                    // Add new alignment class
                                    return `${updatedClasses.trim()} text-left`.trim();
                                  });
                                }}
                              >
                                <Iconify icon="mdi:format-align-left" width={18} height={18} />
                              </button>
                              <button
                                type="button"
                                className={`alignment-button ${elementClasses.includes('text-center') ? 'active' : ''}`}
                                onClick={() => {
                                  updateElementClasses(prevClasses => {
                                    // Remove existing text-* alignment classes
                                    const updatedClasses = prevClasses.replace(/\btext-(left|center|right|justify)\b/g, '');
                                    // Add new alignment class
                                    return `${updatedClasses.trim()} text-center`.trim();
                                  });
                                }}
                              >
                                <Iconify icon="mdi:format-align-center" width={18} height={18} />
                              </button>
                              <button
                                type="button"
                                className={`alignment-button ${elementClasses.includes('text-right') ? 'active' : ''}`}
                                onClick={() => {
                                  updateElementClasses(prevClasses => {
                                    // Remove existing text-* alignment classes
                                    const updatedClasses = prevClasses.replace(/\btext-(left|center|right|justify)\b/g, '');
                                    // Add new alignment class
                                    return `${updatedClasses.trim()} text-right`.trim();
                                  });
                                }}
                              >
                                <Iconify icon="mdi:format-align-right" width={18} height={18} />
                              </button>
                              <button
                                type="button"
                                className={`alignment-button ${elementClasses.includes('text-justify') ? 'active' : ''}`}
                                onClick={() => {
                                  updateElementClasses(prevClasses => {
                                    // Remove existing text-* alignment classes
                                    const updatedClasses = prevClasses.replace(/\btext-(left|center|right|justify)\b/g, '');
                                    // Add new alignment class
                                    return `${updatedClasses.trim()} text-justify`.trim();
                                  });
                                }}
                              >
                                <Iconify icon="mdi:format-align-justify" width={18} height={18} />
                              </button>
                            </div>
                          </div>
                        </>
                      )}

                      <div className="element-property-group">
                        <label className="label-with-info">
                          Class
                          <Tooltip title="Enter custom Tailwind classes (e.g. text-red-500, flex, p-4)">
                            <IconButton size="small">
                              <Iconify icon="mdi:help-circle-outline" width={14} height={14} />
                            </IconButton>
                          </Tooltip>
                        </label>
                        <textarea
                          className="css-class-input"
                          value={elementClasses}
                          onChange={(e) => {
                            // Update React state
                            updateElementClasses(e.target.value);

                            // Also update the DOM element immediately to reflect changes in preview
                            if (selectedElement) {
                              // Save current class to restore 'element-selected' class
                              const wasSelected = selectedElement.classList.contains('element-selected');

                              // Update the element classes in the DOM
                              selectedElement.className = e.target.value;

                              // Re-add the selected class to maintain highlight
                              if (wasSelected) {
                                selectedElement.classList.add('element-selected');
                              }
                            }
                          }}
                          placeholder="Enter Tailwind classes separated by spaces"
                        ></textarea>
                      </div>
                    </div>

                    <div className="element-class-form-actions">
                      <Button
                        variant="outlined"
                        className="discard-button"
                        onClick={handleDiscardChanges}
                        type="button"
                      >
                        Discard
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                      >
                        Apply
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {/* Color Picker Popover */}
              <Popover
                open={showColorPicker}
                anchorEl={colorPickerAnchorEl}
                onClose={() => setShowColorPicker(false)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                classes={{
                  paper: 'color-picker-popover'
                }}
              >
                <div className="color-picker-container">
                  <div className="color-tabs">
                    <div
                      className={`color-tab ${colorPickerTab === 'styles' ? 'color-tab-active' : ''}`}
                      onClick={() => setColorPickerTab('styles')}
                    >
                      Styles
                    </div>
                    <div
                      className={`color-tab ${colorPickerTab === 'custom' ? 'color-tab-active' : ''}`}
                      onClick={() => setColorPickerTab('custom')}
                    >
                      Custom
                    </div>
                  </div>

                  {colorPickerTab === 'styles' ? (
                    <div className="color-swatches">
                      {/* Base Colors */}
                      <div className="color-category">
                        <h5>Base</h5>
                        <div className="swatch-grid">
                          <div
                            className="color-swatch-item"
                            onClick={() => {
                              updateElementClasses(prevClasses => {
                                const updatedClasses = removeColorClasses(prevClasses);
                                return `${updatedClasses}`;
                              });
                              setShowColorPicker(false);
                            }}
                          >
                            <div className="color-swatch" style={{ backgroundColor: '#ffffff' }}></div>
                            <span className="color-swatch-label">inherit</span>
                          </div>
                          <div
                            className="color-swatch-item"
                            onClick={() => {
                              updateElementClasses(prevClasses => {
                                // Remove existing text color classes
                                const updatedClasses = removeColorClasses(prevClasses);
                                // Add new text-* class
                                return `${updatedClasses} text-white`;
                              });
                              setShowColorPicker(false);
                            }}
                          >
                            <div className="color-swatch" style={{ backgroundColor: '#ffffff' }}></div>
                            <span className="color-swatch-label">white</span>
                          </div>
                          <div
                            className="color-swatch-item"
                            onClick={() => {
                              updateElementClasses(prevClasses => {
                                // Remove existing text color classes
                                const updatedClasses = removeColorClasses(prevClasses);
                                // Add new text-* class
                                return `${updatedClasses} text-black`;
                              });
                              setShowColorPicker(false);
                            }}
                          >
                            <div className="color-swatch" style={{ backgroundColor: '#000000' }}></div>
                            <span className="color-swatch-label">black</span>
                          </div>
                        </div>
                      </div>

                      {/* Slate */}
                      <div className="color-category">
                        <h5>Slate</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`slate-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  // Remove existing text color classes
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  // Add new text-* class
                                  return `${updatedClasses} text-slate-${shade}`;
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('slate', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Gray */}
                      <div className="color-category">
                        <h5>Gray</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`gray-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  // Remove existing text color classes
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-gray-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('gray', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Zinc */}
                      <div className="color-category">
                        <h5>Zinc</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`zinc-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  // Remove existing text color classes
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-zinc-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('zinc', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Colors Grid */}
                      <div className="color-category">
                        <h5>Red</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`red-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-red-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('red', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Orange */}
                      <div className="color-category">
                        <h5>Orange</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`orange-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-orange-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('orange', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Yellow */}
                      <div className="color-category">
                        <h5>Yellow</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`yellow-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-yellow-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('yellow', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Green */}
                      <div className="color-category">
                        <h5>Green</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`green-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-green-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('green', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Blue */}
                      <div className="color-category">
                        <h5>Blue</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`blue-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-blue-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('blue', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Indigo */}
                      <div className="color-category">
                        <h5>Indigo</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`indigo-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-indigo-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('indigo', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Violet */}
                      <div className="color-category">
                        <h5>Violet</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`violet-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-violet-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('violet', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Purple */}
                      <div className="color-category">
                        <h5>Purple</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`purple-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-purple-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('purple', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Pink */}
                      <div className="color-category">
                        <h5>Pink</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`pink-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-pink-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('pink', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Rose */}
                      <div className="color-category">
                        <h5>Rose</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`rose-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-rose-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('rose', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Amber */}
                      <div className="color-category">
                        <h5>Amber</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`amber-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-amber-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('amber', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Lime */}
                      <div className="color-category">
                        <h5>Lime</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`lime-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-lime-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('lime', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Emerald */}
                      <div className="color-category">
                        <h5>Emerald</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`emerald-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-emerald-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('emerald', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Teal */}
                      <div className="color-category">
                        <h5>Teal</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`teal-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-teal-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('teal', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Cyan */}
                      <div className="color-category">
                        <h5>Cyan</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`cyan-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-cyan-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('cyan', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Sky */}
                      <div className="color-category">
                        <h5>Sky</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`sky-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-sky-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('sky', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Stone */}
                      <div className="color-category">
                        <h5>Stone</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`stone-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-stone-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('stone', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Neutral */}
                      <div className="color-category">
                        <h5>Neutral</h5>
                        <div className="swatch-grid">
                          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => (
                            <div
                              key={`neutral-${shade}`}
                              className="color-swatch-item"
                              onClick={() => {
                                updateElementClasses(prevClasses => {
                                  const updatedClasses = removeColorClasses(prevClasses);
                                  return `${updatedClasses.trim()} text-neutral-${shade}`.trim();
                                });
                                setShowColorPicker(false);
                              }}
                            >
                              <div className="color-swatch" style={{ backgroundColor: getColorHex('neutral', shade) }}></div>
                              <span className="color-swatch-label">{shade}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="custom-color-picker">
                      <div className="custom-color-inputs">
                        <input
                          type="color"
                          className="color-picker-input"
                          value={customColor}
                          onChange={(e) => setCustomColor(e.target.value)}
                        />
                        <input
                          type="text"
                          className="color-picker-hex"
                          value={customColor}
                          onChange={(e) => {
                            // Only update if it's a valid hex color
                            if (/^#([0-9A-F]{3}){1,2}$/i.test(e.target.value)) {
                              setCustomColor(e.target.value);
                            }
                          }}
                          placeholder="#000000"
                        />
                      </div>
                      <div className="custom-color-actions">
                        <button
                          className="custom-color-apply"
                          onClick={() => {
                            updateElementClasses(prevClasses => {
                              // Remove existing text color classes
                              const updatedClasses = removeColorClasses(prevClasses);
                              // Add the custom color with the Tailwind arbitrary value syntax
                              return `${updatedClasses} text-[${customColor}]`;
                            });
                            setShowColorPicker(false);
                          }}
                        >
                          Apply Color
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </Popover>

              <form onSubmit={handleSendMessage} className="chat-preview__form">
                {!showClassForm && (
                  <div className={`chat-preview__history ${!showHistory ? 'chat-preview__history--collapsed' : ''}`}>
                    <div className="chat-preview__history-header">
                      <h4>
                        Messages
                      </h4>
                      <IconButton
                        onClick={handleToggleHistory}
                        className="chat-preview__action-button"
                        title={showHistory ? "Hide messages" : "Show messages"}
                        disabled={isWaitingResponse}
                      >
                        <Iconify icon={showHistory ? "mdi:chevron-up" : "mdi:chevron-down"} width={18} height={18} />
                      </IconButton>
                    </div>

                    {showHistory && (
                      <div className="chat-preview__history-messages">
                        {isLoadingHistory ? (
                          <div className="chat-preview__history-loading">
                            <CircularProgress size={24} />
                            <span>Loading message history...</span>
                          </div>
                        ) : filteredMessages.length > 0 ? (
                          filteredMessages.map((message, index) => (
                            <div
                              key={index}
                              className={`chat-preview__history-message ${message.type === 'user' ? 'chat-preview__history-message--user' : 'chat-preview__history-message--ai'}`}
                            >
                              {message.type !== 'user' && (
                                <>
                                  <div className="chat-preview__history-message-type">
                                    UIBrainX
                                  </div>
                                </>
                              )}

                              {message.type === 'user' ? (
                                <>
                                  <div className="chat-preview__history-message-content">
                                    {message.content}
                                  </div>
                                  {message.images && message.images.length > 0 && (
                                    <div className="chat-preview__history-message-images">
                                      {message.images.map((image, imgIndex) => (
                                        <img
                                          key={imgIndex}
                                          src={image}
                                          alt={`Image ${imgIndex + 1}`}
                                          className="chat-preview__history-message-image"
                                        />
                                      ))}
                                    </div>
                                  )}

                                  { (project.uibrainxVersion === 2 && message.projectVersion) && (
                                    <div className="chat-preview__history-message-html">
                                      <div className="chat-preview__history-message-html-header">
                                        <span>HTML Version {message.projectVersion}</span>
                                        <button
                                          className="chat-preview__history-message-html-button"
                                          onClick={() => handleApplyHistoryHtml(message.projectVersion)}
                                          title="Apply this HTML version"
                                        >
                                          <Iconify icon="mdi:restore" width={16} height={16} />
                                          <span>Restore</span>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  {/* AI message with explanation and action structure */}
                                  {message.explanation && (
                                    <div className="chat-preview__ai-message-explanation">
                                      <div className="ai-section-title">Explanation</div>
                                      <div className="ai-section-content">{message.explanation}</div>
                                    </div>
                                  )}

                                  {message.action && (
                                    <div className="chat-preview__ai-message-action">
                                      <div className="ai-section-title">Action</div>
                                      <div className="ai-section-content">{message.action}</div>
                                    </div>
                                  )}

                                  {/* Fallback to regular content if no explanation/action */}
                                  {!message.explanation && !message.action && (
                                    <div className="chat-preview__history-message-content">
                                      {message.content}
                                    </div>
                                  )}

                                  { (project.uibrainxVersion === 2 && message.projectVersion) && (
                                    <div className="chat-preview__history-message-html">
                                      <div className="chat-preview__history-message-html-header">
                                        <span>HTML Version {message.projectVersion}</span>
                                        <button
                                          className="chat-preview__history-message-html-button"
                                          onClick={() => handleApplyHistoryHtml(message.projectVersion)}
                                          title="Apply this HTML version"
                                        >
                                          <Iconify icon="mdi:restore" width={16} height={16} />
                                          <span>Restore</span>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          ))
                        ) : null}
                      </div>
                    )}
                  </div>
                )}

                <div className="chat-preview__input-wrapper">
                  {project.uibrainxVersion !== 2 && (
                    <div className="chat-preview__input-warning">
                      <span>Please create a new project to use UIBrainX v2</span>
                    </div>
                  )}
                  {/* Removing the image preview container as requested */}
                  {detectedUrls.length > 0 && UrlActionCard}

                  {isAnalyzingImage && (
                    <div className="chat-preview__status-indicator">
                      Analyzing image...
                    </div>
                  )}
                  <textarea
                    ref={textareaRef}
                    value={userInput}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      isAnalyzingImage
                        ? "Analyzing image..."
                        : showClassForm
                          ? "Ask UIBrainX to modify selected element..."
                          : isAnimationActive
                            ? animatedPlaceholder
                            : "Ask UIBrainX anything about your page..."
                    }
                    className="chat-preview__input"
                    rows={1}
                    style={{
                      resize: 'none',
                      overflow: 'auto',
                      opacity: isAnalyzingImage ? 0.7 : 1
                    }}
                    disabled={isWaitingResponse || isAnalyzingImage}
                  />

                  <div className="chat-preview__actions">
                    <div className="chat-preview__actions-left">
                      <span>Remaining: {state.messagesRemaining}</span>
                      <div className="chat-preview__actions-model">
                        <Select
                          value={selectedModel}
                          onChange={(e) => setSelectedModel(e.target.value)}
                          disabled={isWaitingResponse || isAnalyzingImage}
                        >
                          <MenuItem value="model1">Model 1</MenuItem>
                          <MenuItem value="model2">Model 2</MenuItem>
                        </Select>
                      </div>
                    </div>
                    <div className="chat-preview__actions-right">
                      <IconButton
                        onClick={() => {
                          if (!isEditEditorMode[String(activePage)]) {
                            setIsEditMode(!isEditMode)
                          } else {
                            setError('Please click Save changes or Discard button first');
                            setOpenSnackbar(true);
                          }
                        }}
                        className={`chat-preview__action-button ${isEditMode ? 'active' : ''}`}
                        title={isEditMode ? "Exit edit mode" : "Edit elements"}
                        disabled={isWaitingResponse || isSaving || actionInProgress !== null}
                      >
                        <Iconify icon="mdi:target" width={18} height={18} />
                      </IconButton>
                      <input
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                        disabled={isWaitingResponse || isSaving || actionInProgress !== null}
                      />
                      <label htmlFor="image-upload">
                        <IconButton
                          component="span"
                          className="chat-preview__action-button"
                          title="Upload image"
                          disabled={isWaitingResponse || isUploading || isAnalyzingImage || isSaving || actionInProgress !== null}
                          style={{ opacity: (isUploading || isAnalyzingImage || isSaving || actionInProgress !== null) ? 0.5 : 1 }}
                        >
                          <div
                            style={{
                              position: 'relative',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {(isUploading || isAnalyzingImage) && (
                              <div
                                style={{
                                  position: 'absolute',
                                  width: '100%',
                                  height: '100%',
                                  border: '2px solid rgba(255, 255, 255, 0.2)',
                                  borderTopColor: 'white',
                                  borderRadius: '50%',
                                  animation: 'spin 2s linear infinite'
                                }}
                              />
                            )}
                            <Iconify
                              icon="mdi:image-outline"
                              width={18}
                              height={18}
                            />
                          </div>
                        </IconButton>
                      </label>
                      <IconButton
                        onClick={handleOpenColorPalette}
                        className="chat-preview__action-button"
                        title="Insert color palette"
                        disabled={isWaitingResponse || isSaving || actionInProgress !== null}
                      >
                        <Iconify icon="mdi:palette-outline" width={18} height={18} />
                      </IconButton>
                      <IconButton
                        onClick={handleOpenFontSelector}
                        className="chat-preview__action-button"
                        title="Insert font"
                        disabled={isWaitingResponse || isSaving || actionInProgress !== null}
                      >
                        <Iconify icon="mdi:format-text-variant" width={18} height={18} />
                      </IconButton>
                      <IconButton
                        type="submit"
                        className="chat-preview__send-button"
                        disabled={isWaitingResponse || isUploading || isAnalyzingImage || (!userInput.trim() && !uploadedImages.length) || isSaving || actionInProgress !== null}
                        title={isAnalyzingImage ? "Analyzing image..." : "Send"}
                        style={{ opacity: (isWaitingResponse || isUploading || isAnalyzingImage || isSaving || actionInProgress !== null) ? 0.5 : 1 }}
                      >
                        <Iconify icon="mdi:arrow-right" width={18} height={18} />
                      </IconButton>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="chat-preview__preview">
              <div className="chat-preview__preview-header">
                <div className="chat-preview__preview-header-left">
                </div>
                <div className="chat-preview__preview-header-center">
                  <button
                    onClick={handleOpenInNewTab}
                    className="chat-preview__preview-button"
                    title="Open in new tab"
                  >
                    <Iconify icon="tabler:external-link" width={18} height={18} style={{ display: 'block' }} />
                  </button>
                  <button
                    onClick={handleToggleViewMode}
                    className="chat-preview__preview-button mobile-view-toggle"
                    title={isMobileView ? "Switch to desktop view" : "Switch to mobile view"}
                  >
                    <Iconify icon={isMobileView ? "tabler:device-desktop" : "tabler:device-mobile"} width={18} height={18} style={{ display: 'block' }} />
                  </button>
                  <button
                    onClick={handleToggleExpand}
                    className="chat-preview__preview-button"
                    title={isExpanded ? "Collapse view" : "Expand view"}
                  >
                    <Iconify icon={isExpanded ? "tabler:arrows-minimize" : "tabler:arrows-maximize"} width={18} height={18} style={{ display: 'block' }} />
                  </button>
                  <button
                    onClick={handleRefreshIframe}
                    className="chat-preview__preview-button"
                    title="Refresh"
                  >
                    <Iconify icon="tabler:refresh" width={18} height={18} style={{ display: 'block' }} />
                  </button>
                </div>
                <div className="chat-preview__preview-header-right">
                  <Stack direction="row" spacing={1} alignItems="center">
                    {['admin', 'tester'].includes(state.user?.role ?? '') && activeHtmlId && (
                      <Button onClick={handleOpenCommitDialog} variant="outlined">
                        Commit
                      </Button>
                    )}
                    {!openModal && (
                      <Button onClick={() => { setOpenModal(!openModal) }} variant="contained">
                        Export
                      </Button>
                    )}
                  </Stack>
                </div>
              </div>

              <div className={`chat-preview__iframe-container chat-preview__iframe-container--with-console ${isConsoleCollapsed ? 'chat-preview__iframe-container--console-collapsed' : ''}`}>
                {activeHtmlId ? (
                  <iframe
                    ref={iframeRef}
                    onLoad={() => {
                      setIframeLoaded(prev => !prev);
                    }}
                    src={`/api/v1/html/${activeHtmlId}?getfrom=preview`}
                    title="Website Preview"
                    className={`chat-preview__iframe ${isMobileView ? 'chat-preview__iframe--mobile' : ''} ${isExpanded ? 'chat-preview__iframe--expanded' : ''}`}
                    sandbox="allow-scripts allow-same-origin"
                    data-aaa
                  />
                ) : (
                  <iframe
                    ref={iframeRef}
                    srcDoc={previewHtml}
                    onLoad={() => {
                      setIframeLoaded(true);
                    }}
                    title="Website Preview xx"
                    className={`chat-preview__iframe ${isMobileView ? 'chat-preview__iframe--mobile' : ''} ${isExpanded ? 'chat-preview__iframe--expanded' : ''}`}
                    sandbox="allow-scripts allow-same-origin"
                  />
                )}

                {/* Console Window - Always visible */}
                <div className={`console-window ${isConsoleCollapsed ? 'console-window--collapsed' : ''}`}>
                  <div className="console-window__header">
                    <div className="console-window__title">
                      <Iconify icon="tabler:terminal-2" width={16} height={16} />
                      <span>Console</span>
                      <span className="console-window__count">({consoleLogs.filter(log => log.htmlId === activeHtmlId).length})</span>
                    </div>
                    <div className="console-window__actions">
                      <button
                        onClick={handleClearConsole}
                        className="console-window__action"
                        title="Clear console"
                      >
                        <Iconify icon="tabler:trash" width={14} height={14} />
                      </button>
                      <button
                        onClick={handleToggleConsole}
                        className="console-window__action"
                        title={isConsoleCollapsed ? "Expand console" : "Collapse console"}
                      >
                        <Iconify
                          icon={isConsoleCollapsed ? "tabler:chevron-up" : "tabler:chevron-down"}
                          width={14}
                          height={14}
                        />
                      </button>
                    </div>
                  </div>
                  {!isConsoleCollapsed && (
                    <div className="console-window__content" ref={consoleRef}>
                      {consoleLogs.filter(log => log.htmlId === activeHtmlId).length === 0 ? (
                        <div className="console-window__empty">
                          <span>Console is empty. JavaScript errors will appear here.</span>
                        </div>
                      ) : (
                        consoleLogs.filter(log => log.htmlId === activeHtmlId).map((log) => (
                          <div key={log.id} className={`console-log console-log--${log.type}`}>
                            <div className="console-log__badge">
                              {log.type.toUpperCase()}
                            </div>
                            <div className="console-log__content">
                              <div className="console-log__message">{log.message}</div>
                            </div>
                            {(log.type === 'error' || log.type === 'warn') && (
                              <div className="console-log__actions">
                                <button
                                  onClick={() => handleFixBug(log)}
                                  className="console-log__fix-button"
                                >
                                  <Iconify icon="tabler:wand" width={12} height={12} />
                                  <span>Fix this issue</span>
                                </button>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {openModal && 
                  <Panel setOpenModal={setOpenModal} projectId={projectId} pageId={activePage} pageURL={Config.PUBLIC_URL + `/api/v1/html/${activeHtmlId}?t=${Date.now()}`} iframeRef={iframeRef} cssSelector={cssSelector} setCssSelector={setCssSelector} exportType={exportType} setExportType={setExportType} />
                } 
              </div>
            </div>
          </div>
        )
      )}

      {/* Templates Popover */}
      <Popover
        open={showTemplates}
        anchorEl={null}
        onClose={handleCloseTemplates}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        classes={{
          paper: 'templates-popover'
        }}
        // Style the popover container directly
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <div className="templates-container">
          <div className="templates-header">
            <h4>Community templates</h4>
            <div className="templates-search">
              <input
                type="text"
                placeholder="Search templates..."
                value={templateSearch}
                onChange={handleTemplateSearchChange}
              />
              {templateSearch && (
                <button
                  className="templates-search-clear"
                  onClick={() => setTemplateSearch('')}
                >
                  <Iconify icon="mdi:close" width={16} height={16} />
                </button>
              )}
            </div>
          </div>

          <div className="templates-content">
            {isLoadingTemplates ? (
              <div className="templates-loading">
                <CircularProgress size={24} />
                <span>Loading templates...</span>
              </div>
            ) : filteredTemplates.length > 0 ? (
              <div className="templates-grid">
                {filteredTemplates.map((template) => (
                  <div
                    key={template._id}
                    className="template-item"
                    onClick={() => handleSelectTemplate(template._id)}
                  >
                    {['admin', 'tester'].includes(state.user?.role ?? '') && (
                      <Tooltip title="Remove from community templates">
                        <span className="template-item__delete-wrap">
                          <IconButton
                            type="button"
                            size="small"
                            className="template-item__delete"
                            disabled={deletingCommunityTemplateId === template._id}
                            onClick={(e) => handleDeleteCommunityTemplate(e, template._id, template.name)}
                            aria-label={`Remove ${template.name} from community templates`}
                          >
                            {deletingCommunityTemplateId === template._id ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <Iconify icon="mdi:delete-outline" width={20} height={20} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <div className="template-image">
                      <img
                        src={template.screenshotUrl ? `${Config.PUBLIC_URL}${template.screenshotUrl}` : `${Config.PUBLIC_URL}/resources/screenshots/${template._id}.png`}
                        alt={template.name}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `${Config.PUBLIC_URL}/resources/placeholder.png`;
                        }}
                      />
                    </div>
                    <div className="template-info">
                      <h5 className="template-name">{template.name}</h5>
                      {template.description && (
                        <p className="template-description">{template.description}</p>
                      )}
                      {template.tags && template.tags.length > 0 && (
                        <div className="template-tags">
                          {template.tags.map((tag, index) => (
                            <span key={index} className="template-tag">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="templates-empty">
                <div>
                  {templateSearch ?
                    'No templates match your search.' :
                    'No templates available yet.'}
                  <p style={{ marginTop: '10px', fontSize: '12px', color: '#7c7f8a' }}>
                    The API endpoint /api/v1/templates should return available templates. Make sure your templates are added to the database.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Popover>

      <Dialog open={openCommitDialog} onClose={handleCloseCommitDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Commit template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Save the current page as a new community template, or overwrite an existing template (HTML, thumbnail, and AI-generated name/tags/author).
          </Typography>
          <RadioGroup
            value={commitIsReplace ? 'replace' : 'new'}
            onChange={(e) => {
              const next = e.target.value === 'replace';
              setCommitIsReplace(next);
              if (!next) setCommitReplaceTemplateId('');
            }}
          >
            <FormControlLabel value="new" control={<Radio />} label="Create new template" />
            <FormControlLabel value="replace" control={<Radio />} label="Replace existing template" />
          </RadioGroup>
          {commitIsReplace && (
            <FormControl fullWidth sx={{ mt: 2 }} disabled={isLoadingTemplates}>
              <InputLabel id="commit-replace-template-label">Template to replace</InputLabel>
              <Select
                labelId="commit-replace-template-label"
                value={commitReplaceTemplateId}
                label="Template to replace"
                onChange={(e) => setCommitReplaceTemplateId(String(e.target.value))}
                renderValue={(id) => {
                  if (!id) return '';
                  const t = templates.find((x) => x._id === id);
                  if (!t) return String(id);
                  return (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.25, minWidth: 0 }}>
                      <Box
                        component="img"
                        src={getTemplateThumbnailUrl(t)}
                        alt=""
                        sx={{
                          width: 44,
                          height: 28,
                          objectFit: 'cover',
                          borderRadius: 0.5,
                          flexShrink: 0,
                          bgcolor: 'action.hover',
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.visibility = 'hidden';
                        }}
                      />
                      <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                        {t.name}
                      </Typography>
                    </Stack>
                  );
                }}
              >
                {templates.map((t) => (
                  <MenuItem key={t._id} value={t._id}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                      <Box
                        component="img"
                        src={getTemplateThumbnailUrl(t)}
                        alt=""
                        sx={{
                          width: 56,
                          height: 36,
                          objectFit: 'cover',
                          borderRadius: 1,
                          flexShrink: 0,
                          bgcolor: 'action.hover',
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.visibility = 'hidden';
                        }}
                      />
                      <Typography variant="body2" noWrap sx={{ minWidth: 0, flex: 1 }}>
                        {t.name}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {commitIsReplace && !isLoadingTemplates && templates.length === 0 && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              No templates loaded. Check the API or try again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCommitDialog} disabled={isCommittingTemplate}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCommitTemplateConfirm}
            disabled={
              isCommittingTemplate || (commitIsReplace && (!commitReplaceTemplateId || templates.length === 0))
            }
            startIcon={isCommittingTemplate ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Commit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={openSuccessSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSuccessSnackbar(false)}
      >
        <Alert onClose={() => setOpenSuccessSnackbar(false)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default Project;

